import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCircle2, Circle, Flame, Plus, Sparkles, Trophy, ListTodo, Repeat,
  Trash2, CalendarDays, RotateCcw, ChevronLeft, ChevronRight, Pencil,
  GripVertical, Save, X, Loader2, Wand2, Download, Upload, Target,
  Play, Pause, Sunrise, Sun, Sunset, Moon, Heart, DollarSign, Users,
  Palette, Gamepad2, Clock, ChevronDown, ChevronUp, LogOut, CloudOff, Wifi,
  BookOpen, Eye, Map as MapIcon, UtensilsCrossed, Apple, AlertCircle, AlertTriangle, Footprints, Lock,
  ListOrdered, CalendarRange, Library
} from 'lucide-react';

// --- FIREBASE IMPORTS ---
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut 
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  doc,
  setDoc,
  updateDoc,
  deleteField,
  onSnapshot,
  collection,
  getDocs,
  getDoc,
  getDocFromServer,
  deleteDoc,
  query,
  orderBy,
  limit
} from 'firebase/firestore';

// --- DRAG-AND-DROP (tab reorder) ---
import {
  DndContext, DragOverlay, closestCenter,
  PointerSensor, KeyboardSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, horizontalListSortingStrategy, verticalListSortingStrategy,
  useSortable, sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SG_FOOD_NAMES } from './lib/estimateMealCalories';
import {
  calorieMemoryAsFoodsJson,
  calorieMemoryCloudKey,
  mealMemoryKey,
  calorieMemoryForCloud,
  calorieMemoryHasLocalOnlyUpdates,
  forgetRememberedCalories,
  listRememberedCalories,
  mergeCalorieMemory,
  mergeMealLog,
  normalizeCalorieMemory,
  rememberedMealNames,
  removedCalorieMemoryCloudKeys,
  resolveMealCalories,
  suggestRememberedMeals,
  buildGlmCorrectedCalorieMemory,
  GLM_CORRECTED_SAVED_MEALS,
  clampMacro
} from './lib/calorieMemory';
import { latestCacheStamp, mergeMonthlyJournal, shouldReplaceLocalCache } from './lib/journalSync';
import {
  integrationCloudPatch,
  integrationEntryFromDraft,
  listIntegrations,
  mergeIntegrations,
  newIntegrationId,
  normalizeIntegrations
} from './lib/integrations';
import {
  addChecklistItem,
  deleteChecklistItem,
  deleteGoalShell,
  applyGoalPatches,
  goalCloudPatch,
  goalCreateCloudPatch,
  goalFromDraft,
  goalItemCloudPatch,
  goalShellCloudPatch,
  listGoals,
  mergeGoals,
  normalizeGoals,
  restoreChecklistItem,
  restoreGoalShell,
  toggleChecklistItem,
  updateGoalShell
} from './lib/goals';
import {
  applyDottedPatch,
  buildTodoMapPatch,
  canWriteCloudMerge,
  narrowDateKeyedWrite,
  hasTodoMapPatch,
  BUDGET_SAVE_MESSAGE,
  cloudSaveStateForWrite,
  cloudWriteBudget,
  cloudWriteDomain,
  cloudWriteSignature,
  countCloudWrite,
  emptyWriteLedger,
  isFirestoreQuotaError,
  isQuotaBlocked,
  quotaBlockedUntil,
  deserializePendingUpdate,
  serializePendingUpdate,
  PENDING_QUEUE_MAX,
  WRITE_STALL_DEADLINE_MS,
  isFullDocumentCloudUpdate,
  isIncrementalCloudUpdate,
  mergeInFlightTodos,
  mergeTodoState,
  resequenceTodoOrder,
  sanitizeCloudFieldPaths,
  shouldSkipDuplicateWrite,
  shouldUploadLocalCacheAsAuthority,
  sortTodosByOrder,
  stampForAppliedServerSnapshot,
  todosArrayToMapPatch,
  todosToMap
} from './lib/cloudSync';
import { patchFirestoreDocRest } from './lib/firestoreRest';
import { continueNumberedList, toggleNumberedList } from './lib/textLists';
import {
  PLAN_START_MIN,
  PLAN_END_MIN,
  PLAN_SLOT_MIN,
  AI_QUEST_PREFIX,
  isAiQuestBlock,
  makeSegId,
  defaultPlanSegments,
  normalizeDayPlans,
  migrateDominoGoals
} from './lib/goalPlanSync';
import {
  isConstrainedWebStorage,
  isQuotaExceeded,
  reclaimWebStorage,
  writeLocalCacheRecord
} from './lib/localCache';
import {
  applySprintBoardPatches,
  diffSprintBoard,
  dropShadowedSprintPaths,
  hasWholeSprintBoard,
  normalizeSprintBoard
} from './lib/sprintBoard';
import SprintBoardTab from './SprintBoardTab';
import IntegratedTab from './IntegratedTab';
import GoalsTab from './GoalsTab';

// Main content tabs (reorderable by drag/swipe) — no extra hook libraries.
// 'sprint' is the tab labelled Calendar: the ids 'calendar' (Momentum) and 'schedule'
// were taken long before it, and ids are saved in every user's tabOrder.
const DEFAULT_TAB_ORDER = ['todos', 'habits', 'calendar', 'schedule', 'sprint', 'plan', 'nutrition', 'money', 'journal', 'integrated', 'goals'];
const ALL_TAB_IDS = new Set(DEFAULT_TAB_ORDER);

const normalizeTabOrder = (input) => {
  const incoming = Array.isArray(input)
    ? input.map(String).filter((id) => ALL_TAB_IDS.has(id))
    : [];
  const seen = new Set();
  const ordered = [];
  incoming.forEach((id) => {
    if (!seen.has(id)) {
      seen.add(id);
      ordered.push(id);
    }
  });
  DEFAULT_TAB_ORDER.forEach((id) => {
    if (!seen.has(id)) ordered.push(id);
  });
  return ordered;
};

const arrayMove = (list, fromIndex, toIndex) => {
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return list;
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};

// Monday-start week key, same convention as weekly training progress.
const getWeekStartDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return d.toISOString().split('T')[0];
};

const fmtPlanTime = (totalMin) => {
  const h24 = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

// 30 → "30 min", 60 → "1 hr", 90 → "1.5 hr", 120 → "2 hr"
const fmtPlanDuration = (min) => {
  if (min < 60) return `${min} min`;
  const h = min / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} hr`;
};

// Attach computed clock times to each segment by stacking durations from 6:30 AM.
const withPlanTimes = (segments) => {
  let cursor = PLAN_START_MIN;
  return (segments || []).map((s) => {
    const startMin = cursor;
    cursor += s.duration;
    return {
      ...s,
      startMin,
      endMin: cursor,
      startLabel: fmtPlanTime(startMin),
      endLabel: fmtPlanTime(cursor)
    };
  });
};

// Single reorderable tab. PointerSensor activation distance lets a plain click
// still fire onSelect while a drag past the threshold reorders instead.
function SortableTabButton({ id, label, icon, isActive, onSelect }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      onClick={() => onSelect(id)}
      {...attributes}
      {...listeners}
      className={`flex flex-col items-center justify-center gap-1 py-2.5 sm:py-3 px-2 rounded-xl text-[10px] sm:text-xs font-semibold transition-colors touch-none select-none ${
        isActive
          ? 'bg-white text-zinc-900 shadow-sm'
          : 'text-zinc-500 hover:text-zinc-700 hover:bg-white/60'
      }`}
    >
      {icon}
      <span className="leading-tight">{label}</span>
    </button>
  );
}

function CloudSyncLine({ status, idle, saving, error, detail, className = '', domain, activeDomain }) {
  // Only claim this tab's data is syncing when the write in flight actually touched it.
  // A write from another tab, or one spanning several, gets neutral wording.
  const ownsWrite = !domain || !activeDomain || domain === activeDomain;
  // The per-tab `error` copy names which data is stuck but never why. Every failure
  // state already carries a readable reason — offline, quota, budget, rejected —
  // so show that instead: without it a failing device gives no clue which it is.
  const reason = typeof detail === 'string' ? detail.trim() : '';
  const message =
    status === 'saving'
      ? (ownsWrite ? (saving || 'Syncing to Firebase…') : 'Syncing to Firebase…')
      : status === 'error'
        ? (reason || error || 'Saved on this device — cloud sync failed')
        : (idle || 'Synced to Firebase');
  return (
    <p className={`text-xs text-zinc-500 flex items-center gap-1.5 min-w-0 w-full max-w-full overflow-visible py-1.5 leading-normal ${className}`}>
      <Wifi className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere] leading-normal text-left py-0.5 overflow-visible">
        {message}
      </span>
      {/* Which build this device is actually running. A phone can sit on a cached
          bundle for a long time, and without this there is no way to tell whether a
          sync test failed because the fix is wrong or because the fix is not there. */}
      <span className="flex-shrink-0 font-mono text-[10px] text-zinc-400 tabular-nums">
        {typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'}
      </span>
    </p>
  );
}

// One draggable 30-min / 1-hr block in the plan-day timeline.
function SortablePlanBlock({ seg, isLast, disabled, commitments = [], onText, onExtend, onShrink }) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging
  } = useSortable({ id: seg.id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 30 : undefined
  };
  const isMerged = seg.duration > PLAN_SLOT_MIN;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-1.5 sm:gap-3 rounded-2xl border p-2 sm:p-3 transition-colors ${
        isDragging
          ? 'border-indigo-300 shadow-lg bg-white'
          : isMerged
            ? 'bg-indigo-50/60 border-indigo-200'
            : 'bg-white border-zinc-200'
      }`}
    >
      <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          disabled={disabled}
          title="Drag to reorder"
          className="p-1 -ml-0.5 text-zinc-300 hover:text-indigo-500 cursor-grab active:cursor-grabbing touch-none disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="w-[3.75rem] sm:w-20 text-center">
          <div className="text-[11px] sm:text-xs font-bold text-zinc-800 tabular-nums leading-tight">{seg.startLabel}</div>
          <div className="text-[10px] text-zinc-400 tabular-nums leading-tight">{seg.endLabel}</div>
          <div className={`mt-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
            isMerged ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-zinc-100 text-zinc-500 border-zinc-200'
          }`}>
            {fmtPlanDuration(seg.duration)}
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0 self-stretch flex flex-col justify-center gap-1 py-0.5">
        {commitments.map((c, i) => (
          <div
            key={i}
            title="Fixed block from your Schedule tab"
            className="inline-flex items-center gap-1 w-fit max-w-full text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md px-1.5 py-0.5"
          >
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {fmtPlanTime(c.startMin)}{c.label ? ` · ${c.label}` : ' · scheduled'}
            </span>
          </div>
        ))}
        <textarea
          value={seg.text}
          onChange={(e) => onText(seg.id, e.target.value)}
          disabled={disabled}
          rows={Math.min(6, Math.round(seg.duration / PLAN_SLOT_MIN) + 1)}
          placeholder="Activity for this block…"
          className="w-full bg-transparent border-none focus:outline-none resize-none text-sm text-zinc-800 placeholder-zinc-400 disabled:opacity-60"
        />
        {/* AI's verdict on a quest-derived block: worth your time, or swap it out. */}
        {isAiQuestBlock(seg.text) && seg.remark && (
          <div
            className={`flex items-start gap-1.5 w-fit max-w-full text-[10px] font-semibold rounded-md px-1.5 py-1 border ${
              seg.verdict === 'swap'
                ? 'text-amber-800 bg-amber-50 border-amber-200'
                : 'text-emerald-800 bg-emerald-50 border-emerald-200'
            }`}
          >
            {seg.verdict === 'swap'
              ? <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-px" />
              : <CheckCircle2 className="w-3 h-3 flex-shrink-0 mt-px" />}
            <span className="leading-snug break-words">
              <span className="uppercase tracking-wider">
                {seg.verdict === 'swap' ? 'Swap this' : 'Do this'}
              </span>
              {' · '}{seg.remark}
            </span>
          </div>
        )}
      </div>

      {/* +30 / −30 min. Repeated growth merges consecutive slots into a bigger block. */}
      <div className="flex flex-col items-center justify-center gap-0.5 flex-shrink-0">
        <button
          type="button"
          onClick={() => onShrink(seg.id)}
          disabled={disabled || seg.duration <= PLAN_SLOT_MIN}
          title={seg.duration <= PLAN_SLOT_MIN ? "Can't go below 30 min" : 'Shorten by 30 min'}
          className="p-1.5 rounded-lg text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-25 disabled:hover:bg-transparent"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <span className="text-[9px] font-bold text-zinc-400 tabular-nums leading-none">30m</span>
        <button
          type="button"
          onClick={() => onExtend(seg.id)}
          disabled={disabled || isLast}
          title={isLast ? "Can't extend the last block" : 'Lengthen by 30 min (merges the next slot)'}
          className="p-1.5 rounded-lg text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-25 disabled:hover:bg-transparent"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ==========================================
// ⚠️ REPLACE THIS WITH YOUR FIREBASE CONFIG
// ==========================================
const firebaseConfig = {
    apiKey: "AIzaSyD_Tp38LnG2tNcnqXLsw4Dq-fmgziU_upE",
    authDomain: "rpg-tracker-3949f.firebaseapp.com",
    projectId: "rpg-tracker-3949f",
    storageBucket: "rpg-tracker-3949f.firebasestorage.app",
    messagingSenderId: "52199700437",
    appId: "1:52199700437:web:b8cca3d43fc79c53e92f5c"
  };

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// A mutation handed to persistentLocalCache lands in an IndexedDB queue the SDK
// retries forever, on its own schedule, with no way to inspect or cancel it. The
// write budget and the loop guard below can only see writes this app starts, so
// that queue billed against the daily quota entirely outside their reach — and it
// survives reloads, so a single looping afternoon kept re-draining every following
// day at reset. The app already keeps its own durable copy of every edit in
// localStorage (cacheCloudUpdatesLocally plus the pending-write queue), which is
// the same offline guarantee under code that can actually be governed. Memory cache
// keeps Firestore's retries inside this tab's lifetime, where closing the tab ends
// them. iPhone Safari has run this way for months.
const legacyFirestoreIdbName = (projectId) => `firestore/[DEFAULT]/${projectId}/main`;

// One-time removal of the queue persistentLocalCache left behind. Nothing reads it
// now, but a stale backlog would resurrect the moment the cache mode changed back.
const purgeLegacyFirestorePersistence = (projectId) => {
  if (typeof indexedDB === 'undefined') return;
  try {
    indexedDB.deleteDatabase(legacyFirestoreIdbName(projectId));
  } catch {
    /* a blocked delete must never stop startup */
  }
};

const createFirestore = (firebaseApp) => {
  // iPhone Safari often never upgrades a cache snapshot to a server snapshot
  // over WebChannel, so quest writes stayed queued locally. Force long polling
  // even if IndexedDB persistence is unavailable.
  const longPolling = { experimentalForceLongPolling: true };
  if (isConstrainedWebStorage()) {
    // Firestore's multi-tab mutation log also lives in localStorage. That plus the
    // app cache blows Safari's ~5MB quota and crashes Firestore.
    try {
      reclaimWebStorage(window.localStorage, { dropFirestoreWebStorage: true });
    } catch {
      /* ignore */
    }
  }
  purgeLegacyFirestorePersistence(firebaseConfig.projectId);
  try {
    return initializeFirestore(firebaseApp, {
      ...longPolling,
      localCache: memoryLocalCache()
    });
  } catch (err) {
    console.warn('Firestore memory cache init failed, using default:', err);
    try {
      return initializeFirestore(firebaseApp, longPolling);
    } catch (err2) {
      console.warn('Firestore already initialized, using existing instance:', err2);
      return getFirestore(firebaseApp);
    }
  }
};
const db = createFirestore(app);
const appId = 'rpg-tracker-standalone';

const LOCAL_CACHE_PREFIX = 'rpg-tracker:cache:v1:';
const localCacheKey = (uid) => `${LOCAL_CACHE_PREFIX}${uid}`;

const readLocalCache = (uid) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(localCacheKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.data) return null;
    return parsed;
  } catch (err) {
    console.warn('Local cache read failed:', err);
    return null;
  }
};

const writeLocalCache = (uid, data, savedAt = new Date().toISOString()) => {
  if (!uid || !data) return false;
  try {
    const ok = writeLocalCacheRecord(
      window.localStorage,
      localCacheKey(uid),
      data,
      savedAt,
      { reclaimFirestore: isConstrainedWebStorage() }
    );
    if (!ok) console.warn('Local cache write skipped — browser storage is full.');
    return ok;
  } catch (err) {
    if (isQuotaExceeded(err)) {
      console.warn('Local cache write skipped — browser storage is full.');
      return false;
    }
    console.warn('Local cache write failed:', err);
    return false;
  }
};

// Never let an older Firestore echo erase a newer local/imported snapshot.
// Goals are folded in anyway: the other device's list is merged by updatedAt,
// so a phone whose cache is newer because it logged a meal still keeps a goal
// saved on the laptop, and a reload shows it before the next snapshot.
const writeLocalCacheIfNewer = (uid, data, savedAt = new Date().toISOString()) => {
  if (!uid || !data) return false;
  const existing = readLocalCache(uid);
  if (!shouldReplaceLocalCache(existing?.savedAt, savedAt)) {
    if (!existing?.data) return false;
    const goals = mergeGoals(existing.data.goals, data.goals);
    if (JSON.stringify(normalizeGoals(existing.data.goals)) === JSON.stringify(goals)) return false;
    return writeLocalCache(uid, { ...existing.data, goals }, existing.savedAt);
  }
  return writeLocalCache(uid, data, savedAt);
};

const toCloudPayload = (safeData) => ({
  todos: safeData.todos,
  todoMap: safeData.todoMap && typeof safeData.todoMap === 'object'
    ? safeData.todoMap
    : todosToMap(safeData.todos),
  habits: safeData.habits,
  stats: safeData.stats,
  header: safeData.header,
  dominos: safeData.dominos,
  dominoLabels: safeData.dominoLabels,
  completedDominos: safeData.completedDominos,
  categoryLabels: safeData.categoryLabels,
  collapsedCategories: safeData.collapsedCategories,
  collapsedTodos: safeData.collapsedTodos,
  monthlyJournal: safeData.monthlyJournal,
  penaltyMeta: safeData.penaltyMeta,
  userApiKey: safeData.userApiKey,
  userApiKey2: safeData.userApiKey2,
  deepseekApiKey: safeData.deepseekApiKey,
  visionBoardText: safeData.visionBoardText,
  scheduleConfig: safeData.scheduleConfig,
  scheduleData: safeData.scheduleData,
  dayPlans: safeData.dayPlans,
  mealLog: safeData.mealLog,
  healthProfile: safeData.healthProfile,
  calorieMemory: safeData.calorieMemory,
  stepLog: safeData.stepLog,
  moneyLog: safeData.moneyLog,
  tabOrder: safeData.tabOrder,
  sprintBoard: safeData.sprintBoard,
  integrations: safeData.integrations,
  goals: safeData.goals
});

// How much "real" user content a payload has. Used so an empty local cache
// on a new Cloudflare Pages origin can never overwrite Firebase.
const appDataWeight = (data) => {
  if (!data || typeof data !== 'object') return 0;
  const mealDays = data.mealLog && typeof data.mealLog === 'object' ? Object.keys(data.mealLog).length : 0;
  const rememberedMeals = data.calorieMemory && typeof data.calorieMemory === 'object'
    ? Object.keys(data.calorieMemory).length
    : 0;
  const stepDays = data.stepLog && typeof data.stepLog === 'object' ? Object.keys(data.stepLog).length : 0;
  const planDays = data.dayPlans && typeof data.dayPlans === 'object' ? Object.keys(data.dayPlans).length : 0;
  const moneyDays = data.moneyLog && typeof data.moneyLog === 'object' ? Object.keys(data.moneyLog).length : 0;
  const journalScore = data.monthlyJournal && typeof data.monthlyJournal === 'object'
    ? Object.values(data.monthlyJournal).reduce((n, entry) => {
        const hasText = String(entry?.expectations || '').trim() || String(entry?.accomplished || '').trim();
        return n + (hasText ? 2 : 0);
      }, 0)
    : 0;
  const scheduleSlots = data.scheduleData && typeof data.scheduleData === 'object' ? Object.keys(data.scheduleData).length : 0;
  const integrationCount = data.integrations && typeof data.integrations === 'object' && !Array.isArray(data.integrations)
    ? Object.keys(data.integrations).length
    : 0;
  const goalCount = data.goals && typeof data.goals === 'object' && !Array.isArray(data.goals)
    ? Object.keys(data.goals).length
    : 0;
  return (
    (Array.isArray(data.todos) ? data.todos.length : 0) +
    (Array.isArray(data.habits) ? data.habits.length : 0) +
    mealDays * 2 +
    rememberedMeals +
    stepDays +
    planDays * 2 +
    moneyDays +
    journalScore +
    scheduleSlots +
    integrationCount +
    goalCount +
    (typeof data.visionBoardText === 'string' && data.visionBoardText.trim() ? 1 : 0)
  );
};

const isSparseAppData = (data) => appDataWeight(data) === 0;

const CLOUD_BACKUP_KEEP = 8;
const CLOUD_BACKUP_MIN_MS = 15 * 60 * 1000;
const EXPORT_REMIND_MS = 7 * 24 * 60 * 60 * 1000;
const lastExportKey = (uid) => `rpg-tracker:last-export:${uid}`;
const backupsCol = (uid) => collection(db, 'users', uid, 'rpg_tracker_backups');

// Module scope so the collapsedTodos useState initializer can reach it without a TDZ error.
const defaultCollapsedTodoState = {
  today: false,
  week: true,
  should: true,
  could: true
};

export default function App() {
  // --- CLOUD STATE ---
  const [user, setUser] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [dbError, setDbError] = useState(null);

  // --- RPG STATE ---
  const [level, setLevel] = useState(1);
  const [xp, setXp] = useState(0);
  const [xpNeeded, setXpNeeded] = useState(100);
  
  // --- UI STATE ---
  const [activeTab, setActiveTab] = useState('todos');
  const [tabOrder, setTabOrder] = useState(() => [...DEFAULT_TAB_ORDER]);
  const [draggingTabId, setDraggingTabId] = useState(null);
  const [tabDropTargetId, setTabDropTargetId] = useState(null);
  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [notificationObj, setNotificationObj] = useState({ visible: false, message: '', isLevelUp: false, onUndo: null });
  const [calendarOffset, setCalendarOffset] = useState(0);
  // Pointer-drag state for tab reorder (refs only — no conditional hooks)
  const tabDragRef = useRef(null);
  const tabOrderRef = useRef(tabOrder);
  
  // --- HEADER STATE ---
  const [headerText, setHeaderText] = useState({ title: 'Your Journey', subtitle: 'Every step builds momentum.' });
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerEditForm, setHeaderEditForm] = useState({ title: '', subtitle: '' });

  // --- VISION BOARD STATE ---
  const [visionBoardText, setVisionBoardText] = useState('');
  const visionRef = useRef(null);

  // --- DAILY SCHEDULE STATE ---
  const [scheduleConfig, setScheduleConfig] = useState({ durationHours: 2, distribution: 'morning' });
  const [scheduleData, setScheduleData] = useState({});

  // --- CALENDAR (SPRINT BOARD) STATE ---
  // The ref is the baseline each edit is diffed against, so two edits in one tick
  // each send only their own date instead of the second re-sending the first.
  const [sprintBoard, setSprintBoard] = useState(() => normalizeSprintBoard(null));
  const sprintBoardRef = useRef(sprintBoard);

  // --- 3-DAY ADVANCE PLANNER STATE ---
  // dayPlans: { 'YYYY-MM-DD': { focus: '', notes: '', blocks: { '0': 'plan text', ... } } }
  const [dayPlans, setDayPlans] = useState({});
  const [planDayIndex, setPlanDayIndex] = useState(0); // 0, 1, 2 among next 3 days

  // Default health profile shape. Written as a literal (not via
  // normalizeHealthProfile, which is defined further down) because the
  // useState/useRef initializers below run before that definition executes.
  const DEFAULT_HEALTH_PROFILE = {
    conditions: '',
    dailyCalorieTarget: 1800,
    weightGoal: 'v_taper',
    customDeficit: 400,
    targetProteinGrams: null,
    targetCarbsGrams: null,
    targetFatGrams: null,
    heightCm: 167,
    weightKg: 67,
    age: 33,
    sex: 'male',
    activityLevel: 'moderate',
    autoCalorieTarget: true
  };

  // --- FOOD / MEAL TRACKER STATE ---
  // mealLog: { 'YYYY-MM-DD': [{ id, name, calories, protein, carbs, fat, mealType, createdAt }] }
  const [mealLog, setMealLog] = useState({});
  const [healthProfile, setHealthProfile] = useState({ ...DEFAULT_HEALTH_PROFILE });
  const [healthConditionsDraft, setHealthConditionsDraft] = useState('');
  // Raw text drafts for numeric profile inputs (calories/height/weight/age/macros).
  // Kept while the field is focused so clamping happens on blur, never
  // mid-keystroke — see commitProfileNumber near saveHealthProfile.
  const [profileNumberDrafts, setProfileNumberDrafts] = useState({});
  const [mealForm, setMealForm] = useState({
    name: '',
    mealType: 'lunch',
    portion: 'regular',
    caloriesOverride: null,
    protein: '',
    carbs: '',
    fat: '',
    isManual: false
  });
  const [mealDateOffset, setMealDateOffset] = useState(0); // 0 = today
  const [calorieMemory, setCalorieMemory] = useState(() => buildGlmCorrectedCalorieMemory());
  const [editingMeal, setEditingMeal] = useState(null); // { dateKey, id, name, calories, protein, carbs, fat }
  const [editingMemoryKey, setEditingMemoryKey] = useState(null);
  const [editingMemoryCalories, setEditingMemoryCalories] = useState('');
  const [editingMemoryName, setEditingMemoryName] = useState('');
  const [editingMemoryProtein, setEditingMemoryProtein] = useState('');
  const [editingMemoryCarbs, setEditingMemoryCarbs] = useState('');
  const [editingMemoryFat, setEditingMemoryFat] = useState('');
  const [showCalorieMemory, setShowCalorieMemory] = useState(true);
  const [mergingGlmMeals, setMergingGlmMeals] = useState(false);
  const [showHealthProfile, setShowHealthProfile] = useState(false);
  const [mealSort, setMealSort] = useState('logged'); // logged | high | low
  const [stepLog, setStepLog] = useState({});
  const [stepDraft, setStepDraft] = useState('');
  // moneyLog: { 'YYYY-MM-DD': { items: [string, string, string], updatedAt } }
  const [moneyLog, setMoneyLog] = useState({});
  // integrations: { [id]: { id, book, principle, lifeArea, notes, implementedOn, updatedAt, deleted } }
  const [integrations, setIntegrations] = useState({});
  // goals: { [id]: { id, title, notes, targetDate, updatedAt, deleted, items: { [itemId]: checklist row } } }
  const [goals, setGoals] = useState({});
  const [moneyDateOffset, setMoneyDateOffset] = useState(0);
  const [estimatingCalories, setEstimatingCalories] = useState(false);
  const [suggestingPlan, setSuggestingPlan] = useState(false);
  const [planAiMeta, setPlanAiMeta] = useState({}); // { [dateKey]: { source, updatedAt } }
  const [deepseekApiKey, setDeepseekApiKey] = useState('');
  const [cloudSaveState, setCloudSaveState] = useState({ status: 'idle', message: '' }); // idle | saving | saved | error
  const [cloudReady, setCloudReady] = useState(false);
  const [cloudBackups, setCloudBackups] = useState([]);
  const [showBackupPanel, setShowBackupPanel] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [lastExportAt, setLastExportAt] = useState(null);
  const [dismissExportRemind, setDismissExportRemind] = useState(false);

  // Latest Plan/Meals fields for race-safe Firebase writes (Cloudflare Pages multi-tab safe)
  const lifestyleRef = useRef({
    dayPlans: {},
    mealLog: {},
    healthProfile: { ...DEFAULT_HEALTH_PROFILE },
    calorieMemory: {},
    stepLog: {},
    moneyLog: {},
    deepseekApiKey: '',
    scheduleConfig: { durationHours: 2, distribution: 'morning' },
    scheduleData: {}
  });
  // The date-keyed maps as the last server snapshot showed them. This is the ONLY
  // baseline a per-date write may narrow against. lifestyleRef is not usable: every
  // caller (saveDaySteps, persistMealLogToCloud, persistMoneyLogToCloud,
  // scheduleDayPlansCloudSave) writes the new value into lifestyleRef *before*
  // calling persistLifestyleToCloud, so diffing against it always yields zero
  // changed dates and the write is silently dropped while the UI reports "synced".
  // Empty here means "cloud state unknown", which correctly sends every date.
  const lastCloudDateMapsRef = useRef({});
  const dayPlansSaveTimerRef = useRef(null);
  // Day-plan saves are debounced, so one cloud write covers a batch of edits. The
  // delete baseline must therefore be the state before the FIRST edit of the batch —
  // taking it from the last edit would miss a day cleared earlier in the same window.
  // null means "no baseline", which deletes nothing.
  const dayPlansDeleteBaselineRef = useRef(null);
  const moneyLogSaveTimerRef = useRef(null);
  const pendingCloudWritesRef = useRef(0);
  // True only after a *server* Firestore snapshot. Blocks empty-cache uploads
  // on a fresh Cloudflare Pages origin (empty localStorage / IndexedDB).
  const cloudHydratedRef = useRef(false);
  // Existing cache snapshot is enough for merge-writes; full-doc upload still waits
  // for a server snapshot so an empty new origin cannot clobber Firebase.
  const writeReadyRef = useRef(false);
  const pendingCloudUpdatesRef = useRef([]);
  // Set when Firestore answers resource-exhausted. Suppresses reads and writes
  // until the probe window elapses, so a fresh daily quota is not spent instantly.
  const quotaBlockedUntilRef = useRef(0);
  // Signature of the last payload Firestore confirmed, so an identical repeat
  // (the signature of a sync loop) is skipped instead of re-written.
  const lastWriteSignatureRef = useRef('');
  // Rolling write ledger, persisted so a reload cannot reset the budget.
  const writeLedgerRef = useRef(null);
  const lastCloudBackupAtRef = useRef(0);
  const lastCloudBackupWeightRef = useRef(-1);
  const cloudBackupTimerRef = useRef(null);
  // Latest habits for race-safe Firebase writes (same problem meals had).
  const habitsRef = useRef([]);
  // Latest journal for race-safe Firebase writes (import + Save Journal).
  const journalRef = useRef({});
  // Latest integrated-book map. Saves write this before React re-renders.
  const integrationsRef = useRef({});
  const suppressRemoteIntegrationsUntilRef = useRef(0);
  // Latest goals map, including checklist rows. Saves write this before React re-renders.
  const goalsRef = useRef({});
  const suppressRemoteGoalsUntilRef = useRef(0);
  // Latest quests so a lagging snapshot cannot drop an add still flushing to Firebase.
  const todosRef = useRef([]);
  const todosDeletedIdsRef = useRef(new Set());
  const todoMapPushSigRef = useRef('');
  const writeTraceRef = useRef([]);
  // While user is editing Plan/Meals/Training, don't let an older onSnapshot wipe keystrokes
  const suppressRemoteLifestyleUntilRef = useRef(0);
  const suppressRemoteTodosUntilRef = useRef(0);
  // Hold imported / just-saved journal over a stale Firestore cache echo.
  const suppressRemoteJournalUntilRef = useRef(0);
  // After JSON import / backup restore, keep the whole local snapshot authoritative
  // until Firebase has the write (IndexedDB often replays the previous doc first).
  const suppressRemoteAppUntilRef = useRef(0);

  // Hold the local Plan/Meals copy authoritative for a while so a snapshot landing
  // mid-edit (or mid-write) can't reset state to the older server value.
  // Math.max so a short touch never shortens a longer guard already in place.
  const touchLifestyleEdit = (ms = 2000) => {
    suppressRemoteLifestyleUntilRef.current = Math.max(
      suppressRemoteLifestyleUntilRef.current,
      Date.now() + ms
    );
  };

  const touchJournalEdit = (ms = 2000) => {
    suppressRemoteJournalUntilRef.current = Math.max(
      suppressRemoteJournalUntilRef.current,
      Date.now() + ms
    );
  };

  const touchIntegrationsEdit = (ms = 2000) => {
    suppressRemoteIntegrationsUntilRef.current = Math.max(
      suppressRemoteIntegrationsUntilRef.current,
      Date.now() + ms
    );
  };

  const touchGoalsEdit = (ms = 2000) => {
    suppressRemoteGoalsUntilRef.current = Math.max(
      suppressRemoteGoalsUntilRef.current,
      Date.now() + ms
    );
  };

  const touchTodosEdit = (ms = 8000) => {
    suppressRemoteTodosUntilRef.current = Math.max(
      suppressRemoteTodosUntilRef.current,
      Date.now() + ms
    );
  };

  const touchAppImport = (ms = 15000) => {
    const until = Date.now() + ms;
    suppressRemoteAppUntilRef.current = Math.max(suppressRemoteAppUntilRef.current, until);
    touchLifestyleEdit(ms);
    touchJournalEdit(ms);
    touchIntegrationsEdit(ms);
    touchGoalsEdit(ms);
    touchTodosEdit(ms);
  };

  const applyLifestyleFrom = (data) => {
    lifestyleRef.current = {
      ...lifestyleRef.current,
      dayPlans: data?.dayPlans || {},
      mealLog: data?.mealLog || {},
      healthProfile: resolveAutoVTaperTarget(data?.healthProfile || { ...DEFAULT_HEALTH_PROFILE }),
      calorieMemory: data?.calorieMemory || {},
      stepLog: data?.stepLog || {},
      moneyLog: data?.moneyLog || {},
      deepseekApiKey: data?.deepseekApiKey || '',
      scheduleConfig: data?.scheduleConfig || { durationHours: 2, distribution: 'morning' },
      scheduleData: data?.scheduleData || {}
    };
    habitsRef.current = Array.isArray(data?.habits) ? data.habits : habitsRef.current;
  };

  // --- GEMINI API STATE ---
  const [breakingDownId, setBreakingDownId] = useState(null);
  const [isConsulting, setIsConsulting] = useState(false);
  const [questMasterAdvice, setQuestMasterAdvice] = useState(null);

  // --- DRAG & EDIT STATE ---
  const [draggedItemIdx, setDraggedItemIdx] = useState(null); // Retained for Habits
  const [dragOverItemIdx, setDragOverItemIdx] = useState(null); // Retained for Habits

  // Todo specific Drag & Group State
  const [collapsedTodos, setCollapsedTodos] = useState(defaultCollapsedTodoState);
  
  // Todo Edit State
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editForm, setEditForm] = useState({ text: '', label: 'today', customTag: '', timeOfDay: '', dayOfWeek: '', allocatedTime: '' });

  // Habit Edit State
  const [editingHabitId, setEditingHabitId] = useState(null);
  const [editingHabitFrequency, setEditingHabitFrequency] = useState('daily');
  const [editingHabitWeeklyTarget, setEditingHabitWeeklyTarget] = useState(3);
  const [habitEditForm, setHabitEditForm] = useState('');
  const [editingStreakHabitId, setEditingStreakHabitId] = useState(null);
  const [streakEditForm, setStreakEditForm] = useState('');
  
  // --- CUSTOM DRAG STATE FOR TODOS ---
  const [dragActive, setDragActive] = useState(false);
  const [dragItemId, setDragItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'above' or 'below'
  const [dragOverGroupId, setDragOverGroupId] = useState(null);   // group the pointer is currently over
  const [dragRejected, setDragRejected] = useState(false);        // hovering a group that refuses this task

  // --- DOMINO RUN STATE & CONFIG ---
  // `goal` is the user's own clearly-stated goal; `deadline` / `deadlineMonths` are
  // the horizon the AI judged realistic for it. The year3/year2 and time-of-day keys
  // are legacy: kept in the row so existing cloud documents round-trip untouched,
  // but no longer part of the rendered ladder (see dominoConfig).
  const defaultDominoRow = { goal: '', deadline: '', deadlineMonths: '', year3: '', year2: '', year1: '', month: '', week: '', day: '', morning: '', afternoon: '', evening: '', night: '' };
  const defaultDominoLabels = { year3: '3 Years', year2: '2 Years', year1: '1 Year', month: 'Monthly', week: 'Weekly', day: 'Daily', morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night' };
  const defaultCategoryLabels = { health: 'Health Goals', financial: 'Financial Goals', relationship: 'Relationship Goals', hobby1: 'Hobby Goals (1)', hobby2: 'Hobby Goals (2)' };

  const getInitialDominos = () => ({
    health: { ...defaultDominoRow }, financial: { ...defaultDominoRow }, relationship: { ...defaultDominoRow }, hobby1: { ...defaultDominoRow }, hobby2: { ...defaultDominoRow }
  });
  const getInitialDominoLabels = () => ({
    health: { ...defaultDominoLabels }, financial: { ...defaultDominoLabels }, relationship: { ...defaultDominoLabels }, hobby1: { ...defaultDominoLabels }, hobby2: { ...defaultDominoLabels }
  });

  const defaultCompletedRow = { year3: false, year2: false, year1: false, month: false, week: false, day: false, morning: false, afternoon: false, evening: false, night: false };
  const getInitialCompletedDominos = () => ({
    health: { ...defaultCompletedRow }, financial: { ...defaultCompletedRow }, relationship: { ...defaultCompletedRow }, hobby1: { ...defaultCompletedRow }, hobby2: { ...defaultCompletedRow }
  });

  const [dominos, setDominos] = useState(getInitialDominos());
  const [dominoLabels, setDominoLabels] = useState(getInitialDominoLabels());
  const [completedDominos, setCompletedDominos] = useState(getInitialCompletedDominos());
  const [categoryLabels, setCategoryLabels] = useState(defaultCategoryLabels);
  const [collapsedCategories, setCollapsedCategories] = useState({ health: true, financial: true, relationship: true, hobby1: true, hobby2: true });
  
  const [selectedDomino, setSelectedDomino] = useState(null);
  const [editingDominoText, setEditingDominoText] = useState("");
  const [editingDominoLabel, setEditingDominoLabel] = useState("");
  
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryEditForm, setCategoryEditForm] = useState("");
  const [generatingDominos, setGeneratingDominos] = useState(null);
  // Un-saved goal text per category. Absent key = fall back to the stored goal, so
  // a cloud snapshot arriving mid-typing cannot wipe what is in the box.
  const [goalDrafts, setGoalDrafts] = useState({});
  const [userApiKey, setUserApiKey] = useState("");
  // Second Gemini key on a different Google account. Free-tier quota is per
  // project, so a spare key is a whole extra daily allowance rather than a retry.
  const [userApiKey2, setUserApiKey2] = useState("");

  const dominoCategories = [
    { id: 'health', icon: <Heart className="w-4 h-4 text-rose-500" /> },
    { id: 'financial', icon: <DollarSign className="w-4 h-4 text-emerald-500" /> },
    { id: 'relationship', icon: <Users className="w-4 h-4 text-blue-500" /> },
    { id: 'hobby1', icon: <Palette className="w-4 h-4 text-purple-500" /> },
    { id: 'hobby2', icon: <Gamepad2 className="w-4 h-4 text-amber-500" /> }
  ];

  // The run is deadline → monthly → weekly → daily. The deadline circle's label is
  // whatever horizon the AI judged realistic for the goal (defaults to "1 Year").
  const dominoConfig = [
    { key: 'year1', size: 64 }, { key: 'month', size: 52 }, { key: 'week', size: 44 }, { key: 'day', size: 36 }
  ];

  // --- DATA STATE ---
  const [todos, setTodos] = useState([]);
  const [habits, setHabits] = useState([]);
  const [newItemText, setNewItemText] = useState('');
  const [newTaskLabel, setNewTaskLabel] = useState('today');
  const [customTag, setCustomTag] = useState(''); 
  const [newTaskTimeOfDay, setNewTaskTimeOfDay] = useState(''); 
  const [newTaskDayOfWeek, setNewTaskDayOfWeek] = useState(''); 
  const [newTaskAllocatedTime, setNewTaskAllocatedTime] = useState('');

  const [habitFrequency, setHabitFrequency] = useState('daily');   // 'daily' or 'weekly'
  const [habitWeeklyTarget, setHabitWeeklyTarget] = useState(3);   // 1-7, only used if weekly

  const defaultJournalEntry = { expectations: '', accomplished: '', updatedAt: null };

  const [monthlyJournal, setMonthlyJournal] = useState({});
  const [journalMonthOffset, setJournalMonthOffset] = useState(0);
  const [journalDraft, setJournalDraft] = useState(defaultJournalEntry);

  const [penaltyMeta, setPenaltyMeta] = useState({
    lastAuditDate: null
  });
  
  const defaultCollapsedCategoryState = {
  health: true,
  financial: true,
  relationship: true,
  hobby1: true,
  hobby2: true
};

const mergeNestedRows = (factory, incoming) => {
  const safe = factory();

  if (incoming && typeof incoming === 'object' && !Array.isArray(incoming)) {
    Object.keys(incoming).forEach((key) => {
      if (safe[key] && incoming[key] && typeof incoming[key] === 'object' && !Array.isArray(incoming[key])) {
        safe[key] = { ...safe[key], ...incoming[key] };
      }
    });
  }

  return safe;
};

const normalizeTodos = (input) => {
  if (!Array.isArray(input)) return [];
  return input.map((todo, idx) => ({
    ...todo,
    id: todo?.id ?? Date.now() + idx,
    text: typeof todo?.text === 'string' ? todo.text : '',
    completed: !!todo?.completed,
    label: ['today', 'week', 'should', 'could'].includes(todo?.label) ? todo.label : 'today',
    customTag: typeof todo?.customTag === 'string' ? todo.customTag : '',
    timeSpent: Number.isFinite(Number(todo?.timeSpent)) ? Number(todo.timeSpent) : 0,
    isRunning: !!todo?.isRunning,
    lastTick: todo?.lastTick ?? null,
    timeOfDay: typeof todo?.timeOfDay === 'string' ? todo.timeOfDay : '',
    dayOfWeek: typeof todo?.dayOfWeek === 'string' ? todo.dayOfWeek : '',
    allocatedTime: Number.isFinite(Number(todo?.allocatedTime)) ? Number(todo.allocatedTime) : 0,
    updatedAt: typeof todo?.updatedAt === 'string' ? todo.updatedAt : null
  }));
};

const normalizeHabits = (input) => {
  if (!Array.isArray(input)) return [];
  return input.map((habit, idx) => {
    // Determine frequency (default 'daily' for old habits)
    const frequency = (habit.frequency === 'weekly' || habit.frequency === 'daily') 
      ? habit.frequency 
      : 'daily';
    
    // Weekly target (1-7) – for daily we set null
    let weeklyTarget = null;
    if (frequency === 'weekly') {
      weeklyTarget = Number.isFinite(Number(habit.weeklyTarget)) 
        ? Math.min(7, Math.max(1, Math.floor(Number(habit.weeklyTarget)))) 
        : 3;
    }
    
    // Weekly progress object: { weekStartDate, completionsCount }
    let weeklyProgress = { weekStartDate: null, completionsCount: 0 };
    if (habit.weeklyProgress && typeof habit.weeklyProgress === 'object') {
      weeklyProgress = {
        weekStartDate: habit.weeklyProgress.weekStartDate ?? null,
        completionsCount: Number.isFinite(Number(habit.weeklyProgress.completionsCount))
          ? Math.max(0, Math.floor(Number(habit.weeklyProgress.completionsCount)))
          : 0
      };
    }
    
    return {
      ...habit,
      id: habit?.id ?? Date.now() + idx,
      text: typeof habit?.text === 'string' ? habit.text : '',
      streak: Number.isFinite(Number(habit?.streak)) ? Math.max(0, Math.floor(Number(habit.streak))) : 0,
      lastCompletedDate: habit?.lastCompletedDate ?? null,
      history: Array.isArray(habit?.history) ? habit.history : [],
      penaltyHistory: Array.isArray(habit?.penaltyHistory) ? habit.penaltyHistory : [],
      frequency,               // new
      weeklyTarget,            // new (null for daily)
      weeklyProgress           // new
    };
  });
};

const normalizeMonthlyJournal = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const safe = {};
  Object.entries(input).forEach(([monthKey, entry]) => {
    safe[monthKey] = {
      expectations: typeof entry?.expectations === 'string' ? entry.expectations : '',
      accomplished: typeof entry?.accomplished === 'string' ? entry.accomplished : '',
      updatedAt: entry?.updatedAt ?? null
    };
  });

  return safe;
};

const normalizeStats = (input) => ({
  level: Number.isFinite(Number(input?.level)) && Number(input.level) > 0 ? Number(input.level) : 1,
  xp: Number.isFinite(Number(input?.xp)) && Number(input.xp) >= 0 ? Number(input.xp) : 0,
  xpNeeded: Number.isFinite(Number(input?.xpNeeded)) && Number(input.xpNeeded) > 0 ? Number(input.xpNeeded) : 100
});

const normalizeHeader = (input) => ({
  title: typeof input?.title === 'string' && input.title.trim() ? input.title : 'Your Journey',
  subtitle:
    typeof input?.subtitle === 'string' && input.subtitle.trim()
      ? input.subtitle
      : 'Every step builds momentum.'
});

const normalizePenaltyMeta = (input) => ({
  lastAuditDate: input?.lastAuditDate ?? null
});

const normalizeScheduleData = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const safe = {};
  Object.keys(input).forEach(key => {
    const block = input[key];
    if (block && typeof block === 'object') {
      safe[key] = {
        time: typeof block.time === 'string' ? block.time : '',
        todoId: block.todoId !== undefined && block.todoId !== null ? String(block.todoId) : ''
      };
    }
  });
  return safe;
};

// --- SCHEDULE DISTRIBUTION (period labels for the Daily Schedule blocks) ---
const PERIOD_BY_CODE = { M: 'Morning', A: 'Afternoon', E: 'Evening', N: 'Night' };
const PERIOD_CODES = ['M', 'A', 'E', 'N'];

// All ordered 3-part splits of the four periods, minus the all-same ones (those
// are the friendlier "All in X" options). e.g. "MMN" → Morning / Morning / Night.
const SPLIT_DISTRIBUTIONS = (() => {
  const out = [];
  PERIOD_CODES.forEach((a) => PERIOD_CODES.forEach((b) => PERIOD_CODES.forEach((c) => {
    if (a === b && b === c) return;
    out.push(`${a}${b}${c}`);
  })));
  return out;
})();

const parseSplitCodes = (distribution) => {
  if (typeof distribution === 'string' && distribution.startsWith('split:')) {
    const codes = distribution.slice(6).toUpperCase().split('').filter((c) => PERIOD_BY_CODE[c]);
    if (codes.length === 3) return codes;
  }
  if (distribution === 'split') return ['M', 'A', 'N']; // legacy value
  return null;
};

const splitLabel = (code) => code.split('').map((c) => PERIOD_BY_CODE[c]).join(' / ');

const KCAL_PER_STEP = 0.04; // ~280 kcal at 7,000 steps for an average adult walk

const stepsToCalories = (steps) =>
  Math.max(0, Math.round((Number(steps) || 0) * KCAL_PER_STEP));

// --- V-TAPER CALORIE DEFICIT & MACRO MATH (Strict Kinobody Standard) ---
const V_TAPER_DEFICIT_KCAL = 400; // Kinobody sweet spot: burns fat steadily while protecting strength & testosterone (~0.36 kg fat/wk)
const KCAL_PER_KG_FAT = 7700;
const roundTo10 = (n) => Math.round(n / 10) * 10;
const roundTo5 = (n) => Math.round(n / 5) * 5;

// Full V-taper plan: calculates maintenance (TDEE = BW (lbs) × 15 for 10k steps/day), daily deficit,
// cutting target, protein/fat/carb macro distributions, and 4-meal non-fasting pacing budgets.
const computeVTaperPlan = (profile = {}) => {
  const weightKg = Number.isFinite(Number(profile?.weightKg)) && Number(profile.weightKg) > 0 ? Number(profile.weightKg) : 67;
  const deficitKcal = Number.isFinite(Number(profile?.customDeficit)) && Number(profile.customDeficit) >= 0 ? Number(profile.customDeficit) : V_TAPER_DEFICIT_KCAL;

  const weightLbs = weightKg * 2.20462;

  // Kinobody Standard: Maintenance TDEE = Bodyweight (lbs) × 15 (for 10k steps/day + 3 workouts/wk)
  const tdee = roundTo5(weightLbs * 15);
  const target = Math.max(800, Math.min(6000, roundTo5(tdee - deficitKcal)));
  const actualDeficit = tdee - target;
  const deficitPct = tdee > 0 ? Math.round((actualDeficit / tdee) * 100) : 0;

  // Macros calculation:
  // Kinobody protein rule: 0.82g per lb of bodyweight
  const defaultProtein = Math.round(weightLbs * 0.82);
  const targetProtein = Number.isFinite(Number(profile?.targetProteinGrams)) && Number(profile.targetProteinGrams) > 0
    ? clampMacro(profile.targetProteinGrams)
    : defaultProtein;

  // Fat target: ~28% of target calories -> 9 kcal/g
  const defaultFat = Math.max(20, Math.round((target * 0.28) / 9));
  const targetFat = Number.isFinite(Number(profile?.targetFatGrams)) && Number(profile.targetFatGrams) > 0
    ? clampMacro(profile.targetFatGrams)
    : defaultFat;

  // Carbs target: remainder of target calories -> 4 kcal/g
  const remainingCals = Math.max(0, target - (targetProtein * 4) - (targetFat * 9));
  const defaultCarbs = Math.round(remainingCals / 4);
  const targetCarbs = Number.isFinite(Number(profile?.targetCarbsGrams)) && Number(profile.targetCarbsGrams) > 0
    ? clampMacro(profile.targetCarbsGrams)
    : defaultCarbs;

  // 4-Meal Non-Fasting Budget Breakdown (Breakfast ~25%, Lunch ~30%, Afternoon Snack ~15%, Dinner ~30%):
  const breakfastBudget = roundTo10(target * 0.25);
  const lunchBudget = roundTo10(target * 0.30);
  const snackBudget = roundTo10(target * 0.15);
  const dinnerBudget = Math.max(0, target - breakfastBudget - lunchBudget - snackBudget);

  // Crash diet detection: Kinobody strictly bans <1,400 kcal cuts
  const isCrashDiet = target < 1400 || (deficitPct >= 28 && actualDeficit >= 600);

  return {
    formula: 'kinobody',
    tdee,
    weightLbs: Math.round(weightLbs * 10) / 10,
    deficit: actualDeficit,
    deficitPct,
    target,
    weeklyFatLossKg: Math.round(((actualDeficit * 7) / KCAL_PER_KG_FAT) * 100) / 100,
    macros: {
      protein: targetProtein,
      fat: targetFat,
      carbs: targetCarbs,
      defaultProtein,
      defaultFat,
      defaultCarbs
    },
    mealBudgets: {
      breakfast: breakfastBudget,
      lunch: lunchBudget,
      snack: snackBudget,
      dinner: dinnerBudget
    },
    isCrashDiet
  };
};

// Normalizes a profile and, when the V-taper goal is active, strictly enforces
// the computed Kinobody target so manual desync is impossible.
const resolveAutoVTaperTarget = (profile) => {
  const p = normalizeHealthProfile(profile);
  return p.weightGoal === 'v_taper'
    ? { ...p, dailyCalorieTarget: computeVTaperPlan(p).target, autoCalorieTarget: true }
    : p;
};

const normalizeStepLog = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const safe = {};
  Object.entries(input).forEach(([dateKey, entry]) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) return;
    const raw = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry.steps : entry;
    const steps = Number.isFinite(Number(raw)) ? Math.max(0, Math.min(200000, Math.round(Number(raw)))) : 0;
    if (steps <= 0) return;
    safe[dateKey] = {
      steps,
      updatedAt: typeof entry?.updatedAt === 'string' ? entry.updatedAt : null
    };
  });
  return safe;
};

const mergeStepLog = (local, remote) => {
  const localNorm = normalizeStepLog(local);
  const remoteNorm = normalizeStepLog(remote);
  const out = { ...remoteNorm };
  Object.entries(localNorm).forEach(([dateKey, entry]) => {
    const cloudEntry = out[dateKey];
    if (!cloudEntry) {
      out[dateKey] = entry;
    } else {
      const localTime = entry.updatedAt ? new Date(entry.updatedAt).getTime() : 0;
      const cloudTime = cloudEntry.updatedAt ? new Date(cloudEntry.updatedAt).getTime() : 0;
      if (localTime >= cloudTime || !cloudEntry.steps) {
        out[dateKey] = entry;
      }
    }
  });
  return normalizeStepLog(out);
};

const mergeTodos = (local, remote, cloudUpdatedAt, opts) =>
  mergeInFlightTodos(local, remote, cloudUpdatedAt, opts);

const mergeHealthProfile = (local, remote) => {
  const localNorm = normalizeHealthProfile(local);
  const remoteNorm = normalizeHealthProfile(remote);
  return {
    ...remoteNorm,
    conditions: (remoteNorm.conditions && remoteNorm.conditions.trim())
      ? remoteNorm.conditions
      : (localNorm.conditions || '')
  };
};

// Long enough that any device syncing inside the window learns the deletion; a device
// dormant longer than this re-adds the meal, which is the lesser failure.
const MEAL_TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const normalizeMealLog = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const safe = {};
  Object.entries(input).forEach(([dateKey, entry]) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) return;
    const meals = Array.isArray(entry)
      ? entry
      : (Array.isArray(entry?.meals) ? entry.meals : []);
    if (!meals.length) return;
    safe[dateKey] = meals.map((meal, idx) => ({
      id: meal?.id !== undefined && meal?.id !== null ? String(meal.id) : `${dateKey}-${idx}`,
      name: typeof meal?.name === 'string' ? meal.name : '',
      calories: Number.isFinite(Number(meal?.calories)) ? Math.max(0, Math.round(Number(meal.calories))) : 0,
      protein: clampMacro(meal?.protein),
      carbs: clampMacro(meal?.carbs),
      fat: clampMacro(meal?.fat),
      mealType: ['breakfast', 'lunch', 'dinner', 'snack'].includes(meal?.mealType) ? meal.mealType : 'snack',
      createdAt: typeof meal?.createdAt === 'string' ? meal.createdAt : (meal?.createdAt ? String(meal.createdAt) : null),
      caloriesSource: ['gemini', 'deepseek', 'local', 'manual', 'sg-db', 'saved', 'similar'].includes(meal?.caloriesSource) ? meal.caloriesSource : 'local',
      caloriesNote: typeof meal?.caloriesNote === 'string' ? meal.caloriesNote : '',
      caloriesMin: Number.isFinite(Number(meal?.caloriesMin)) ? Math.max(0, Math.round(Number(meal.caloriesMin))) : null,
      caloriesMax: Number.isFinite(Number(meal?.caloriesMax)) ? Math.max(0, Math.round(Number(meal.caloriesMax))) : null
    })).filter(m => m.name.trim() || m.calories > 0);
    // A deleted meal is kept as a tombstone, not dropped. mergeMealLog unions by id,
    // so a meal merely absent from the cloud reads as "the cloud needs this" and the
    // other device re-uploads it — the deletion undoes itself on the next snapshot.
    // Tombstones carry no name and no calories, so every consumer that sums or lists
    // meals already skips them; getMealsForDate filters them out of the UI.
    const stones = meals
      .filter((m) => m?.deleted === true && m?.id !== undefined && m?.id !== null)
      .map((m) => ({
        id: String(m.id),
        deleted: true,
        updatedAt: typeof m.updatedAt === 'string' ? m.updatedAt : new Date().toISOString()
      }))
      .filter((m) => {
        const age = Date.now() - new Date(m.updatedAt).getTime();
        return !Number.isFinite(age) || age <= MEAL_TOMBSTONE_TTL_MS;
      });
    const live = safe[dateKey] || [];
    if (!live.length && !stones.length) delete safe[dateKey];
    else safe[dateKey] = [...live, ...stones];
  });
  return safe;
};

const normalizeMoneyLog = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const safe = {};
  Object.entries(input).forEach(([dateKey, entry]) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) return;
    const rawItems = Array.isArray(entry?.items)
      ? entry.items
      : (Array.isArray(entry) ? entry : []);
    const items = [0, 1, 2].map((i) => (typeof rawItems[i] === 'string' ? rawItems[i] : ''));
    const updatedAt = typeof entry?.updatedAt === 'string' ? entry.updatedAt : null;
    if (!items.some((text) => text.trim()) && !updatedAt) return;
    safe[dateKey] = { items, updatedAt };
  });
  return safe;
};

const normalizeHealthProfile = (input) => {
  const weightGoal = ['lose', 'maintain', 'gain', 'v_taper'].includes(input?.weightGoal) ? input.weightGoal : 'v_taper';
  const customDeficit = Number.isFinite(Number(input?.customDeficit)) && Number(input.customDeficit) >= 0
    ? Math.round(Number(input.customDeficit))
    : 400;
  const heightCm = Number.isFinite(Number(input?.heightCm)) && Number(input.heightCm) > 0
    ? Math.max(100, Math.min(250, Math.round(Number(input.heightCm))))
    : 167;
  const weightKg = Number.isFinite(Number(input?.weightKg)) && Number(input.weightKg) > 0
    ? Math.max(30, Math.min(300, Math.round(Number(input.weightKg) * 10) / 10))
    : 67;
  const age = Number.isFinite(Number(input?.age)) && Number(input.age) > 0
    ? Math.max(13, Math.min(100, Math.round(Number(input.age))))
    : 33;
  const sex = input?.sex === 'female' ? 'female' : 'male';
  const activityLevel = ['sedentary', 'light', 'moderate', 'active'].includes(input?.activityLevel) ? input.activityLevel : 'moderate';

  const base = {
    conditions: typeof input?.conditions === 'string' ? input.conditions : '',
    weightGoal,
    customDeficit,
    targetProteinGrams: clampMacro(input?.targetProteinGrams),
    targetCarbsGrams: clampMacro(input?.targetCarbsGrams),
    targetFatGrams: clampMacro(input?.targetFatGrams),
    heightCm,
    weightKg,
    age,
    sex,
    activityLevel,
    autoCalorieTarget: true
  };

  const computedPlan = computeVTaperPlan(base);
  const dailyCalorieTarget = weightGoal === 'v_taper'
    ? computedPlan.target
    : (Number.isFinite(Number(input?.dailyCalorieTarget)) && Number(input.dailyCalorieTarget) > 0
        ? Math.round(Number(input.dailyCalorieTarget))
        : computedPlan.target);

  return {
    ...base,
    dailyCalorieTarget
  };
};

const repairZeroMacros = (memory) => {
  if (!memory || typeof memory !== 'object' || Array.isArray(memory)) return memory;
  const glmMap = {};
  GLM_CORRECTED_SAVED_MEALS.forEach((m) => {
    if (m?.name) {
      glmMap[m.name.toLowerCase()] = m;
      if (Array.isArray(m.aliases)) {
        m.aliases.forEach((alias) => {
          if (alias) glmMap[alias.toLowerCase()] = m;
        });
      }
    }
  });
  const repaired = { ...memory };
  Object.keys(repaired).forEach((key) => {
    const entry = repaired[key];
    if (!entry || typeof entry !== 'object') return;
    // A tombstone carries no macros by definition, and its key can match a GLM name
    // when the meal being forgotten was a seeded one. Repairing it would be a no-op
    // the normalizer undoes anyway, so skip it outright.
    if (entry.deleted === true) return;
    // Only repair entries where ALL macros are 0 or null
    const hasNoMacros = (entry.protein == null || entry.protein === 0)
      && (entry.carbs == null || entry.carbs === 0)
      && (entry.fat == null || entry.fat === 0);
    if (!hasNoMacros) return;
    // Try exact name match from GLM list or aliases
    const lookupKey = String(entry.name || key).toLowerCase().trim();
    const glm = glmMap[lookupKey];
    if (glm) {
      repaired[key] = {
        ...entry,
        protein: glm.protein ?? entry.protein,
        carbs: glm.carbs ?? entry.carbs,
        fat: glm.fat ?? entry.fat,
      };
    }
  });
  return repaired;
};

const normalizeAppData = (raw = {}) => {
  const resolvedTodos = mergeTodoState(normalizeTodos(raw.todos), [], {
    cloudMap: raw.todoMap
  });
  return {
  todos: resolvedTodos.list,
  todoMap: resolvedTodos.map,
  habits: normalizeHabits(raw.habits),
  stats: normalizeStats(raw.stats),
  header: normalizeHeader(raw.header),
  // Migrate BEFORE merging defaults: the merge gives every row `goal: ''`, after
  // which "never had a goal" and "cleared the goal" are indistinguishable.
  dominos: mergeNestedRows(getInitialDominos, migrateDominoGoals(raw.dominos)),
  dominoLabels: mergeNestedRows(getInitialDominoLabels, raw.dominoLabels),
  completedDominos: mergeNestedRows(getInitialCompletedDominos, raw.completedDominos),
  categoryLabels: {
    ...defaultCategoryLabels,
    ...(raw.categoryLabels && typeof raw.categoryLabels === 'object' && !Array.isArray(raw.categoryLabels)
      ? raw.categoryLabels
      : {})
  },
  collapsedCategories: {
    ...defaultCollapsedCategoryState,
    ...(raw.collapsedCategories &&
    typeof raw.collapsedCategories === 'object' &&
    !Array.isArray(raw.collapsedCategories)
      ? raw.collapsedCategories
      : {})
  },
  collapsedTodos: {
    ...defaultCollapsedTodoState,
    ...(raw.collapsedTodos &&
    typeof raw.collapsedTodos === 'object' &&
    !Array.isArray(raw.collapsedTodos)
      ? raw.collapsedTodos
      : {})
  },
  monthlyJournal: normalizeMonthlyJournal(raw.monthlyJournal),
  penaltyMeta: normalizePenaltyMeta(raw.penaltyMeta),
  userApiKey: typeof raw.userApiKey === 'string' ? raw.userApiKey : '',
  userApiKey2: typeof raw.userApiKey2 === 'string' ? raw.userApiKey2 : '',
  deepseekApiKey: typeof raw.deepseekApiKey === 'string' ? raw.deepseekApiKey : '',
  visionBoardText: typeof raw.visionBoardText === 'string' ? raw.visionBoardText : '',
  scheduleConfig: {
    durationHours: Number.isFinite(Number(raw.scheduleConfig?.durationHours)) ? Number(raw.scheduleConfig.durationHours) : 2,
    distribution: raw.scheduleConfig?.distribution === 'split'
      ? 'split:MAN'
      : (typeof raw.scheduleConfig?.distribution === 'string' ? raw.scheduleConfig.distribution : 'morning')
  },
  scheduleData: normalizeScheduleData(raw.scheduleData),
  dayPlans: normalizeDayPlans(raw.dayPlans),
  mealLog: normalizeMealLog(raw.mealLog),
  healthProfile: normalizeHealthProfile(raw.healthProfile),
  calorieMemory: repairZeroMacros(normalizeCalorieMemory(
    raw.calorieMemory || (Array.isArray(raw.savedMealsDatabase)
      ? raw.savedMealsDatabase.reduce((acc, m) => {
          if (m?.name) acc[m.name] = m;
          return acc;
        }, {})
      : raw.foods && Array.isArray(raw.foods)
        ? raw.foods.reduce((acc, f) => {
            if (f?.name) acc[f.name] = { ...f, calories: f.kcal };
            return acc;
          }, {})
        : {})
  )),
  stepLog: normalizeStepLog(raw.stepLog),
  moneyLog: normalizeMoneyLog(raw.moneyLog),
  tabOrder: normalizeTabOrder(raw.tabOrder),
  sprintBoard: normalizeSprintBoard(raw.sprintBoard),
  integrations: normalizeIntegrations(raw.integrations),
  goals: normalizeGoals(raw.goals)
  };
};

const applyAppDataToState = (safeData) => {
  setTodos(safeData.todos);
  todosRef.current = Array.isArray(safeData.todos) ? safeData.todos : [];
  setHabits(safeData.habits);
  habitsRef.current = Array.isArray(safeData.habits) ? safeData.habits : [];
  setLevel(safeData.stats.level);
  setXp(safeData.stats.xp);
  setXpNeeded(safeData.stats.xpNeeded);
  setHeaderText(safeData.header);
  setDominos(safeData.dominos);
  setDominoLabels(safeData.dominoLabels);
  setCompletedDominos(safeData.completedDominos);
  setCategoryLabels(safeData.categoryLabels);
  setCollapsedCategories(safeData.collapsedCategories);
  setCollapsedTodos(safeData.collapsedTodos);
  journalRef.current = safeData.monthlyJournal || {};
  setMonthlyJournal(safeData.monthlyJournal);
  setPenaltyMeta(safeData.penaltyMeta);
  setUserApiKey(safeData.userApiKey);
  setUserApiKey2(safeData.userApiKey2 || '');
  setDeepseekApiKey(safeData.deepseekApiKey || '');
  // SET NEW STATE FIELDS
  setVisionBoardText(safeData.visionBoardText);
  setScheduleConfig(safeData.scheduleConfig);
  setScheduleData(safeData.scheduleData);
  setDayPlans(safeData.dayPlans);
  setMealLog(safeData.mealLog);
  setCalorieMemory(safeData.calorieMemory || {});
  setStepLog(safeData.stepLog || {});
  setHealthProfile(resolveAutoVTaperTarget(safeData.healthProfile));
  setHealthConditionsDraft(safeData.healthProfile.conditions);
  setMoneyLog(safeData.moneyLog || {});
  integrationsRef.current = safeData.integrations || {};
  setIntegrations(integrationsRef.current);
  goalsRef.current = safeData.goals || {};
  setGoals(goalsRef.current);
  setTabOrder(safeData.tabOrder || [...DEFAULT_TAB_ORDER]);
  sprintBoardRef.current = safeData.sprintBoard || normalizeSprintBoard(null);
  setSprintBoard(sprintBoardRef.current);
  // Rebuild AI badges from persisted day plan metadata
  const metaFromPlans = {};
  Object.entries(safeData.dayPlans || {}).forEach(([dateKey, plan]) => {
    if (plan?.aiSource) {
      metaFromPlans[dateKey] = {
        source: plan.aiSource,
        updatedAt: plan.aiUpdatedAt || null
      };
    }
  });
  setPlanAiMeta(metaFromPlans);
};

const buildBackupPayload = () =>
  normalizeAppData({
    todos,
    habits,
    stats: { level, xp, xpNeeded },
    header: headerText,
    dominos,
    dominoLabels,
    completedDominos,
    categoryLabels,
    collapsedCategories,
    collapsedTodos,
    monthlyJournal,
    penaltyMeta,
    userApiKey,
    userApiKey2,
    deepseekApiKey,
    visionBoardText,
    scheduleConfig,
    scheduleData,
    dayPlans,
    mealLog,
    healthProfile,
    calorieMemory,
    stepLog,
    moneyLog,
    tabOrder,
    sprintBoard,
    integrations: integrationsRef.current,
    goals: goalsRef.current
  });

// A backup taken before the Calendar tab existed has no board. Normalizing it as-is
// yields an empty one, and the full-document write that follows would erase the live
// calendar — so a restore that says nothing about the board leaves it alone.
const keepLiveSprintBoard = (raw) => (
  raw && raw.sprintBoard === undefined ? { ...raw, sprintBoard: sprintBoardRef.current } : raw
);

// A backup from before this tab has no integrations field. Restoring it must
// not wipe ideas already saved on this device.
const keepLiveIntegrations = (raw) => (
  raw && raw.integrations === undefined ? { ...raw, integrations: integrationsRef.current } : raw
);

// A backup from before the Goals tab has no goals field. Restoring it must
// not wipe goals already saved on this device.
const keepLiveGoals = (raw) => (
  raw && raw.goals === undefined ? { ...raw, goals: goalsRef.current } : raw
);

const hasKnownBackupKeys = (obj) => {
  const knownKeys = [
    'todos',
    'todoMap',
    'habits',
    'stats',
    'header',
    'dominos',
    'dominoLabels',
    'completedDominos',
    'categoryLabels',
    'collapsedCategories',
    'collapsedTodos',
    'monthlyJournal',
    'penaltyMeta',
    'userApiKey',
    'userApiKey2',
    'deepseekApiKey',
    'visionBoardText',
    'scheduleConfig',
    'scheduleData',
    'dayPlans',
    'mealLog',
    'healthProfile',
    'calorieMemory',
    'savedMealsDatabase',
    'stepLog',
    'moneyLog',
    'tabOrder',
    'sprintBoard',
    'integrations',
    'goals'
  ];

  return knownKeys.some((key) => Object.prototype.hasOwnProperty.call(obj, key));
};
  
  // --- AUTH EFFECTS ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoadingData(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setLoadingData(true);
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed", error);
      setLoadingData(false);
      alert("Login failed: " + error.message);
    }
  };

const handleLogout = async () => {
  await signOut(auth);

  setTodos([]);
  todosRef.current = [];
  todosDeletedIdsRef.current = new Set();
  todoMapPushSigRef.current = '';
  suppressRemoteTodosUntilRef.current = 0;
  setHabits([]);
  habitsRef.current = [];
  setLevel(1);
  setXp(0);
  setXpNeeded(100);

  setHeaderText({ title: 'Your Journey', subtitle: 'Every step builds momentum.' });

  setDominos(getInitialDominos());
  setDominoLabels(getInitialDominoLabels());
  setCompletedDominos(getInitialCompletedDominos());

  setCategoryLabels(defaultCategoryLabels);
  setCollapsedCategories(defaultCollapsedCategoryState);
  setCollapsedTodos(defaultCollapsedTodoState);

  setMonthlyJournal({});
  journalRef.current = {};
  setIntegrations({});
  integrationsRef.current = {};
  suppressRemoteIntegrationsUntilRef.current = 0;
  setGoals({});
  goalsRef.current = {};
  suppressRemoteGoalsUntilRef.current = 0;
  setJournalMonthOffset(0);
  setJournalDraft(defaultJournalEntry);
  suppressRemoteJournalUntilRef.current = 0;
  suppressRemoteAppUntilRef.current = 0;

  setPenaltyMeta({ lastAuditDate: null });
  setUserApiKey('');
  setUserApiKey2('');
  setDeepseekApiKey('');
  
  // RESET NEW STATES
  setVisionBoardText('');
  setScheduleConfig({ durationHours: 2, distribution: 'morning' });
  setScheduleData({});
  setDayPlans({});
  setMealLog({});
  setCalorieMemory({});
  setStepLog({});
  setStepDraft('');
  setMealSort('logged');
  setEditingMeal(null);
  setEditingMemoryKey(null);
  setEditingMemoryCalories('');
  setEditingMemoryName('');
  setEditingMemoryProtein('');
  setEditingMemoryCarbs('');
  setEditingMemoryFat('');
  setHealthProfile({ ...DEFAULT_HEALTH_PROFILE });
  setHealthConditionsDraft('');
  setProfileNumberDrafts({});
  setMealForm({ name: '', mealType: 'lunch', portion: 'regular', caloriesOverride: null, protein: '', carbs: '', fat: '', isManual: false });
  setMealDateOffset(0);
  setMoneyLog({});
  setMoneyDateOffset(0);
  setPlanDayIndex(0);
  setEstimatingCalories(false);
  setSuggestingPlan(false);
  setPlanAiMeta({});
  setTabOrder([...DEFAULT_TAB_ORDER]);
  sprintBoardRef.current = normalizeSprintBoard(null);
  setSprintBoard(sprintBoardRef.current);
  setDraggingTabId(null);
  setTabDropTargetId(null);
  tabDragRef.current = null;
  setActiveTab('todos');
  cloudHydratedRef.current = false;
  writeReadyRef.current = false;
  suppressRemoteTodosUntilRef.current = 0;
  pendingCloudUpdatesRef.current = [];
  setCloudReady(false);
  setCloudBackups([]);
  setShowBackupPanel(false);
  setLastExportAt(null);
  setDismissExportRemind(false);
  
  setDbError(null);
};

  // --- CLOUD SYNC EFFECT ---
useEffect(() => {
  if (!user) return;

  cloudHydratedRef.current = false;
  writeReadyRef.current = false;
  setLoadingData(true);
  setDbError(null);

  const cached = readLocalCache(user.uid);
  if (cached?.data && !isSparseAppData(cached.data)) {
    const cachedData = normalizeAppData(cached.data);
    applyAppDataToState(cachedData);
    applyLifestyleFrom(cachedData);
    setLoadingData(false);
  }

  // Edits from a previous session that never reached Firebase. The flush timer and
  // the post-hydration flush below both drain this once a write is possible again.
  restorePendingCloudUpdates(user.uid);

  const docRef = doc(db, 'users', user.uid, 'rpg_tracker', 'main');

  let serverRefreshInFlight = false;
  let serverRefreshAttempts = 0;
  let serverRefreshRetryTimer = null;
  let lastServerRefreshAt = 0;
  let syncCancelled = false;
  const handleTrackerSnapshot = (docSnap) => {
      if (syncCancelled) return;
      const fromCache = !!docSnap.metadata?.fromCache;
      const hasPendingWrites = !!docSnap.metadata?.hasPendingWrites;
      const cacheNow = readLocalCache(user.uid);

      // Empty IndexedDB on a new Pages origin is NOT "the cloud doc is gone".
      // Wait for the server snapshot before creating/overwriting anything.
      if (!docSnap.exists() && fromCache) {
        return;
      }

      if (docSnap.exists()) {
        // Cache hit means the doc is real — merge-writes are safe. Full-doc
        // upload still waits for !fromCache so a blank origin cannot clobber it.
        writeReadyRef.current = true;
        if (!fromCache && !hasPendingWrites) {
          cloudHydratedRef.current = true;
          // Proof the connection is back. Nothing else cleared the offline notice, so
          // one dropped read at startup left this device claiming "sync failed" for
          // the rest of the session while it was in fact syncing fine.
          setCloudSaveState((prev) => (
            prev.status === 'error'
              && prev.reason === 'offline'
              && !pendingCloudUpdatesRef.current.length
              ? { status: 'saved', message: 'Saved to Firebase' }
              : prev
          ));
        }
        setCloudReady(true);

        // After a real server read, ignore IndexedDB replays so a richer local
        // cache cannot hide quests that another device just saved.
        const guardsActive = Date.now() < suppressRemoteTodosUntilRef.current
          || Date.now() < suppressRemoteLifestyleUntilRef.current
          || Date.now() < suppressRemoteAppUntilRef.current
          || Date.now() < suppressRemoteJournalUntilRef.current;
        if (fromCache && cloudHydratedRef.current && !hasPendingWrites && !guardsActive) {
          Promise.resolve().then(() => flushPendingCloudUpdates());
          return;
        }

        const raw = docSnap.data() || {};
        const cloudUpdatedAt = typeof raw.updatedAt === 'string' ? raw.updatedAt : null;
        const safeCloud = normalizeAppData(raw);
        const cachedData = cacheNow?.data ? normalizeAppData(cacheNow.data) : null;
        const cacheNewer = !!(
          cacheNow?.savedAt &&
          cloudUpdatedAt &&
          new Date(cacheNow.savedAt) > new Date(cloudUpdatedAt)
        );
        const cacheRicher = !!(
          cachedData &&
          !isSparseAppData(cachedData) &&
          appDataWeight(cachedData) > appDataWeight(safeCloud)
        );

        // Never replace the server document with a "richer" local cache.
        // That used to upload this device's older quest list and wipe iPhone adds.
        if (shouldUploadLocalCacheAsAuthority({
          fromCache,
          cacheNewer,
          cacheRicher,
          cloudUpdatedAt,
          cacheSavedAt: cacheNow?.savedAt
        })) {
          applyAppDataToState(cachedData);
          applyLifestyleFrom(cachedData);
          // This path uploads the cache as a full document, so the cloud baseline is
          // now stale in an unknown direction. Clearing it makes the next per-date
          // write send every date rather than trust a baseline that no longer
          // describes the server. Over-sending is safe; per-date paths still never
          // replace a map wholesale. (Deletions are not at stake here — they need a
          // caller-supplied pre-edit baseline, and this path has no caller.)
          lastCloudDateMapsRef.current = {};
          updateCloud(toCloudPayload(cachedData), { fullDocument: true });
          setLoadingData(false);
          return;
        }

        // Record what the server actually holds, before any merge overwrites it.
        // Both write paths narrow against this, so the push decision and the write
        // measure the same thing. Only a server snapshot counts — a cached one has
        // not confirmed anything.
        if (!fromCache) {
          lastCloudDateMapsRef.current = {
            dayPlans: safeCloud.dayPlans,
            mealLog: safeCloud.mealLog,
            stepLog: safeCloud.stepLog,
            moneyLog: safeCloud.moneyLog
          };
        }
        const safeData = { ...safeCloud };
        // Calendar edits still parked in the pending queue have not reached the
        // server, so the snapshot does not hold them yet. Lay them back on top or the
        // tab shows the board without them until the queue drains.
        if (pendingCloudUpdatesRef.current.length) {
          safeData.sprintBoard = applySprintBoardPatches(
            safeCloud.sprintBoard,
            pendingCloudUpdatesRef.current
          );
        }
        const importGuardActive = Date.now() < suppressRemoteAppUntilRef.current;
        const journalGuardActive = importGuardActive || Date.now() < suppressRemoteJournalUntilRef.current;
        const todosGuardActive = importGuardActive || Date.now() < suppressRemoteTodosUntilRef.current;
        const localJournal = {
          ...(cachedData?.monthlyJournal || {}),
          ...(journalRef.current || {})
        };
        const cloudJournal = safeCloud.monthlyJournal || {};
        const mergedJournal = mergeMonthlyJournal(localJournal, cloudJournal);
        safeData.monthlyJournal = journalGuardActive
          ? normalizeMonthlyJournal(journalRef.current && Object.keys(journalRef.current).length
            ? journalRef.current
            : (cachedData?.monthlyJournal || mergedJournal))
          : mergedJournal;
        const journalChanged = JSON.stringify(safeData.monthlyJournal) !== JSON.stringify(cloudJournal);

        const integrationsGuardActive = importGuardActive || Date.now() < suppressRemoteIntegrationsUntilRef.current;
        const localIntegrations = mergeIntegrations(cachedData?.integrations, integrationsRef.current);
        const cloudIntegrations = safeCloud.integrations || {};
        const mergedIntegrations = mergeIntegrations(localIntegrations, cloudIntegrations);
        safeData.integrations = integrationsGuardActive
          ? normalizeIntegrations(
            integrationsRef.current && Object.keys(integrationsRef.current).length
              ? integrationsRef.current
              : (cachedData?.integrations || mergedIntegrations)
          )
          : mergedIntegrations;
        const integrationsPatch = integrationCloudPatch(cloudIntegrations, safeData.integrations);
        const integrationsChanged = Object.keys(integrationsPatch).length > 0;

        const goalsGuardActive = importGuardActive || Date.now() < suppressRemoteGoalsUntilRef.current;
        // Queue patches sit on top of the cache and the open tab. A step check
        // that has not reached Firebase yet still has a goal to land on.
        const localGoals = applyGoalPatches(
          mergeGoals(cachedData?.goals, goalsRef.current),
          pendingCloudUpdatesRef.current
        );
        const cloudGoals = safeCloud.goals || {};
        const mergedGoals = mergeGoals(localGoals, cloudGoals);
        safeData.goals = goalsGuardActive
          ? normalizeGoals(
            goalsRef.current && Object.keys(goalsRef.current).length
              ? goalsRef.current
              : (cachedData?.goals || mergedGoals)
          )
          : mergedGoals;
        const goalsPatch = goalCloudPatch(cloudGoals, safeData.goals);
        const goalsChanged = Object.keys(goalsPatch).length > 0;

        // A stale IndexedDB echo after import/restore must not replace the local snapshot.
        if (importGuardActive && cachedData) {
          [
            'todos', 'habits', 'stats', 'header', 'dominos', 'dominoLabels',
            'completedDominos', 'categoryLabels', 'collapsedCategories', 'collapsedTodos',
            'penaltyMeta', 'userApiKey', 'userApiKey2', 'deepseekApiKey', 'visionBoardText',
            'scheduleConfig', 'scheduleData', 'dayPlans', 'mealLog', 'healthProfile',
            'calorieMemory', 'stepLog', 'moneyLog', 'tabOrder', 'sprintBoard'
          ].forEach((key) => {
            if (cachedData[key] !== undefined) safeData[key] = cachedData[key];
          });
          if (Array.isArray(cachedData.habits)) habitsRef.current = cachedData.habits;
          if (Array.isArray(cachedData.todos)) todosRef.current = cachedData.todos;
        }

        // Protect in-progress Plan/Meals/Training edits from a lagging snapshot
        if (Date.now() < suppressRemoteLifestyleUntilRef.current) {
          const local = lifestyleRef.current;
          safeData.dayPlans = local.dayPlans;
          safeData.mealLog = local.mealLog;
          safeData.healthProfile = local.healthProfile;
          safeData.calorieMemory = local.calorieMemory;
          safeData.stepLog = local.stepLog;
          safeData.moneyLog = local.moneyLog;
          safeData.deepseekApiKey = local.deepseekApiKey;
          safeData.scheduleConfig = local.scheduleConfig;
          safeData.scheduleData = local.scheduleData;
          safeData.habits = habitsRef.current;
        } else {
          // Never drop locally logged meals / remembered calories just because
          // a later cloud write (or a failed meal write) has a newer updatedAt.
          const local = lifestyleRef.current;
          const mergedMeals = mergeMealLog(local.mealLog || cachedData?.mealLog, safeData.mealLog);
          const cloudMemory = safeData.calorieMemory || {};
          // Three-way union: cloud is the base, then the on-device cache, then whatever
          // this tab holds in memory. Built-in GLM seeds carry an epoch `updatedAt`, so
          // they only fill gaps and can never shadow a meal saved on another device.
          // Preferring local wholesale was what made a cache-less iPhone show nothing
          // but the 27 seeded meals.
          const mergedMemory = mergeCalorieMemory(
            mergeCalorieMemory(local.calorieMemory, cachedData?.calorieMemory),
            cloudMemory
          );
          const cloudMealIds = new Set(
            Object.values(safeCloud.mealLog || {}).flatMap((meals) =>
              (Array.isArray(meals) ? meals : []).map((m) => String(m?.id))
            )
          );
          const mealsChanged = Object.values(mergedMeals).some((meals) =>
            (Array.isArray(meals) ? meals : []).some((m) => m && !cloudMealIds.has(String(m.id)))
          );
          const mergedSteps = mergeStepLog(local.stepLog || cachedData?.stepLog, safeData.stepLog);
          const mergedProfile = mergeHealthProfile(local.healthProfile || cachedData?.healthProfile, safeData.healthProfile);
          const memoryNeedsPush = calorieMemoryHasLocalOnlyUpdates(mergedMemory, cloudMemory);
          // Legacy cloud keys (pre field-path sanitizing) get rewritten once from the
          // merged state, which is authoritative right after a real server read.
          // Only retire a key whose meal survives in the merged state under its new
          // name. Deleting one the merge dropped is unrecoverable data loss, and a
          // merge that loses an entry is exactly the bug this guard exists to contain.
          // Read the RAW document, not safeCloud. normalizeAppData re-keys calorie
          // memory by mealMemoryKey ("chicken rice"), and calorieMemoryCloudKey of that
          // is "chicken_rice__1ad1dp5" — never equal, so every already-migrated entry
          // looked legacy and the whole map was rewritten on every single snapshot.
          // Each write bumped updatedAt, which pushed a fresh snapshot, which fired the
          // migration again. Raw keys are the actual Firestore field names, and
          // calorieMemoryCloudKey is a fixed point on them, so the check can settle.
          const rawCloudMemory = (raw?.calorieMemory && typeof raw.calorieMemory === 'object'
            && !Array.isArray(raw.calorieMemory))
            ? raw.calorieMemory
            : {};
          const legacyMemoryKeys = Object.keys(rawCloudMemory).filter((k) => {
            if (k === calorieMemoryCloudKey(k)) return false;
            const entry = rawCloudMemory[k];
            const mealKey = mealMemoryKey(entry?.key || entry?.name || k);
            return !!mergedMemory[mealKey];
          });
          const stepsChanged = JSON.stringify(mergedSteps) !== JSON.stringify(safeCloud.stepLog);
          const profileChanged = JSON.stringify(mergedProfile) !== JSON.stringify(safeCloud.healthProfile);
          safeData.mealLog = mergedMeals;
          safeData.calorieMemory = mergedMemory;
          safeData.stepLog = mergedSteps;
          safeData.healthProfile = mergedProfile;
          if (cacheNewer && cachedData) {
            // Field-level edits (streak, meal kcal, steps, health profile, money text)
            const fieldKeys = ['habits', 'moneyLog', 'stepLog', 'healthProfile', 'dayPlans', 'monthlyJournal'];
            const push = {};
            fieldKeys.forEach((key) => {
              if (key === 'monthlyJournal') return;
              const localVal = cachedData[key];
              const cloudVal = safeCloud[key];
              if (JSON.stringify(localVal) !== JSON.stringify(cloudVal)) {
                // Never let an empty local array wipe out a non-empty cloud array
                if (key === 'habits' && Array.isArray(localVal) && localVal.length === 0
                    && Array.isArray(cloudVal) && cloudVal.length > 0) {
                  return; // Keep cloud habits, skip override
                }
                safeData[key] = localVal;
                push[key] = localVal;
              }
            });
            if (push.habits) habitsRef.current = push.habits;
            if (mealsChanged) push.mealLog = mergedMeals;
            if (legacyMemoryKeys.length || memoryNeedsPush) {
              push.calorieMemory = calorieMemoryForCloud(mergedMemory);
            }
            if (stepsChanged || push.stepLog) push.stepLog = mergedSteps;
            if (profileChanged || push.healthProfile) push.healthProfile = mergedProfile;
            if (journalChanged) push.monthlyJournal = safeData.monthlyJournal;
            Object.assign(push, integrationsPatch);
            Object.assign(push, goalsPatch);
            // Narrow against safeCloud — the SAME baseline stepsChanged/mealsChanged
            // were decided against. v1 narrowed the write against lifestyleRef while
            // this decision used safeCloud, so the two never agreed and the snapshot
            // re-pushed forever. One baseline is what makes this a fixed point.
            // Removals are deliberately dropped here. push.moneyLog/push.dayPlans are
            // the raw local cache, never merged with the cloud, and that cache is
            // trimmed on storage pressure (see the keepDays reclaim). A snapshot
            // cannot tell "the user cleared this day" from "this device never had
            // it", so emitting deleteField() for the difference would erase the other
            // device's history — the exact clobber per-date writes exist to prevent.
            // Only persistLifestyleToCloud may delete: its baseline is this device's
            // own prior state, so a dropped date really is a user deletion.
            const outboundPush = narrowDateKeyedWrite(push, safeCloud).updates;
            if (!fromCache && Object.keys(outboundPush).length) {
              updateCloud(outboundPush, { wholeMemoryMap: legacyMemoryKeys.length > 0 });
            }
          } else if (!fromCache && (mealsChanged || stepsChanged || profileChanged || journalChanged || memoryNeedsPush || legacyMemoryKeys.length > 0 || integrationsChanged || goalsChanged)) {
            const push = {};
            if (mealsChanged) push.mealLog = mergedMeals;
            if (legacyMemoryKeys.length || memoryNeedsPush) {
              push.calorieMemory = calorieMemoryForCloud(mergedMemory);
            }
            if (stepsChanged) push.stepLog = mergedSteps;
            if (profileChanged) push.healthProfile = mergedProfile;
            if (journalChanged) push.monthlyJournal = safeData.monthlyJournal;
            Object.assign(push, integrationsPatch);
            Object.assign(push, goalsPatch);
            // Same baseline rule as the branch above, and the same no-delete rule.
            const outbound = narrowDateKeyedWrite(push, safeCloud).updates;
            // Retiring legacy keys needs a whole-map write: dotted deletes would
            // target the sanitized name, which is the key the same patch just wrote.
            if (Object.keys(outbound).length) {
              updateCloud(outbound, { wholeMemoryMap: legacyMemoryKeys.length > 0 });
            }
          }
        }
        if (!fromCache && journalChanged && Date.now() < suppressRemoteLifestyleUntilRef.current) {
          updateCloud({ monthlyJournal: safeData.monthlyJournal });
        }
        if (!fromCache && integrationsChanged && Date.now() < suppressRemoteLifestyleUntilRef.current) {
          updateCloud(integrationsPatch);
        }
        if (!fromCache && goalsChanged && Date.now() < suppressRemoteLifestyleUntilRef.current) {
          updateCloud(goalsPatch);
        }

        // Union cloud + local quests by id (todoMap + legacy todos array).
        // Per-item todoMap writes never replace the other device's keys.
        if (!importGuardActive) {
          const localTodos = Array.isArray(todosRef.current)
            ? todosRef.current
            : (cachedData?.todos || []);
          const mergedState = mergeTodoState(localTodos, safeCloud.todos, {
            deletedIds: Array.from(todosDeletedIdsRef.current),
            localMap: cachedData?.todoMap,
            cloudMap: safeCloud.todoMap
          });
          const cloudIds = new Set(
            mergeTodoState(safeCloud.todos, [], { cloudMap: safeCloud.todoMap }).list
              .map((t) => String(t?.id))
          );
          if (!fromCache) {
            todosDeletedIdsRef.current.forEach((id) => {
              if (!cloudIds.has(String(id))) todosDeletedIdsRef.current.delete(id);
            });
          }
          safeData.todos = mergedState.list;
          safeData.todoMap = mergedState.map;
          const missingOnCloud = mergedState.list.filter((t) => t && !cloudIds.has(String(t.id)));
          const missingSig = missingOnCloud.map((t) => String(t.id)).sort().join(',');
          if (writeReadyRef.current && missingSig && missingSig !== todoMapPushSigRef.current) {
            todoMapPushSigRef.current = missingSig;
            updateCloud(todosArrayToMapPatch(missingOnCloud));
          }
        } else if (todosGuardActive && Array.isArray(todosRef.current)) {
          safeData.todos = sortTodosByOrder(todosRef.current);
        }

        applyAppDataToState(safeData);
        applyLifestyleFrom(safeData);
        const mergedLocalOnlyTodos = JSON.stringify(safeData.todos) !== JSON.stringify(safeCloud.todos || []);
        const cacheStamp = !fromCache
          ? stampForAppliedServerSnapshot({
              cloudUpdatedAt,
              mergedLocalOnlyTodos,
              cacheSavedAt: cacheNow?.savedAt
            })
          : latestCacheStamp(cacheNow?.savedAt, cloudUpdatedAt);
        writeLocalCacheIfNewer(user.uid, safeData, cacheStamp);
      } else if (cacheNow?.data && !isSparseAppData(cacheNow.data)) {
        const cachedData = normalizeAppData(cacheNow.data);
        applyAppDataToState(cachedData);
        applyLifestyleFrom(cachedData);
        cloudHydratedRef.current = true;
        writeReadyRef.current = true;
        setCloudReady(true);
        updateCloud(toCloudPayload(cachedData), { fullDocument: true });
      } else {
        applyAppDataToState(normalizeAppData({}));
        cloudHydratedRef.current = true;
        writeReadyRef.current = true;
        setCloudReady(true);
      }

      setLoadingData(false);
      if (writeReadyRef.current || cloudHydratedRef.current) {
        Promise.resolve().then(() => flushPendingCloudUpdates());
      }
      settleCloudSaveState();
  };

  const refreshFromServer = () => {
    if (syncCancelled || serverRefreshInFlight) return;
    if (pendingCloudWritesRef.current > 0 && cloudHydratedRef.current) return;
    if (cloudHydratedRef.current && Date.now() < suppressRemoteTodosUntilRef.current) return;
    if (Date.now() < suppressRemoteAppUntilRef.current) return;
    if (lastServerRefreshAt && Date.now() - lastServerRefreshAt < 15000) return;
    // One probe per window once the quota is spent, so the app recovers on its own
    // after the daily reset without hammering the backend in the meantime.
    if (isQuotaBlocked(quotaBlockedUntilRef.current)) return;
    serverRefreshInFlight = true;
    serverRefreshAttempts += 1;
    lastServerRefreshAt = Date.now();
    getDocFromServer(docRef)
      .then((serverSnap) => {
        if (!syncCancelled) handleTrackerSnapshot(serverSnap);
      })
      .catch((err) => {
        console.warn('Firestore server refresh failed:', err);
        if (isFirestoreQuotaError(err)) {
          quotaBlockedUntilRef.current = quotaBlockedUntil();
          setCloudSaveState(cloudSaveStateForWrite({ quotaBlocked: true }));
          return;
        }
        if (!syncCancelled && !cloudHydratedRef.current && serverRefreshAttempts < 4) {
          serverRefreshRetryTimer = setTimeout(() => {
            serverRefreshInFlight = false;
            refreshFromServer();
          }, 1200 * serverRefreshAttempts);
          return;
        }
      })
      .finally(() => {
        serverRefreshInFlight = false;
      });
  };

  const unsubscribe = onSnapshot(
    docRef,
    { includeMetadataChanges: true },
    (docSnap) => {
      handleTrackerSnapshot(docSnap);
      // Cache-only snapshots never flip cloudHydrated on iPhone Safari (metadata
      // changes are dropped by default). Pull the server copy once so quest
      // writes are allowed to flush.
      if (docSnap.metadata?.fromCache && !cloudHydratedRef.current) refreshFromServer();
    },
    (error) => {
      console.error('Firestore error:', error);
      const fallback = readLocalCache(user.uid);
      if (isFirestoreQuotaError(error)) {
        if (fallback?.data && !isSparseAppData(fallback.data)) {
          applyAppDataToState(normalizeAppData(fallback.data));
          applyLifestyleFrom(normalizeAppData(fallback.data));
        }
        writeReadyRef.current = true;
        quotaBlockedUntilRef.current = quotaBlockedUntil();
        setCloudSaveState(cloudSaveStateForWrite({ quotaBlocked: true }));
        setDbError(null);
        setLoadingData(false);
        return;
      }
      if (fallback?.data && !isSparseAppData(fallback.data)) {
        applyAppDataToState(normalizeAppData(fallback.data));
        applyLifestyleFrom(normalizeAppData(fallback.data));
        writeReadyRef.current = true;
        setCloudSaveState({ status: 'error', reason: 'offline', message: 'Offline — showing cached data' });
        setDbError(null);
      } else {
        setDbError(error.message);
      }
      setLoadingData(false);
    }
  );

  const onAppVisible = () => {
    if (document.visibilityState === 'visible') refreshFromServer();
  };
  document.addEventListener('visibilitychange', onAppVisible);

  // Edits parked by the write budget or a spent quota would otherwise sit until
  // something else triggered a snapshot. Costs no reads — it only drains the queue.
  const pendingFlushTimer = setInterval(() => {
    if (syncCancelled) return;
    settleCloudSaveState();
    if (!pendingCloudUpdatesRef.current.length) return;
    if (!cloudWriteBudget(readWriteLedger()).allowed) return;
    if (isQuotaBlocked(quotaBlockedUntilRef.current)) return;
    flushPendingCloudUpdates();
  }, 60000);

  return () => {
    syncCancelled = true;
    unsubscribe();
    clearInterval(pendingFlushTimer);
    document.removeEventListener('visibilitychange', onAppVisible);
    if (serverRefreshRetryTimer) clearTimeout(serverRefreshRetryTimer);
    if (dayPlansSaveTimerRef.current) clearTimeout(dayPlansSaveTimerRef.current);
    // The window this belonged to is gone with the listener; a stale baseline would
    // otherwise be inherited by the next, unrelated batch of plan edits.
    dayPlansDeleteBaselineRef.current = null;
    if (moneyLogSaveTimerRef.current) clearTimeout(moneyLogSaveTimerRef.current);
  };
}, [user]);

  // Keep lifestyle ref in sync so async Firebase writes never use stale Plan/Meals snapshots
  useEffect(() => {
    lifestyleRef.current = {
      dayPlans,
      mealLog,
      healthProfile,
      calorieMemory,
      stepLog,
      moneyLog,
      deepseekApiKey,
      scheduleConfig,
      scheduleData
    };
  }, [dayPlans, mealLog, healthProfile, calorieMemory, stepLog, moneyLog, deepseekApiKey, scheduleConfig, scheduleData]);

  useEffect(() => {
    habitsRef.current = Array.isArray(habits) ? habits : [];
  }, [habits]);

  useEffect(() => {
    todosRef.current = Array.isArray(todos) ? todos : [];
  }, [todos]);

  useEffect(() => {
    integrationsRef.current = integrations && typeof integrations === 'object' ? integrations : {};
  }, [integrations]);

  useEffect(() => {
    goalsRef.current = goals && typeof goals === 'object' ? goals : {};
  }, [goals]);

  // Firestore rejects `undefined` — strip it deeply so Plan/Meals always persist cleanly
  const sanitizeForFirestore = (value) => {
    if (value === undefined) return undefined;
    if (value === null) return null;
    // Firestore sentinels (deleteField, serverTimestamp) must pass through untouched.
    if (value && typeof value === 'object' && value._methodName) return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' || typeof value === 'boolean') return value;
    if (Array.isArray(value)) {
      return value
        .map((item) => sanitizeForFirestore(item))
        .filter((item) => item !== undefined);
    }
    if (typeof value === 'object') {
      const out = {};
      Object.entries(value).forEach(([key, nested]) => {
        if (nested === undefined) return;
        const cleaned = sanitizeForFirestore(nested);
        if (cleaned !== undefined) out[key] = cleaned;
      });
      return out;
    }
    // Drop functions / symbols / non-serializable
    return null;
  };

  // Persisted so a reload cannot hand a runaway loop a fresh allowance.
  const writeLedgerKey = () => `rpg-tracker:writes:v1:${user?.uid || 'anon'}`;

  const readWriteLedger = () => {
    if (writeLedgerRef.current) return writeLedgerRef.current;
    try {
      const raw = localStorage.getItem(writeLedgerKey());
      writeLedgerRef.current = raw ? JSON.parse(raw) : emptyWriteLedger();
    } catch {
      writeLedgerRef.current = emptyWriteLedger();
    }
    return writeLedgerRef.current;
  };

  // Parked edits are the only copy of work the cloud has not accepted yet, so the
  // queue has to outlive the tab. It used to be memory-only, which was survivable
  // only because Firestore's IndexedDB queue happened to hold a second copy.
  const pendingQueueKey = (uid = user?.uid) => `rpg-tracker:pending:v1:${uid || 'anon'}`;

  const persistPendingCloudUpdates = () => {
    try {
      const queue = pendingCloudUpdatesRef.current;
      if (!queue.length) {
        localStorage.removeItem(pendingQueueKey());
        return;
      }
      localStorage.setItem(
        pendingQueueKey(),
        JSON.stringify(queue.slice(-PENDING_QUEUE_MAX).map(serializePendingUpdate))
      );
    } catch {
      /* a full storage quota must never block a write */
    }
  };

  const queuePendingCloudUpdate = (queued) => {
    pendingCloudUpdatesRef.current.push(queued);
    persistPendingCloudUpdates();
  };

  const restorePendingCloudUpdates = (uid) => {
    try {
      const raw = localStorage.getItem(pendingQueueKey(uid));
      const saved = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(saved) || !saved.length) return;
      const seen = new Set(pendingCloudUpdatesRef.current.map((u) => JSON.stringify(serializePendingUpdate(u))));
      saved.forEach((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
        if (seen.has(JSON.stringify(entry))) return;
        pendingCloudUpdatesRef.current.push(deserializePendingUpdate(entry, deleteField));
      });
      console.warn(`Restored ${saved.length} edit(s) that never reached Firebase.`);
    } catch {
      /* a corrupt queue must never block startup */
    }
  };

  const recordCloudWrite = () => {
    const next = countCloudWrite(readWriteLedger());
    writeLedgerRef.current = next;
    try {
      localStorage.setItem(writeLedgerKey(), JSON.stringify(next));
    } catch {
      /* a full storage quota must never block a write */
    }
  };

  // A queued write sets status 'saving' and returns immediately, so nothing clears it
  // if the queue never drains — the sync line then claims to be syncing forever. Report
  // what is actually true: parked when the budget is spent, done when nothing is left.
  const settleCloudSaveState = () => {
    if (pendingCloudWritesRef.current > 0) return;
    const parked = pendingCloudUpdatesRef.current.length > 0;
    if (parked && !cloudWriteBudget(readWriteLedger()).allowed) {
      setCloudSaveState((prev) => (
        prev.status === 'saving' ? { status: 'error', reason: 'budget', message: BUDGET_SAVE_MESSAGE } : prev
      ));
      return;
    }
    if (parked) return;
    setCloudSaveState((prev) => {
      if (prev.status === 'saving') return { status: 'saved', message: 'Saved to Firebase' };
      // A budget or quota park is only ever entered alongside a queued update. The
      // queue is empty and nothing is in flight, so those edits have since landed —
      // leaving the warning up makes a working device look permanently broken.
      if (prev.status === 'error' && (prev.reason === 'budget' || prev.reason === 'quota')) {
        const clear = cloudWriteBudget(readWriteLedger()).allowed
          && !isQuotaBlocked(quotaBlockedUntilRef.current);
        if (clear) return { status: 'saved', message: 'Saved to Firebase' };
      }
      return prev;
    });
  };

  const prepareCloudUpdates = (updates, { wholeMemoryMap = false } = {}) => {
    const next = { ...dropShadowedSprintPaths(updates || {}) };
    if (next.calorieMemory && typeof next.calorieMemory === 'object' && !Array.isArray(next.calorieMemory)) {
      const cloudMemory = calorieMemoryForCloud(next.calorieMemory);
      if (wholeMemoryMap) {
        next.calorieMemory = cloudMemory;
      } else {
        // Per-key field paths. `updateDoc` REPLACES a whole map written at a bare
        // field name, which is how an iPhone with a thin cache used to delete the
        // desktop's saved meals. Dotted paths only touch the keys we name.
        delete next.calorieMemory;
        Object.entries(cloudMemory).forEach(([key, entry]) => {
          next[`calorieMemory.${key}`] = entry;
        });
      }
    }
    return next;
  };

  const cacheCloudUpdatesLocally = (updates, stamp = new Date().toISOString()) => {
    const prev = readLocalCache(user.uid)?.data || {};
    const pending = sanitizeForFirestore(updates);
    if (pending && typeof pending === 'object') {
      const mergedCache = normalizeAppData(applyDottedPatch(prev, pending));
      if (!(isSparseAppData(mergedCache) && !isSparseAppData(prev))) {
        writeLocalCache(user.uid, mergedCache, stamp);
      }
    }
    return stamp;
  };

  const flushPendingCloudUpdates = async () => {
    if (!user || !pendingCloudUpdatesRef.current.length) return true;
    if (!cloudHydratedRef.current && !writeReadyRef.current) return false;
    // Draining into an exhausted quota just re-queues everything and spends reads.
    if (isQuotaBlocked(quotaBlockedUntilRef.current)) return false;
    const batch = pendingCloudUpdatesRef.current.splice(0);
    // Clear the persisted copy too. Anything that fails below is re-queued by
    // updateCloud's park paths, which persist it again.
    persistPendingCloudUpdates();
    const merged = batch.reduce((acc, u) => {
      const remove = [
        ...(Array.isArray(acc._calorieMemoryRemove) ? acc._calorieMemoryRemove : []),
        ...(Array.isArray(u._calorieMemoryRemove) ? u._calorieMemoryRemove : [])
      ];
      const dateRemove = [
        ...(Array.isArray(acc._dateKeyRemove) ? acc._dateKeyRemove : []),
        ...(Array.isArray(u._dateKeyRemove) ? u._dateKeyRemove : [])
      ];
      const next = { ...acc, ...u };
      if (remove.length) next._calorieMemoryRemove = Array.from(new Set(remove));
      else delete next._calorieMemoryRemove;
      if (dateRemove.length) next._dateKeyRemove = Array.from(new Set(dateRemove));
      else delete next._dateKeyRemove;
      return next;
    }, {});
    return updateCloud(merged, { flushing: true });
  };

  const updateCloud = async (updates, opts = {}) => {
    if (!user) return false;
    const removeMemoryKeys = Array.isArray(updates?._calorieMemoryRemove)
      ? updates._calorieMemoryRemove.filter((k) => typeof k === 'string' && k)
      : [];
    const removeDateKeys = Array.isArray(updates?._dateKeyRemove)
      ? updates._dateKeyRemove.filter((k) => typeof k === 'string' && k)
      : [];
    let prepared = prepareCloudUpdates(
      { ...updates, _calorieMemoryRemove: undefined, _dateKeyRemove: undefined },
      {
        wholeMemoryMap: !!opts.fullDocument
          || !!opts.wholeMemoryMap
          || isFullDocumentCloudUpdate(updates)
      }
    );
    delete prepared._calorieMemoryRemove;
    delete prepared._dateKeyRemove;
    if (!opts.fullDocument && !isFullDocumentCloudUpdate(prepared) && Array.isArray(prepared.todos)) {
      prepared = { ...todosArrayToMapPatch(prepared.todos), ...prepared };
      delete prepared.todos;
    }
    removeMemoryKeys.forEach((key) => {
      const path = `calorieMemory.${calorieMemoryCloudKey(key)}`;
      // A delete must never cancel a set in the same patch. Passing raw cloud map
      // keys here re-sanitized them onto the live key, so every entry the patch
      // wrote was deleted again — the cloud copy stayed empty and the migration
      // re-fired on every snapshot, which is what looped "Saving to Firebase…".
      if (Object.prototype.hasOwnProperty.call(prepared, path)) return;
      prepared[path] = deleteField();
    });
    removeDateKeys.forEach((path) => {
      // Same rule as calorie memory: a delete must never cancel a set made by the
      // same patch, or the write erases the day it just saved.
      if (Object.prototype.hasOwnProperty.call(prepared, path)) return;
      prepared[path] = deleteField();
    });
    // Last line of defence before Firestore sees the patch. updateDoc() throws
    // synchronously on a bad field path, which failed the entire write and left it
    // re-queued forever — one unsaveable meal name blocked every later save.
    prepared = sanitizeCloudFieldPaths(prepared);
    // Local-only loop detector. Costs no Firestore operations. If the same patch shape
    // fires more than 10 times in 10 seconds, something is looping — print the shape so
    // the offending write path is identifiable from the console alone.
    if (typeof window !== 'undefined') {
      const nowMs = Date.now();
      const shape = Object.keys(prepared).sort().join(',');
      writeTraceRef.current = writeTraceRef.current.filter((e) => nowMs - e.at < 10000);
      writeTraceRef.current.push({ at: nowMs, shape });
      const sameShape = writeTraceRef.current.filter((e) => e.shape === shape).length;
      if (sameShape > 10) {
        console.warn(`[sync-loop] ${sameShape} writes in 10s with the same shape:`, shape);
      }
    }
    const incremental = isIncrementalCloudUpdate(prepared);
    const canWrite = canWriteCloudMerge({
      hydrated: cloudHydratedRef.current,
      writeReady: writeReadyRef.current,
      updates: prepared,
      fullDocument: !!opts.fullDocument || isFullDocumentCloudUpdate(prepared)
    });
    let queued = prepared;
    if (removeMemoryKeys.length) queued = { ...queued, _calorieMemoryRemove: removeMemoryKeys };
    if (removeDateKeys.length) queued = { ...queued, _dateKeyRemove: removeDateKeys };

    const quotaBlocked = isQuotaBlocked(quotaBlockedUntilRef.current);
    const budget = cloudWriteBudget(readWriteLedger());
    writeLedgerRef.current = budget.ledger;

    if (!budget.allowed) {
      // Something is writing far more than a person can. Park the edit on-device
      // rather than spend the day's Firestore allowance; the queue coalesces, so
      // the backlog goes up as one write once the window rolls over.
      cacheCloudUpdatesLocally(prepared);
      queuePendingCloudUpdate(queued);
      setCloudSaveState({ status: 'error', reason: 'budget', message: BUDGET_SAVE_MESSAGE });
      console.warn(
        `Cloud write budget reached (${budget.overHour ? 'hourly' : 'daily'}). Edits stay on this device.`
      );
      return true;
    }

    if (!canWrite || quotaBlocked) {
      // New origin / still waiting on the server copy — cache locally and queue.
      // Field-level quest/meal writes flush as soon as the existing doc is seen.
      // While the daily quota is spent there is nothing to wait for, so keep the
      // edit on-device and say so rather than spinning on "Waiting for Firebase…".
      cacheCloudUpdatesLocally(prepared);
      queuePendingCloudUpdate(queued);
      setCloudSaveState({
        ...cloudSaveStateForWrite({ quotaBlocked, pending: true }),
        domain: cloudWriteDomain(prepared)
      });
      return true;
    }

    pendingCloudWritesRef.current += 1;
    setCloudSaveState({ ...cloudSaveStateForWrite({}), domain: cloudWriteDomain(prepared) });
    const stamp = new Date().toISOString();
    let clean = null;
    const writeQuestViaRest = async () => {
      if (!hasTodoMapPatch(clean) || !user) return false;
      const token = await user.getIdToken();
      await patchFirestoreDocRest({
        projectId: firebaseConfig.projectId,
        docPath: `users/${user.uid}/rpg_tracker/main`,
        token,
        updates: clean
      });
      return true;
    };
    try {
      const docRef = doc(db, 'users', user.uid, 'rpg_tracker', 'main');
      clean = sanitizeForFirestore({ ...prepared, updatedAt: stamp });
      if (!clean || typeof clean !== 'object' || Array.isArray(clean) || Object.keys(clean).length === 0) {
        throw new Error('Nothing valid to save to Firebase.');
      }
      cacheCloudUpdatesLocally(clean, stamp);
      // Loop guard. A snapshot handler that keeps deciding the cloud needs the same
      // patch will otherwise write it forever; that is how a day's write quota went
      // in an hour. The edit is already cached above, so skipping costs nothing.
      const signature = cloudWriteSignature(clean);
      if (shouldSkipDuplicateWrite({
        signature,
        lastSignature: lastWriteSignatureRef.current,
        payload: clean
      })) {
        console.warn('Skipped a duplicate Firestore write — cloud already has this payload.');
        setCloudSaveState({ status: 'saved', message: 'Saved to Firebase' });
        return true;
      }
      // Count on attempt, not on success: a write that reaches the backend and
      // then fails has still been billed.
      recordCloudWrite();
      const writePromise = updateDoc(docRef, clean).catch((err) => {
        if (err?.code === 'not-found' || String(err?.message || '').includes('No document to update')) {
          return setDoc(docRef, clean, { merge: true });
        }
        throw err;
      });
      if (incremental) {
        // Meal saves are already on-device. Do not fail the toast if Firebase is slow —
        // a Promise.race timeout was aborting the UI while the write was still in flight.
        const raced = await Promise.race([
          writePromise.then(() => 'ok').catch((err) => {
            throw err;
          }),
          new Promise((resolve) => setTimeout(() => resolve('slow'), 4000))
        ]);
        if (raced === 'slow') {
          // Slow is not the same as fine. On a spent quota this branch is where every
          // write ended up: the 4s race expired, the function returned true, and the
          // patch was left in a queue that would never drain — so the app went on
          // accepting edits it reported as in flight. Give the stall a deadline.
          let settled = false;
          let stallTimer = null;
          // Whichever of the three outcomes lands first owns the result: the SDK
          // write, the REST write, or the deadline. The other two must not overwrite it.
          const runOnce = (fn) => {
            if (settled) return;
            settled = true;
            clearTimeout(stallTimer);
            fn();
          };
          const parkAsQuotaSpent = () => {
            quotaBlockedUntilRef.current = quotaBlockedUntil();
            setDbError(null);
            setCloudSaveState(cloudSaveStateForWrite({ quotaBlocked: true }));
            queuePendingCloudUpdate(queued);
          };
          stallTimer = setTimeout(() => runOnce(() => {
            console.warn('Firestore write stalled past the deadline — treating the daily quota as spent.');
            parkAsQuotaSpent();
          }), WRITE_STALL_DEADLINE_MS);
          if (hasTodoMapPatch(clean) && !isQuotaBlocked(quotaBlockedUntilRef.current)) {
            // REST is the one path that names a spent quota straight away, as an HTTP
            // 429, instead of parking the write in silence. It bills like any other
            // write, so count it — an uncounted write is a hole in the budget.
            recordCloudWrite();
            writeQuestViaRest().then(() => runOnce(() => {
              setDbError(null);
              setCloudSaveState({ status: 'saved', message: 'Saved to Firebase' });
              scheduleCloudBackup('auto');
            })).catch((restErr) => {
              if (isFirestoreQuotaError(restErr)) {
                runOnce(parkAsQuotaSpent);
                return;
              }
              console.warn('Firestore REST quest write:', restErr);
            });
          }
          writePromise.then(() => runOnce(() => {
            setDbError(null);
            quotaBlockedUntilRef.current = 0;
            lastWriteSignatureRef.current = signature;
            setCloudSaveState({ status: 'saved', message: 'Saved to Firebase' });
            scheduleCloudBackup('auto');
          })).catch((err) => runOnce(() => {
            console.error('Cloud update error:', err);
            if (isFirestoreQuotaError(err)) {
              parkAsQuotaSpent();
              return;
            }
            setDbError(err.message);
            setCloudSaveState({ status: 'error', reason: 'write', message: `Saved on this device — ${err.message || 'Firebase save failed'}` });
            queuePendingCloudUpdate(queued);
          }));
          if (!opts.flushing && pendingCloudUpdatesRef.current.length) {
            flushPendingCloudUpdates();
          }
          return true;
        }
      } else {
        // writePromise already sent this patch. The old code issued a second
        // updateDoc() here, so every full-document save was billed twice while
        // recordCloudWrite() counted it once — a hole straight through the budget.
        // Await the write already in flight, and bound it: a bare await never
        // returns on a spent quota, because Firestore parks the mutation rather
        // than rejecting it.
        const outcome = await Promise.race([
          writePromise.then(() => 'ok'),
          new Promise((resolve) => setTimeout(() => resolve('stalled'), WRITE_STALL_DEADLINE_MS))
        ]);
        if (outcome === 'stalled') {
          console.warn('Firestore write stalled past the deadline — treating the daily quota as spent.');
          quotaBlockedUntilRef.current = quotaBlockedUntil();
          setDbError(null);
          setCloudSaveState(cloudSaveStateForWrite({ quotaBlocked: true }));
          queuePendingCloudUpdate(queued);
          return false;
        }
      }
      setDbError(null);
      quotaBlockedUntilRef.current = 0;
      lastWriteSignatureRef.current = signature;
      setCloudSaveState({ status: 'saved', message: 'Saved to Firebase' });
      scheduleCloudBackup('auto');
      if (!opts.flushing && pendingCloudUpdatesRef.current.length) {
        flushPendingCloudUpdates();
      }
      return true;
    } catch (err) {
      console.error("Cloud update error:", err);
      if (isFirestoreQuotaError(err)) {
        // Park the edit on-device. Retrying now only burns an already-empty meter.
        quotaBlockedUntilRef.current = quotaBlockedUntil();
        setDbError(null);
        setCloudSaveState(cloudSaveStateForWrite({ quotaBlocked: true }));
        queuePendingCloudUpdate(queued);
        return false;
      }
      try {
        if (await writeQuestViaRest()) {
          setDbError(null);
          setCloudSaveState({ status: 'saved', message: 'Saved to Firebase' });
          scheduleCloudBackup('auto');
          return true;
        }
      } catch (restErr) {
        console.error('Firestore REST quest write failed:', restErr);
      }
      setDbError(err.message);
      setCloudSaveState({ status: 'error', reason: 'write', message: `Saved on this device — ${err.message || 'Firebase save failed'}` });
      queuePendingCloudUpdate(queued);
      return false;
    } finally {
      pendingCloudWritesRef.current = Math.max(0, pendingCloudWritesRef.current - 1);
    }
  };

  const loadCloudBackups = async () => {
    if (!user) return [];
    try {
      const snap = await getDocs(
        query(backupsCol(user.uid), orderBy('createdAt', 'desc'), limit(CLOUD_BACKUP_KEEP + 6))
      );
      const rows = snap.docs.map((d) => ({
        id: d.id,
        createdAt: typeof d.data()?.createdAt === 'string' ? d.data().createdAt : d.id,
        weight: Number(d.data()?.weight) || 0,
        source: typeof d.data()?.source === 'string' ? d.data().source : 'auto'
      }));
      setCloudBackups(rows);
      return rows;
    } catch (err) {
      console.warn('Could not list cloud backups:', err);
      return [];
    }
  };

  const pruneCloudBackups = async (rows) => {
    if (!user || !Array.isArray(rows) || rows.length <= CLOUD_BACKUP_KEEP) return;
    await Promise.all(
      rows.slice(CLOUD_BACKUP_KEEP).map((row) =>
        deleteDoc(doc(db, 'users', user.uid, 'rpg_tracker_backups', row.id)).catch(() => {})
      )
    );
  };

  const writeCloudBackup = async (source = 'auto') => {
    if (!user || !cloudHydratedRef.current) return false;
    const payload = normalizeAppData(readLocalCache(user.uid)?.data || buildBackupPayload());
    if (isSparseAppData(payload)) return false;
    const weight = appDataWeight(payload);
    const now = Date.now();
    if (
      source === 'auto' &&
      lastCloudBackupAtRef.current &&
      now - lastCloudBackupAtRef.current < CLOUD_BACKUP_MIN_MS &&
      weight === lastCloudBackupWeightRef.current
    ) {
      return false;
    }
    const createdAt = new Date().toISOString();
    const id = `b-${createdAt.replace(/[:.]/g, '-')}`;
    const clean = sanitizeForFirestore({
      createdAt,
      source,
      weight,
      data: toCloudPayload(payload)
    });
    await setDoc(doc(db, 'users', user.uid, 'rpg_tracker_backups', id), clean);
    lastCloudBackupAtRef.current = now;
    lastCloudBackupWeightRef.current = weight;
    const rows = await loadCloudBackups();
    await pruneCloudBackups(rows);
    return true;
  };

  const scheduleCloudBackup = (source = 'auto') => {
    if (cloudBackupTimerRef.current) clearTimeout(cloudBackupTimerRef.current);
    cloudBackupTimerRef.current = setTimeout(() => {
      writeCloudBackup(source).catch((err) => console.warn('Cloud backup failed:', err));
    }, source === 'manual' || source === 'export' || source === 'session' ? 0 : 4000);
  };

  const restoreCloudBackup = async (backupId) => {
    if (!user || !backupId || backupBusy) return;
    setBackupBusy(true);
    try {
      const snap = await getDoc(doc(db, 'users', user.uid, 'rpg_tracker_backups', backupId));
      const raw = snap.exists() ? snap.data()?.data : null;
      if (!raw || isSparseAppData(raw)) {
        showNotification('That snapshot is empty — not restoring.');
        return;
      }
      const safeData = normalizeAppData(keepLiveGoals(keepLiveIntegrations(keepLiveSprintBoard(raw))));
      applyAppDataToState(safeData);
      applyLifestyleFrom(safeData);
      journalRef.current = safeData.monthlyJournal || {};
      setJournalDraft(safeData.monthlyJournal[getMonthKey(journalMonthOffset)] || defaultJournalEntry);
      writeLocalCache(user.uid, safeData);
      cloudHydratedRef.current = true;
      setCloudReady(true);
      touchAppImport(15000);
      const ok = await updateCloud(toCloudPayload(safeData), { fullDocument: true });
      touchAppImport(ok ? 8000 : 20000);
      showNotification(ok ? 'Restored snapshot to the app and Firebase.' : 'Restored locally — Firebase sync failed.');
    } catch (err) {
      console.error(err);
      showNotification('Could not restore that snapshot. Check Firestore rules for rpg_tracker_backups.');
    } finally {
      setBackupBusy(false);
    }
  };

  useEffect(() => {
    if (!user || !cloudReady) return;
    try {
      const stored = localStorage.getItem(lastExportKey(user.uid));
      if (stored) setLastExportAt(stored);
    } catch {
      /* ignore */
    }
    loadCloudBackups().then((rows) => {
      if (!rows.length) scheduleCloudBackup('session');
    });
    return () => {
      if (cloudBackupTimerRef.current) clearTimeout(cloudBackupTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cloudReady]);

  // Persist Plan + Meals (+ schedule template those tabs rely on) as one bundle.
  // Pass onlyKeys (e.g. ['mealLog','calorieMemory']) to keep meal saves small.
  // `deletesAgainst` is how a caller says "I am rewriting this field, and here is what
  // this device held immediately before I touched it". Only dates present there and
  // absent from the new payload are deleted from the cloud. Omit it and nothing is
  // deleted — that default is deliberate, because the alternatives are all unsafe:
  // the cloud baseline holds dates this device never had, and any long-lived mirror of
  // lifestyleRef can go stale (the state-sync effect near App.jsx:2075 rewrites
  // lifestyleRef wholesale from React state, so lifestyleRef has two writers). A
  // baseline captured by the mutating code itself, one statement before the mutation,
  // is the only one that cannot drift.
  const persistLifestyleToCloud = async (overrides = {}, onlyKeys = null, deletesAgainst = null) => {
    // Cover the whole round-trip. Callers may have touched this many seconds ago
    // (addMeal waits on the AI estimate first), so re-arm it here or a snapshot
    // arriving mid-write would reset state to the pre-write server value.
    touchLifestyleEdit(15000);
    // Same hazard as the date-keyed maps: commitCalorieMemory writes the post-edit map
    // into lifestyleRef before this runs, so reading the baseline from the ref makes
    // prev === next, removedCalorieMemoryCloudKeys returns nothing, and a forgotten
    // meal is never deleted from the cloud — the next snapshot merges it straight back.
    // Callers that can drop a key pass the map they held before they mutated it.
    const prevCalorieMemory = deletesAgainst?.calorieMemory ?? lifestyleRef.current.calorieMemory;
    // Normalized with the same functions the payload uses, or a raw-vs-normalized
    // diff would report every date as deleted-and-re-added on every save.
    const deleteBaseline = deletesAgainst
      ? {
        dayPlans: normalizeDayPlans(deletesAgainst.dayPlans),
        mealLog: normalizeMealLog(deletesAgainst.mealLog),
        stepLog: normalizeStepLog(deletesAgainst.stepLog),
        moneyLog: normalizeMoneyLog(deletesAgainst.moneyLog)
      }
      : null;
    const latest = { ...lifestyleRef.current, ...overrides };
    // Normalize before write so retrieval is stable across devices / Cloudflare Pages
    const payload = {
      dayPlans: normalizeDayPlans(latest.dayPlans),
      mealLog: normalizeMealLog(latest.mealLog),
      healthProfile: normalizeHealthProfile(latest.healthProfile),
      calorieMemory: normalizeCalorieMemory(latest.calorieMemory),
      stepLog: normalizeStepLog(latest.stepLog),
      moneyLog: normalizeMoneyLog(latest.moneyLog),
      deepseekApiKey: typeof latest.deepseekApiKey === 'string' ? latest.deepseekApiKey : '',
      scheduleConfig: {
        durationHours: Number.isFinite(Number(latest.scheduleConfig?.durationHours))
          ? Number(latest.scheduleConfig.durationHours)
          : 2,
        distribution: typeof latest.scheduleConfig?.distribution === 'string'
          ? latest.scheduleConfig.distribution
          : 'morning'
      },
      scheduleData: normalizeScheduleData(latest.scheduleData)
    };
    lifestyleRef.current = { ...lifestyleRef.current, ...payload };
    cacheLifestyleNow();
    const overrideKeys = Object.keys(overrides);
    const keysToWrite = Array.isArray(onlyKeys) && onlyKeys.length
      ? onlyKeys
      : (overrideKeys.length > 0 && overrideKeys.length <= 4 ? overrideKeys : null);
    const toWrite = keysToWrite
      ? Object.fromEntries(keysToWrite.filter((k) => payload[k] !== undefined).map((k) => [k, payload[k]]))
      : payload;
    if (toWrite.calorieMemory !== undefined) {
      const removed = removedCalorieMemoryCloudKeys(prevCalorieMemory, toWrite.calorieMemory);
      if (removed.length) toWrite._calorieMemoryRemove = removed;
    }
    // Changes measure against the last SERVER state, never against lifestyleRef:
    // callers overwrite lifestyleRef with the new value before calling this, so a
    // lifestyleRef baseline is byte-identical to the payload and every write silently
    // shrinks to nothing. Deletions measure against the caller's own captured
    // pre-edit state, or do not happen at all — see the note on deletesAgainst.
    const narrowedWrite = narrowDateKeyedWrite(
      toWrite,
      lastCloudDateMapsRef.current,
      deleteBaseline
    );
    const outboundWrite = narrowedWrite.updates;
    if (narrowedWrite.removed.length) outboundWrite._dateKeyRemove = narrowedWrite.removed;
    // Nothing actually changed. Writing a stamp-only patch would spend quota to say
    // so, and the resulting snapshot would re-enter this path.
    if (!Object.keys(outboundWrite).length) {
      cacheLifestyleNow();
      touchLifestyleEdit(4000);
      return true;
    }
    const ok = await updateCloud(outboundWrite);
    cacheLifestyleNow();
    // Keep the guard alive past the ack so the write's own echo can't undo it.
    // On failure, hold local meals/memory longer so a stale snapshot cannot wipe them.
    touchLifestyleEdit(ok ? 4000 : 20000);
    return ok;
  };

  const cacheLifestyleNow = () => {
    if (!user) return;
    const existing = readLocalCache(user.uid)?.data;
    // Use habitsRef (always current) or fall back to existing cache — never write [] over real habits
    const safeHabits = (Array.isArray(habitsRef.current) && habitsRef.current.length > 0)
      ? habitsRef.current
      : (Array.isArray(existing?.habits) ? existing.habits : []);
    const payload = normalizeAppData({
      ...buildBackupPayload(),
      habits: safeHabits,
      dayPlans: lifestyleRef.current.dayPlans,
      mealLog: lifestyleRef.current.mealLog,
      healthProfile: lifestyleRef.current.healthProfile,
      calorieMemory: lifestyleRef.current.calorieMemory,
      stepLog: lifestyleRef.current.stepLog,
      moneyLog: lifestyleRef.current.moneyLog
    });
    if (!(isSparseAppData(payload) && existing && !isSparseAppData(existing))) {
      writeLocalCache(user.uid, payload);
    }
  };

  const persistMealLogToCloud = async (nextMealLog, extras = {}, prevMemory = null) => {
    const safeMeals = normalizeMealLog(nextMealLog);
    // Captured before the mutation below. Deleting the last meal of a day drops the
    // date, and this is the only baseline that can tell that from "this device never
    // had that date".
    const prevMealLog = lifestyleRef.current.mealLog;
    const extraPayload = { ...extras };
    if (extraPayload.calorieMemory !== undefined) {
      extraPayload.calorieMemory = normalizeCalorieMemory(extraPayload.calorieMemory);
      setCalorieMemory(extraPayload.calorieMemory);
    }
    lifestyleRef.current = {
      ...lifestyleRef.current,
      mealLog: safeMeals,
      ...extraPayload
    };
    setMealLog(safeMeals);
    cacheLifestyleNow();
    return persistLifestyleToCloud(
      { mealLog: safeMeals, ...extraPayload },
      ['mealLog', 'calorieMemory'],
      // A rename retires the old memory key, so that caller hands over the memory map
      // it held before it edited. It cannot be read from lifestyleRef here — the
      // caller ran commitCalorieMemory before calling this.
      { mealLog: prevMealLog, ...(prevMemory ? { calorieMemory: prevMemory } : {}) }
    );
  };

  const persistMoneyLogToCloud = async (nextMoneyLog) => {
    const safe = normalizeMoneyLog(nextMoneyLog);
    // Captured before the mutation below, for the same reason as meals: clearing a
    // day's items drops the date, and only this baseline proves the user did it.
    const prevMoneyLog = lifestyleRef.current.moneyLog;
    lifestyleRef.current = { ...lifestyleRef.current, moneyLog: safe };
    setMoneyLog(safe);
    cacheLifestyleNow();
    return persistLifestyleToCloud({ moneyLog: safe }, null, { moneyLog: prevMoneyLog });
  };

  const currentHabits = () => (Array.isArray(habitsRef.current) ? habitsRef.current : (habits || []));

  const persistHabitsToCloud = async (nextHabits, extras = {}) => {
    touchLifestyleEdit(15000);
    const safe = normalizeHabits(nextHabits);
    habitsRef.current = safe;
    setHabits(safe);
    const ok = await updateCloud({ habits: safe, ...extras });
    touchLifestyleEdit(3000);
    return ok;
  };

  const persistTodosToCloud = (nextTodos, extras = {}) => {
    const prev = Array.isArray(todosRef.current) ? todosRef.current : [];
    const stamp = new Date().toISOString();
    const prevById = Object.fromEntries(prev.map((todo) => [String(todo?.id), todo]));
    // The caller's array order IS the user's intended order. Stamp it onto the records
    // before the map patch is built, or the positions never reach the cloud.
    const safe = resequenceTodoOrder(Array.isArray(nextTodos) ? nextTodos : []).map((todo) => {
      const before = prevById[String(todo?.id)];
      const unchanged = before && JSON.stringify(before) === JSON.stringify(todo);
      if (unchanged && todo?.updatedAt) return todo;
      return { ...todo, updatedAt: stamp };
    });
    todosRef.current = safe;
    setTodos(safe);
    touchTodosEdit(8000);
    return updateCloud({
      ...buildTodoMapPatch(prev, safe, stamp),
      ...extras
    });
  };

  const scheduleDayPlansCloudSave = (nextDayPlans, prevDayPlans) => {
    touchLifestyleEdit();
    // First edit of the window owns the baseline; later ones in the same window leave
    // it alone so the single write still sees what the batch started from.
    if (dayPlansDeleteBaselineRef.current === null && prevDayPlans !== undefined) {
      dayPlansDeleteBaselineRef.current = prevDayPlans;
    }
    lifestyleRef.current = { ...lifestyleRef.current, dayPlans: nextDayPlans };
    if (dayPlansSaveTimerRef.current) clearTimeout(dayPlansSaveTimerRef.current);
    dayPlansSaveTimerRef.current = setTimeout(() => {
      const baseline = dayPlansDeleteBaselineRef.current;
      dayPlansDeleteBaselineRef.current = null;
      persistLifestyleToCloud(
        { dayPlans: lifestyleRef.current.dayPlans },
        null,
        baseline === null ? null : { dayPlans: baseline }
      );
    }, 450);
  };

  const scheduleMoneyLogCloudSave = (nextMoneyLog) => {
    touchLifestyleEdit(15000);
    const safe = normalizeMoneyLog(nextMoneyLog);
    lifestyleRef.current = { ...lifestyleRef.current, moneyLog: safe };
    setMoneyLog(safe);
    cacheLifestyleNow();
    if (moneyLogSaveTimerRef.current) clearTimeout(moneyLogSaveTimerRef.current);
    moneyLogSaveTimerRef.current = setTimeout(() => {
      persistMoneyLogToCloud(lifestyleRef.current.moneyLog);
    }, 450);
  };

  // Debounced plan edits would otherwise be lost when the tab is backgrounded or
  // closed (common on mobile). Flush any pending write immediately in that case.
  useEffect(() => {
    if (!user) return;
    const flushPendingPlanSave = () => {
      if (cloudHydratedRef.current) {
        const payload = normalizeAppData({
          ...buildBackupPayload(),
          dayPlans: lifestyleRef.current.dayPlans,
          mealLog: lifestyleRef.current.mealLog,
          healthProfile: lifestyleRef.current.healthProfile,
          calorieMemory: lifestyleRef.current.calorieMemory,
          stepLog: lifestyleRef.current.stepLog,
          moneyLog: lifestyleRef.current.moneyLog
        });
        const existing = readLocalCache(user.uid)?.data;
        if (!(isSparseAppData(payload) && existing && !isSparseAppData(existing))) {
          writeLocalCache(user.uid, payload);
        }
        if (dayPlansSaveTimerRef.current) {
          clearTimeout(dayPlansSaveTimerRef.current);
          dayPlansSaveTimerRef.current = null;
        }
        if (moneyLogSaveTimerRef.current) {
          clearTimeout(moneyLogSaveTimerRef.current);
          moneyLogSaveTimerRef.current = null;
        }
        // This write replaces the debounced one cancelled above, so it inherits that
        // batch's delete baseline. Dropping it instead would silently discard a day
        // the user cleared just before backgrounding the tab. Only dayPlans carries a
        // baseline here — the other fields are flushed from the ref, not from a
        // captured pre-edit state, so they must not delete.
        const pendingPlanBaseline = dayPlansDeleteBaselineRef.current;
        dayPlansDeleteBaselineRef.current = null;
        // ONLY flush to Firebase after cloud data is loaded,
        // so we don't overwrite real habits with an empty [] snapshot
        persistLifestyleToCloud({
          dayPlans: lifestyleRef.current.dayPlans,
          moneyLog: lifestyleRef.current.moneyLog,
          healthProfile: lifestyleRef.current.healthProfile,
          stepLog: lifestyleRef.current.stepLog
        }, null, pendingPlanBaseline === null ? null : { dayPlans: pendingPlanBaseline });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushPendingPlanSave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushPendingPlanSave);
    window.addEventListener('beforeunload', flushPendingPlanSave);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushPendingPlanSave);
      window.removeEventListener('beforeunload', flushPendingPlanSave);
    };
  }, [user]);

  const handleTestConnection = async () => {
    if (!user) return alert("Please sign in first!");
    setLoadingData(true);
    try {
      const testRef = doc(db, 'users', user.uid, 'rpg_tracker', 'test_connection');
      const savePromise = setDoc(testRef, { timestamp: new Date().toISOString() });
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Connection Timed Out.")), 10000));
      await Promise.race([savePromise, timeoutPromise]);
      setDbError(null);
      alert("✅ SUCCESS! Your app has a live, working connection to the Google servers!");
    } catch (err) {
      setDbError(err.message + " Check if your Firestore database actually exists and its rules are published.");
    } finally {
      setLoadingData(false);
    }
  };

  // --- LIVE TIMER EFFECT (BACKGROUND TAB FIX) ---
  useEffect(() => {
    const interval = setInterval(() => {
      setTodos(prev => {
        let isChanged = false;
        const now = Date.now();
        
        const updated = prev.map(t => {
          if (t.isRunning && !t.completed) {
            // Safely initialize the tick if it's missing (legacy data support)
            if (!t.lastTick) {
              isChanged = true;
              return { ...t, lastTick: now, updatedAt: new Date(now).toISOString() };
            }
            
            // Calculate absolute difference between now and the last tick timestamp
            const deltaSeconds = Math.floor((now - t.lastTick) / 1000);
            
            // If at least 1 second has passed, update the time Spent!
            // This easily catches up if the browser paused the tab for 10 minutes.
            if (deltaSeconds > 0) {
              isChanged = true;
              return { 
                ...t, 
                timeSpent: (t.timeSpent || 0) + deltaSeconds,
                lastTick: t.lastTick + (deltaSeconds * 1000), // increment last tick perfectly
                // pickNewerTodo resolves equal updatedAt in favour of the cloud copy, so a
                // tick without a stamp is rolled back by the next snapshot and re-applied
                // by the next tick — that oscillation is the visible flicker.
                updatedAt: new Date(now).toISOString()
              };
            }
          }
          return t;
        });
        
        return isChanged ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // --- HELPERS & XP MATH ---
  const todayDateStr = new Date().toISOString().split('T')[0];

  const getMonthKey = (offset = 0) => {
    const d = new Date();
    d.setMonth(d.getMonth() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const getDateRange = (startStr, endStr) => {
    const dates = [];
    const current = new Date(startStr);
    const end = new Date(endStr);

    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const getDaysWindow = (n, offsetPeriods) => {
    const days = [];
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i - (offsetPeriods * n));
      days.push(d.toISOString().split('T')[0]);
    }
    return days;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (seconds) => {
    if (!seconds) return "00:00";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  
    useEffect(() => {
    if (!user || loadingData) return;

    // First-time setup: start auditing from today onward only
    if (!penaltyMeta.lastAuditDate) {
      const initMeta = { lastAuditDate: todayDateStr };
      setPenaltyMeta(initMeta);
      updateCloud({ penaltyMeta: initMeta });
      return;
    }

    const yesterday = addDays(todayDateStr, -1);

    // Nothing new to audit yet
    if (penaltyMeta.lastAuditDate >= yesterday) return;

    const datesToAudit = getDateRange(addDays(penaltyMeta.lastAuditDate, 1), yesterday);

    let totalPenaltyXp = 0;
    let hasChanges = false;

    let updatedHabits = habits.map(h => ({
      ...h,
      history: h.history || [],
      penaltyHistory: h.penaltyHistory || []
    }));

    datesToAudit.forEach(dateStr => {
        updatedHabits = updatedHabits.map(habit => {
          // Skip weekly habits
          if (habit.frequency === 'weekly') return habit;
          
          const completedThatDay = habit.history.includes(dateStr);
          const alreadyPenalized = habit.penaltyHistory.includes(dateStr);
          if (!completedThatDay && !alreadyPenalized) {
            hasChanges = true;
            const harshLoss = 60 + Math.min(habit.streak * 5, 40);
            totalPenaltyXp += harshLoss;
            return { ...habit, streak: 0, penaltyHistory: [...habit.penaltyHistory, dateStr] };
          }
          return habit;
        });
    });

    const newPenaltyMeta = { lastAuditDate: yesterday };

    if (!hasChanges) {
      setPenaltyMeta(newPenaltyMeta);
      updateCloud({ penaltyMeta: newPenaltyMeta });
      return;
    }

    const lossResult = calculateLoseXp(totalPenaltyXp, level, xp, xpNeeded);
    const statsToUpdate = {
      level: lossResult.newLevel,
      xp: lossResult.newXp,
      xpNeeded: lossResult.newXpNeeded
    };

    setPenaltyMeta(newPenaltyMeta);
    setLevel(statsToUpdate.level);
    setXp(statsToUpdate.xp);
    setXpNeeded(statsToUpdate.xpNeeded);

    persistHabitsToCloud(updatedHabits, {
      penaltyMeta: newPenaltyMeta,
      stats: statsToUpdate
    });

    showNotification(`Missed training penalty: -${totalPenaltyXp} XP. Discipline has consequences.`);
  }, [user, loadingData, habits, penaltyMeta, todayDateStr, level, xp, xpNeeded]);

  const encouragingMessages = ["Amazing job!", "You're crushing it!", "One step closer to your goals.", "Outstanding work!", "Keep this momentum going!", "You are unstoppable."];

  const showNotification = (message, isLevelUp = false, onUndo = null) => {
    setNotificationObj({ visible: true, message, isLevelUp, onUndo });
    if (window.toastTimeout) clearTimeout(window.toastTimeout);
    window.toastTimeout = setTimeout(() => {
      setNotificationObj(prev => ({ ...prev, visible: false }));
    }, 5000); 
  };

  const getRandomEncouragement = () => encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];

  const calculateGainXp = (amount, currLevel, currXp, currXpNeeded) => {
    let newXp = currXp + amount;
    let newLevel = currLevel;
    let newXpNeeded = currXpNeeded;
    let leveledUp = false;

    while (newXp >= newXpNeeded) {
      newXp -= newXpNeeded;
      newLevel += 1;
      newXpNeeded = Math.floor(newXpNeeded * 1.2); 
      leveledUp = true;
    }
    return { newLevel, newXp, newXpNeeded, leveledUp };
  };

  const calculateLoseXp = (amount, currLevel, currXp, currXpNeeded) => {
    let newXp = currXp - amount;
    let newLevel = currLevel;
    let newXpNeeded = currXpNeeded;

    while (newXp < 0 && newLevel > 1) {
      newLevel -= 1;
      newXpNeeded = Math.ceil(newXpNeeded / 1.2); 
      newXp += newXpNeeded;
    }
    if (newXp < 0 && newLevel === 1) newXp = 0;
    return { newLevel, newXp, newXpNeeded };
  };

  const handleApiError = (e, fallbackMsg) => {
    console.error("API Error:", e);
    if (e.message && e.message.includes('401')) {
      if (hasGeminiKey()) {
        showNotification("⚠️ API Error (401): one of your Gemini keys is not valid — check both at the bottom of the page.");
      } else {
        showNotification("⚠️ You need to provide a valid Gemini API Key at the bottom of the page to use this feature.");
      }
    } else {
      showNotification(fallbackMsg || "Failed to consult the magic. Try again later.");
    }
  };

  // Every Gemini key the user has given, in the order they should be spent.
  const geminiKeys = () => [userApiKey, userApiKey2].map((k) => (k || '').trim()).filter(Boolean);
  const hasGeminiKey = () => geminiKeys().length > 0;

  // 401 = bad key, 403 = key disabled or the API not enabled on that project,
  // 429 = quota spent. All three mean "this key is finished for now", and none of
  // them get better by waiting — so they must not eat the retry backoff when the
  // very next key would have answered immediately.
  const isKeyDeadError = (err) => /HTTP (401|403|429)/.test(err?.message || '');

  const getApiOptions = (payload, key) => {
    const activeKey = (key || userApiKey).trim();
    if (!activeKey) throw new Error("No API Key provided. Please enter one at the bottom of the page.");
    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${activeKey}`;
    // gemini-2.5-flash is a reasoning model with "thinking" ON by default.
    // With a small maxOutputTokens budget the thinking tokens consume the whole
    // allowance and the API returns an empty response (finishReason MAX_TOKENS,
    // no text parts) — which broke AI day plans and slowed meal estimates.
    // thinkingBudget: 0 disables thinking so the budget goes to the JSON answer.
    const body = {
      ...payload,
      generationConfig: {
        thinkingConfig: { thinkingBudget: 0 },
        ...(payload.generationConfig || {})
      }
    };
    return { url, options: { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } };
  };

  const fetchWithBackoff = async (url, options) => {
    for (let i = 0; i < 5; i++) {
      try {
        const res = await fetch(url, options);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (err) {
        // Surface a dead key immediately so callGeminiRaw can rotate to the next
        // one. Spending ~15s of backoff on a key that is out of quota, while a
        // spare key sits unused, is the slowest possible way to fail.
        if (isKeyDeadError(err)) throw err;
        if (i === 4) throw err;
        await new Promise(r => setTimeout(r, Math.pow(2, i) * 1000));
      }
    }
  };

  // Spend Gemini keys in order, moving to the next the moment one is rejected or
  // out of quota. Free-tier limits are per project, so a key on a second Google
  // account is a whole extra allowance, not a retry of the same one.
  const callGeminiRaw = async (payload) => {
    const keys = geminiKeys();
    if (!keys.length) throw new Error('NO_GEMINI_KEY');
    for (let i = 0; i < keys.length; i += 1) {
      try {
        const { url, options } = getApiOptions(payload, keys[i]);
        return await fetchWithBackoff(url, options);
      } catch (err) {
        const isLast = i === keys.length - 1;
        if (!isKeyDeadError(err) || isLast) throw err;
        console.warn(`Gemini key ${i + 1} unavailable (${err.message}) — trying key ${i + 2}.`);
      }
    }
    throw new Error('NO_GEMINI_KEY');
  };

  const extractJsonObject = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const cleaned = raw.replace(/```json\s*/gi, '').replace(/```/g, '').trim();
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]);
    } catch {
      return null;
    }
  };

  // Primary: Gemini 2.5 Flash
  const callGeminiFlash = async (prompt, { temperature = 0.4, maxOutputTokens = 1024 } = {}) => {
    if (!hasGeminiKey()) throw new Error('NO_GEMINI_KEY');
    const payload = {
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: { temperature, maxOutputTokens }
    };
    const data = await callGeminiRaw(payload);
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!text || !String(text).trim()) throw new Error('Gemini returned empty response');
    return { text: String(text).trim(), source: 'gemini' };
  };

  // Fallback: DeepSeek Chat (V3 / cost-effective "flash" tier — much cheaper than reasoner/pro)
  const callDeepSeekFlash = async (prompt, { temperature = 0.4, max_tokens = 1024, system } = {}) => {
    const key = deepseekApiKey.trim();
    if (!key) throw new Error('NO_DEEPSEEK_KEY');
    const res = await fetch('https://api.deepseek.com/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${key}`
      },
      body: JSON.stringify({
        // deepseek-v4-flash = non-thinking flash tier: fastest + lowest cost fallback
        model: 'deepseek-v4-flash',
        messages: [
          ...(system ? [{ role: 'system', content: system }] : []),
          { role: 'user', content: prompt }
        ],
        temperature,
        max_tokens
      })
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`DeepSeek HTTP ${res.status}${errText ? `: ${errText.slice(0, 160)}` : ''}`);
    }
    const data = await res.json();
    const text = data?.choices?.[0]?.message?.content;
    if (!text || !String(text).trim()) throw new Error('DeepSeek returned empty response');
    return { text: String(text).trim(), source: 'deepseek' };
  };

  // Gemini first, DeepSeek flash as cost-effective fallback
  const callAiWithFallback = async (prompt, options = {}) => {
    const geminiAvailable = hasGeminiKey();
    const deepseekAvailable = !!deepseekApiKey.trim();
    if (!geminiAvailable && !deepseekAvailable) {
      throw new Error('NO_AI_KEYS');
    }

    if (geminiAvailable) {
      try {
        return await callGeminiFlash(prompt, options);
      } catch (err) {
        console.warn('Gemini failed, trying DeepSeek flash fallback:', err);
        if (!deepseekAvailable) throw err;
      }
    }

    return callDeepSeekFlash(prompt, {
      temperature: options.temperature,
      max_tokens: options.maxOutputTokens || options.max_tokens || 1024,
      system: options.system
    });
  };

  const handleConsultQuestMaster = async () => {
    if (!hasGeminiKey()) {
      showNotification("⚠️ Please enter a Gemini API Key at the bottom of the page first!");
      return;
    }
    setIsConsulting(true);
    try {
      const pendingQuests = todos.filter(t => !t.completed).map(t => t.text).join(", ");
      const activeHabits = habits.map(h => h.text).join(", ");
      const prompt = `You are an RPG Quest Master. User is Level ${level}. Pending quests: ${pendingQuests || "None"}. Habits: ${activeHabits || "None"}. Give a short 2-sentence pep talk. No markdown, no emojis.`;
      const payload = { contents: [{ parts: [{ text: prompt }] }] };
      const data = await callGeminiRaw(payload);
      const advice = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (advice) setQuestMasterAdvice(advice);
    } catch (e) { handleApiError(e, "The Quest Master is currently away."); } finally { setIsConsulting(false); }
  };

  const handleMagicBreakdown = async (todo) => {
    if (!hasGeminiKey()) {
      showNotification("⚠️ Please enter a Gemini API Key at the bottom of the page first!");
      return;
    }
    setBreakingDownId(todo.id);
    try {
      const prompt = `Break down this task into 2 to 3 smaller actionable sub-tasks. Task: "${todo.text}". Return ONLY a JSON object with a property "subtasks" (array of strings).`;
      const payload = {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: { responseMimeType: "application/json", responseSchema: { type: "OBJECT", properties: { subtasks: { type: "ARRAY", items: { type: "STRING" } } } } }
      };
      const data = await callGeminiRaw(payload);
      let text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!text) { showNotification("The magic fizzled out."); setBreakingDownId(null); return; }
      
      let cleanText = text.replace(/```json/gi, '').replace(/```/g, '');
      const startIdx = cleanText.indexOf('{');
      const endIdx = cleanText.lastIndexOf('}');
      if (startIdx !== -1 && endIdx !== -1) cleanText = cleanText.substring(startIdx, endIdx + 1);
      
      let parsed;
      try { parsed = JSON.parse(cleanText); } catch (e) { showNotification("The AI's magic was confusing."); setBreakingDownId(null); return; }
      
      if (parsed && Array.isArray(parsed.subtasks)) {
        const newTodos = parsed.subtasks.map((st, idx) => {
          const safeText = typeof st === 'string' ? st : (st?.text || JSON.stringify(st) || 'Sub-quest');
          return { id: Date.now() + idx, text: safeText, completed: false, label: todo.label, customTag: todo.customTag, timeSpent: 0, isRunning: false, lastTick: null, timeOfDay: todo.timeOfDay || '', allocatedTime: todo.allocatedTime || 0 };
        });
        
        const idx = todos.findIndex(t => String(t.id) === String(todo.id));
        if (idx !== -1) {
          const copy = [...(todosRef.current || todos)];
          copy.splice(idx + 1, 0, ...newTodos);
          persistTodosToCloud(copy);
          showNotification(`✨ Task broken down into ${parsed.subtasks.length} sub-quests!`);
        }
      }
    } catch (e) { handleApiError(e, "Failed to consult the magic."); } finally { setBreakingDownId(null); }
  };

const handleExport = () => {
  const dataToExport = buildBackupPayload();
  const dataStr = JSON.stringify(dataToExport, null, 2);

  const blob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `life-tracker-backup-${todayDateStr}.json`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  const stamp = new Date().toISOString();
  setLastExportAt(stamp);
  setDismissExportRemind(false);
  if (user) {
    try { localStorage.setItem(lastExportKey(user.uid), stamp); } catch { /* ignore */ }
    scheduleCloudBackup('export');
  }

  showNotification('Data exported — keep this JSON file somewhere safe.');
};

const handleImport = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();

  reader.onload = async (event) => {
    try {
      const rawText = event.target?.result;
      const importedData = JSON.parse(rawText);

      if (!importedData || typeof importedData !== 'object' || Array.isArray(importedData) || !hasKnownBackupKeys(importedData)) {
        showNotification('Invalid backup file format.');
        e.target.value = null;
        return;
      }

      const safeData = normalizeAppData(keepLiveGoals(keepLiveIntegrations(keepLiveSprintBoard(importedData))));

      applyAppDataToState(safeData);
      applyLifestyleFrom(safeData);
      journalRef.current = safeData.monthlyJournal || {};
      setJournalDraft(safeData.monthlyJournal[getMonthKey(journalMonthOffset)] || defaultJournalEntry);
      writeLocalCache(user.uid, safeData);
      touchAppImport(15000);
      const ok = await updateCloud(toCloudPayload(safeData), { fullDocument: true });
      touchAppImport(ok ? 8000 : 20000);

      showNotification(ok
        ? 'Data imported · cached locally · synced to Firebase'
        : 'Data imported locally · Firebase sync failed');
    } catch (error) {
      console.error('Import error:', error);
      showNotification('Error importing data. File might be corrupted.');
    }

    e.target.value = null;
  };

  reader.readAsText(file);
};

  // --- CALENDAR (SPRINT BOARD) ---
  // Every edit in the Calendar tab arrives here as a board => board updater. Only the
  // sections and dates that actually changed are sent, as dotted field paths, so a
  // phone holding an older copy can overwrite one day at most — never the board.
  const commitSprintBoard = (updater) => {
    const prev = sprintBoardRef.current;
    const next = normalizeSprintBoard(updater(prev));
    const patch = diffSprintBoard(prev, next);
    if (!Object.keys(patch).length) return;
    sprintBoardRef.current = next;
    setSprintBoard(next);
    // While a whole-board write is still parked, keep sending whole boards: the queue
    // coalesces into one patch, and a field cannot travel with its own sub-paths.
    updateCloud(
      pendingCloudUpdatesRef.current.some(hasWholeSprintBoard) ? { sprintBoard: next } : patch
    );
  };

  // The one deliberate whole-board write: an import replaces the calendar by design.
  const replaceSprintBoard = (board) => {
    const next = normalizeSprintBoard(board);
    sprintBoardRef.current = next;
    setSprintBoard(next);
    updateCloud({ sprintBoard: next });
    showNotification('Calendar imported.');
  };

  // --- VISION BOARD NUMBERED LISTS ---
  // React owns the textarea value, so the caret has to be restored by hand after a
  // programmatic edit — otherwise it jumps to the end of the text on every Enter.
  const applyVisionEdit = (result) => {
    if (!result) return false;
    setVisionBoardText(result.value);
    const el = visionRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.selectionStart = result.selectionStart;
        el.selectionEnd = result.selectionStart;
        el.focus();
      });
    }
    return true;
  };

  const handleVisionKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) return;
    const el = e.target;
    const result = continueNumberedList(el.value, el.selectionStart, el.selectionEnd);
    // Not on a numbered line — let the browser insert a plain newline as before.
    if (!result) return;
    e.preventDefault();
    applyVisionEdit(result);
  };

  const applyVisionNumbering = () => {
    const el = visionRef.current;
    if (!el) return;
    const result = toggleNumberedList(el.value, el.selectionStart, el.selectionEnd);
    if (!result) showNotification('Select the lines you want numbered first.');
    else applyVisionEdit(result);
  };

  const startCategoryEdit = (id, currentLabel) => { setEditingCategory(id); setCategoryEditForm(currentLabel); };
  const saveCategoryEdit = (id) => { const newLabels = { ...categoryLabels, [id]: categoryEditForm.trim() || defaultCategoryLabels[id] }; setCategoryLabels(newLabels); updateCloud({ categoryLabels: newLabels }); setEditingCategory(null); };
  const toggleCategoryCollapse = (id) => { const newCollapsed = { ...collapsedCategories, [id]: !collapsedCategories[id] }; setCollapsedCategories(newCollapsed); updateCloud({ collapsedCategories: newCollapsed }); };

  const handleSelectDomino = (cat, key) => {
    if (selectedDomino && selectedDomino.cat === cat && selectedDomino.key === key) { saveDominoAndClose(); } else {
      let currentDominos = dominos; let currentLabels = dominoLabels;
      if (selectedDomino) {
        const { cat: prevCat, key: prevKey } = selectedDomino;
        currentDominos = { ...currentDominos, [prevCat]: { ...currentDominos[prevCat], [prevKey]: editingDominoText } };
        currentLabels = { ...currentLabels, [prevCat]: { ...currentLabels[prevCat], [prevKey]: editingDominoLabel || currentLabels[prevCat][prevKey] } };
        setDominos(currentDominos); setDominoLabels(currentLabels); updateCloud({ dominos: currentDominos, dominoLabels: currentLabels });
      }
      setSelectedDomino({ cat, key }); setEditingDominoText(currentDominos[cat][key] || ""); setEditingDominoLabel(currentLabels[cat][key] || "");
    }
  };

  const saveDominoAndClose = () => {
    if (!selectedDomino) return; const { cat, key } = selectedDomino;
    const newDominos = { ...dominos, [cat]: { ...dominos[cat], [key]: editingDominoText } };
    const newLabels = { ...dominoLabels, [cat]: { ...dominoLabels[cat], [key]: editingDominoLabel || dominoLabels[cat][key] } };
    setDominos(newDominos); setDominoLabels(newLabels); updateCloud({ dominos: newDominos, dominoLabels: newLabels }); setSelectedDomino(null);
  };

  const toggleDominoCompletion = () => {
    if (!selectedDomino) return; const { cat, key } = selectedDomino; const isCompleting = !completedDominos[cat][key];
    const newCompleted = { ...completedDominos, [cat]: { ...completedDominos[cat], [key]: isCompleting } };
    setCompletedDominos(newCompleted);
    if (isCompleting) {
      const res = calculateGainXp(20, level, xp, xpNeeded);
      setLevel(res.newLevel); setXp(res.newXp); setXpNeeded(res.newXpNeeded);
      updateCloud({ completedDominos: newCompleted, stats: { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded } });
      if (res.leveledUp) showNotification(`Level Up! You are now Level ${res.newLevel}! 🎉`, true); else showNotification(`+20 XP! Domino toppled!`);
    } else {
      const res = calculateLoseXp(20, level, xp, xpNeeded); setLevel(res.newLevel); setXp(res.newXp); setXpNeeded(res.newXpNeeded);
      updateCloud({ completedDominos: newCompleted, stats: { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded } });
    }
  };

  const goalDraftFor = (catId) =>
    goalDrafts[catId] !== undefined ? goalDrafts[catId] : (dominos[catId]?.goal || '');
  const setGoalDraft = (catId, value) => setGoalDrafts((prev) => ({ ...prev, [catId]: value }));

  // Writes the typed goal into `dominos` and returns the post-write row map, because
  // setDominos is async and the AI fill below needs the goal in the same tick.
  const commitDominoGoal = (catId) => {
    const text = goalDraftFor(catId).trim();
    const next = { ...dominos, [catId]: { ...dominos[catId], goal: text } };
    if ((dominos[catId]?.goal || '') !== text) {
      setDominos(next);
      updateCloud({ dominos: next });
    }
    setGoalDrafts((prev) => {
      if (prev[catId] === undefined) return prev;
      const copy = { ...prev };
      delete copy[catId];
      return copy;
    });
    return { text, dominos: next };
  };

  // Given today, what calendar date is N months out. Used to turn the AI's
  // "12 months" answer into a deadline the user can actually aim at.
  const deadlineDateLabel = (months) => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // The user states the goal; the AI decides how long it realistically takes and
  // lays the ladder deadline → monthly → weekly → daily.
  const handleAIFillDominos = async (catId) => {
    if (!hasGeminiKey() && !deepseekApiKey.trim()) {
      showNotification('⚠️ Add a Gemini API key (preferred) or DeepSeek key in the footer first!');
      return;
    }
    const { text: goal, dominos: withGoal } = commitDominoGoal(catId);
    if (!goal) return showNotification('Write your goal clearly first, then let AI set the deadline.');

    setGeneratingDominos(catId);
    try {
      const prompt = `You are a goal-setting coach. The user stated ONE goal for their "${categoryLabels[catId] || catId}" area.

Goal: "${goal}"
User level: ${level}
Today: ${new Date().toISOString().split('T')[0]}
Context (their vision, may be empty): ${(visionBoardText || '').slice(0, 240) || '(none)'}

Do two things:
1. Judge a REALISTIC deadline for this goal. Be honest — not motivational. Answer in whole months (1-36).
2. Break the goal into exactly three nested commitments: what must be true this month, this week, and today.

Rules:
- "horizon" restates the goal as a finished outcome with a measurable marker.
- "month", "week", "day" each name ONE concrete action, not a theme. The daily one must fit in under 60 minutes.
- Each field under 120 characters.
- Return ONLY valid JSON (no markdown):
{
  "deadlineMonths": 12,
  "deadlineLabel": "1 Year",
  "horizon": "the finished outcome",
  "month": "one thing this month",
  "week": "one thing this week",
  "day": "one thing today"
}
"deadlineLabel" is a short human label like "6 Months", "1 Year", "18 Months".`;

      const { text } = await callAiWithFallback(prompt, {
        temperature: 0.4,
        maxOutputTokens: 1024,
        system: 'Return only compact JSON goal ladders.'
      });
      const parsed = extractJsonObject(text);
      if (!parsed || typeof parsed !== 'object') {
        showNotification('AI returned invalid format.');
        return;
      }

      const extractStr = (val) => (val ? (typeof val === 'string' ? val : (val.text || val.value || '')) : '');
      const monthsRaw = Number(parsed.deadlineMonths);
      const months = Number.isFinite(monthsRaw) ? Math.min(36, Math.max(1, Math.round(monthsRaw))) : 12;
      const deadlineLabel = extractStr(parsed.deadlineLabel).trim()
        || (months === 12 ? '1 Year' : `${months} Month${months === 1 ? '' : 's'}`);
      const deadline = `${deadlineLabel} · by ${deadlineDateLabel(months)}`;

      const nextDominos = {
        ...withGoal,
        [catId]: {
          ...withGoal[catId],
          deadline,
          deadlineMonths: String(months),
          year1: extractStr(parsed.horizon) || goal,
          month: extractStr(parsed.month),
          week: extractStr(parsed.week),
          day: extractStr(parsed.day)
        }
      };
      const nextLabels = {
        ...dominoLabels,
        [catId]: { ...dominoLabels[catId], year1: deadlineLabel }
      };
      const nextCompleted = {
        ...completedDominos,
        [catId]: { ...completedDominos[catId], year1: false, month: false, week: false, day: false }
      };

      setDominos(nextDominos);
      setDominoLabels(nextLabels);
      setCompletedDominos(nextCompleted);
      updateCloud({ dominos: nextDominos, dominoLabels: nextLabels, completedDominos: nextCompleted });

      showNotification(`✨ Deadline set: ${deadline}`);
    } catch (e) { handleApiError(e, 'Failed to consult magic for dominos.'); } finally { setGeneratingDominos(null); }
  };

const handleAddItem = (e) => {
  e.preventDefault();
  if (!newItemText.trim()) return;
  if (activeTab === 'todos') {
    const newTodos = [...(todosRef.current || todos), {
      id: Date.now(),
      text: newItemText.trim(),
      completed: false,
      label: newTaskLabel,
      customTag: customTag.trim(),
      timeSpent: 0,
      isRunning: false,
      lastTick: null,
      timeOfDay: newTaskTimeOfDay,
      dayOfWeek: newTaskDayOfWeek,
      allocatedTime: parseInt(newTaskAllocatedTime) || 0
    }];
    persistTodosToCloud(newTodos);
    setNewTaskLabel('today');
    setCustomTag('');
    setNewTaskTimeOfDay('');
    setNewTaskDayOfWeek('');
    setNewTaskAllocatedTime('');
  } else if (activeTab === 'habits') {
    const newHabit = {
      id: Date.now(),
      text: newItemText.trim(),
      streak: 0,
      lastCompletedDate: null,
      history: [],
      penaltyHistory: [],
      frequency: habitFrequency,
      weeklyTarget: habitFrequency === 'weekly' ? habitWeeklyTarget : null,
      weeklyProgress: { weekStartDate: null, completionsCount: 0 }
    };
    const newHabits = [...currentHabits(), newHabit];
    persistHabitsToCloud(newHabits).then((ok) => {
      showNotification(ok ? 'Habit saved · synced' : 'Habit saved locally · cloud sync failed');
    });
    setHabitFrequency('daily');
    setHabitWeeklyTarget(3);
  }
  setNewItemText('');
};

  const toggleTodo = (id) => {
    let xpChange = 0;
    const newTodos = (todosRef.current || todos).map(todo => {
      if (String(todo.id) === String(id)) { 
        const isCompleting = !todo.completed; 
        xpChange = isCompleting ? 15 : -15; 
        return { 
          ...todo, 
          completed: isCompleting, 
          isRunning: isCompleting ? false : todo.isRunning,
          lastTick: isCompleting ? null : todo.lastTick
        }; 
      }
      return todo;
    });
    let statsToUpdate = { level, xp, xpNeeded };
    if (xpChange > 0) {
      const res = calculateGainXp(xpChange, level, xp, xpNeeded); statsToUpdate = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded };
      if (res.leveledUp) showNotification(`Level Up! You are now Level ${res.newLevel}! 🎉`, true); else showNotification(`+${xpChange} XP! ${getRandomEncouragement()}`);
    } else if (xpChange < 0) { const res = calculateLoseXp(Math.abs(xpChange), level, xp, xpNeeded); statsToUpdate = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded }; }
    setLevel(statsToUpdate.level); setXp(statsToUpdate.xp); setXpNeeded(statsToUpdate.xpNeeded);
    persistTodosToCloud(newTodos, { stats: statsToUpdate });
  };

  const deleteTodo = (id) => {
    const itemToDelete = (todosRef.current || todos).find(t => String(t.id) === String(id)); let statsToUpdate = { level, xp, xpNeeded };
    if (itemToDelete?.completed) { const res = calculateLoseXp(15, level, xp, xpNeeded); statsToUpdate = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded }; setLevel(statsToUpdate.level); setXp(statsToUpdate.xp); setXpNeeded(statsToUpdate.xpNeeded); }
    const newTodos = (todosRef.current || todos).filter(todo => String(todo.id) !== String(id));
    if (itemToDelete?.id !== undefined && itemToDelete?.id !== null) {
      todosDeletedIdsRef.current.add(String(itemToDelete.id));
    }
    persistTodosToCloud(newTodos, { stats: statsToUpdate });
    showNotification(`Quest deleted.`, false, () => {
      let undoStats = { level: statsToUpdate.level, xp: statsToUpdate.xp, xpNeeded: statsToUpdate.xpNeeded };
      if (itemToDelete?.completed) { const res = calculateGainXp(15, undoStats.level, undoStats.xp, undoStats.xpNeeded); undoStats = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded }; setLevel(undoStats.level); setXp(undoStats.xp); setXpNeeded(undoStats.xpNeeded); }
      if (itemToDelete?.id !== undefined && itemToDelete?.id !== null) {
        todosDeletedIdsRef.current.delete(String(itemToDelete.id));
      }
      const restoredTodos = [...newTodos, itemToDelete]; persistTodosToCloud(restoredTodos, { stats: undoStats });
    });
  };

  const toggleTimer = (e, id) => { 
    e.stopPropagation(); 
    const updatedTodos = (todosRef.current || todos).map(t => { 
      if (String(t.id) === String(id)) {
        const isRunning = !t.isRunning;
        return { ...t, isRunning, lastTick: isRunning ? Date.now() : null }; 
      }
      return { ...t, isRunning: false, lastTick: null }; // Only allow one running at a time
    }); 
    persistTodosToCloud(updatedTodos);
  };

  const startEditing = (todo) => { 
    setEditingTodoId(todo.id); 
    setEditForm({ 
      text: todo.text || '', 
      label: todo.label || 'today', 
      customTag: todo.customTag || '', 
      timeOfDay: todo.timeOfDay || '', 
      dayOfWeek: todo.dayOfWeek || '', 
      allocatedTime: todo.allocatedTime || '' 
    }); 
  };
  const saveEdit = async () => { 
    if (!editForm?.text?.trim()) return; 
    const targetIdStr = String(editingTodoId);
    const newTodos = (todosRef.current || todos).map(t => 
      String(t?.id) === targetIdStr 
        ? { 
            ...t, 
            text: editForm.text.trim(), 
            label: ['today', 'week', 'should', 'could'].includes(editForm.label) ? editForm.label : 'today', 
            customTag: (editForm.customTag || '').trim().replace(/^#/, ''), 
            timeOfDay: editForm.timeOfDay || '', 
            dayOfWeek: editForm.dayOfWeek || '', 
            allocatedTime: parseInt(editForm.allocatedTime, 10) || 0 
          } 
        : t 
    ); 
    setEditingTodoId(null); 
    const ok = await persistTodosToCloud(newTodos); 
    showNotification(ok ? 'Quest updated · synced' : 'Quest saved locally · cloud sync failed');
  };
  const cancelEdit = () => setEditingTodoId(null);

  // New Habit Editing Functions
    const startEditingHabit = (habit) => {
      setEditingHabitId(habit.id);
      setHabitEditForm(habit.text);
      setEditingHabitFrequency(habit.frequency || 'daily');
      setEditingHabitWeeklyTarget(habit.weeklyTarget || 3);
    };

const saveHabitEdit = async () => {
  if (!habitEditForm.trim()) return;
  const targetIdStr = String(editingHabitId);
  const newHabits = currentHabits().map(h => {
    if (String(h?.id) === targetIdStr) {
      const updated = {
        ...h,
        text: habitEditForm.trim(),
        frequency: editingHabitFrequency,
        weeklyTarget: editingHabitFrequency === 'weekly' ? editingHabitWeeklyTarget : null
      };
      // Switching from weekly to daily
      if (editingHabitFrequency === 'daily' && h.frequency === 'weekly') {
        updated.weeklyProgress = { weekStartDate: null, completionsCount: 0 };
        updated.streak = 0;
      }
      // Switching from daily to weekly
      if (editingHabitFrequency === 'weekly' && h.frequency === 'daily') {
        updated.streak = 0;
        updated.weeklyProgress = { weekStartDate: null, completionsCount: 0 };
      }
      return updated;
    }
    return h;
  });
  setEditingHabitId(null);
  const ok = await persistHabitsToCloud(newHabits);
  showNotification(ok ? 'Habit saved · synced' : 'Habit saved locally · cloud sync failed');
};

  const cancelHabitEdit = () => setEditingHabitId(null);
  
  const startEditingHabitStreak = (habit) => {
  const isWeekly = habit.frequency === 'weekly';
  const raw = isWeekly
    ? Number(habit.weeklyProgress?.completionsCount)
    : Number(habit.streak);
  const safeValue = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;

  setEditingStreakHabitId(habit.id);
  setStreakEditForm(String(safeValue));
};

const saveHabitStreakEdit = async () => {
  const parsed = Math.floor(Number(streakEditForm));

  if (!Number.isFinite(parsed) || parsed < 0) {
    showNotification('Please enter a valid number.');
    return;
  }

  const base = currentHabits();
  const target = base.find((habit) => String(habit?.id) === String(editingStreakHabitId));
  if (!target) {
    cancelHabitStreakEdit();
    return;
  }

  const isWeekly = target.frequency === 'weekly';
  const nextValue = isWeekly ? Math.min(14, parsed) : parsed;
  const yesterday = addDays(todayDateStr, -1);
  const protectDates = penaltyMeta?.lastAuditDate && penaltyMeta.lastAuditDate < yesterday
    ? getDateRange(addDays(penaltyMeta.lastAuditDate, 1), yesterday)
    : [];

  const newHabits = base.map((habit) => {
    if (habit.id !== editingStreakHabitId) return habit;
    if (habit.frequency === 'weekly') {
      return {
        ...habit,
        weeklyProgress: {
          weekStartDate: getWeekStartDate(todayDateStr),
          completionsCount: nextValue
        }
      };
    }
    return {
      ...habit,
      streak: nextValue,
      penaltyHistory: Array.from(new Set([...(habit.penaltyHistory || []), ...protectDates]))
    };
  });

  setEditingStreakHabitId(null);
  setStreakEditForm('');

  const ok = await persistHabitsToCloud(newHabits);
  const label = isWeekly
    ? `This week updated to ${nextValue} time${nextValue === 1 ? '' : 's'}`
    : `Day streak updated to ${nextValue}`;
  showNotification(ok ? `${label} · synced` : `${label} locally · cloud sync failed`);
};

const cancelHabitStreakEdit = () => {
  setEditingStreakHabitId(null);
  setStreakEditForm('');
};

  // --- DRAG AND DROP HANDLERS FOR HABITS ---
  const handleDragStart = (e, index) => { setDraggedItemIdx(index); e.dataTransfer.setData("text/plain", index); };
  const handleDragEnter = (e, index) => { e.preventDefault(); setDragOverItemIdx(index); };
  const handleDrop = (e, index) => {
    e.preventDefault(); e.stopPropagation(); if (draggedItemIdx === null || draggedItemIdx === index) { setDraggedItemIdx(null); setDragOverItemIdx(null); return; }
    if (activeTab === 'habits') { const newHabits = [...currentHabits()]; const draggedItem = newHabits[draggedItemIdx]; newHabits.splice(draggedItemIdx, 1); newHabits.splice(index, 0, draggedItem); persistHabitsToCloud(newHabits); }
    setDraggedItemIdx(null); setDragOverItemIdx(null);
  };
  const handleDragEnd = () => { setDraggedItemIdx(null); setDragOverItemIdx(null); };

// --- CUSTOM DRAG HANDLERS FOR TODOS (works on desktop & mobile) ---
const dragState = useRef({
  active: false,
  dragItemId: null,
  dragOverItemId: null,
  dragOverPosition: null,
  dragOverGroupId: null,
  rejected: false,
}).current;

// Mirror the ref into React state only when something actually changed, so dragging
// across a long list doesn't re-render on every pointer event.
const setDragIndicator = (itemId, position, groupId, rejected) => {
  if (
    dragState.dragOverItemId === itemId &&
    dragState.dragOverPosition === position &&
    dragState.dragOverGroupId === groupId &&
    dragState.rejected === rejected
  ) return;

  dragState.dragOverItemId = itemId;
  dragState.dragOverPosition = position;
  dragState.dragOverGroupId = groupId;
  dragState.rejected = rejected;

  setDragOverItemId(itemId);
  setDragOverPosition(position);
  setDragOverGroupId(groupId);
  setDragRejected(rejected);
};

const startDrag = (e, id) => {
  if (editingTodoId !== null && editingTodoId !== undefined && String(editingTodoId) === String(id)) return;
  e.preventDefault();
  e.stopPropagation();
  
  // Get touch point if on mobile
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  
  dragState.active = true;
  dragState.dragItemId = id;
  dragState.dragOverItemId = null;
  dragState.dragOverPosition = null;
  dragState.dragOverGroupId = null;
  dragState.rejected = false;

  setDragActive(true);
  setDragItemId(id);
  
  // Prevent scrolling and selection
  document.body.style.userSelect = 'none';
  document.body.style.webkitUserSelect = 'none';
  document.body.style.overflow = 'hidden';
  
  // Add event listeners
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onEnd);
  window.addEventListener('touchmove', onMove, { passive: false });
  window.addEventListener('touchend', onEnd);
};

const onMove = (e) => {
  if (!dragState.active) return;
  e.preventDefault();
  
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  
  const draggedTodo = todos.find(t => String(t.id) === String(dragState.dragItemId));
  if (!draggedTodo) return;

  // Find both the hovered task and its enclosing group under the cursor.
  const elements = document.elementsFromPoint(clientX, clientY);
  let itemEl = null;
  let groupEl = null;
  for (const el of elements) {
    if (!el.hasAttribute) continue;
    if (!itemEl && el.hasAttribute('data-todo-id')) itemEl = el;
    if (!groupEl && el.hasAttribute('data-group-id')) groupEl = el;
    if (itemEl && groupEl) break;
  }

  const hoveredTodo = itemEl
    ? todos.find(t => String(t.id) === itemEl.getAttribute('data-todo-id'))
    : null;

  // A hovered task decides the group; otherwise fall back to the group container
  // itself, so dropping on a header or a collapsed/empty group still works.
  const groupId = hoveredTodo ? hoveredTodo.label : (groupEl ? groupEl.getAttribute('data-group-id') : null);

  if (!groupId) {
    setDragIndicator(null, null, null, false);
    return;
  }

  // "This Week's Priority" is a promotion lane: only Should Do tasks may enter.
  if (groupId === 'week' && !['should', 'week'].includes(draggedTodo.label)) {
    setDragIndicator(null, null, groupId, true);
    return;
  }

  if (hoveredTodo && String(hoveredTodo.id) !== String(draggedTodo.id)) {
    const rect = itemEl.getBoundingClientRect();
    const position = clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    setDragIndicator(hoveredTodo.id, position, groupId, false);
    return;
  }

  // Over the group but not over another task — append to the end of that group.
  setDragIndicator(null, null, groupId, false);
};

const onEnd = (e) => {
  if (!dragState.active) {
    resetDrag();
    return;
  }
  
  const { dragItemId, dragOverItemId, dragOverPosition, dragOverGroupId, rejected } = dragState;

  if (!rejected && dragOverGroupId) {
    const newTodos = [...todos];
    const draggedIdx = newTodos.findIndex(t => String(t.id) === String(dragItemId));

    if (draggedIdx !== -1) {
      const originalLabel = newTodos[draggedIdx].label;
      const movedItem = { ...newTodos[draggedIdx], label: dragOverGroupId };
      newTodos.splice(draggedIdx, 1);

      let insertAt;
      if (dragOverItemId && dragOverItemId !== dragItemId) {
        const targetIdx = newTodos.findIndex(t => t.id === dragOverItemId);
        insertAt = targetIdx === -1
          ? newTodos.length
          : targetIdx + (dragOverPosition === 'below' ? 1 : 0);
      } else {
        // Dropped on a header or empty space — place after the group's last task.
        let lastIdx = -1;
        newTodos.forEach((t, i) => { if (t.label === dragOverGroupId) lastIdx = i; });
        insertAt = lastIdx + 1;
      }

      newTodos.splice(insertAt, 0, movedItem);

      // Skip the write when the task landed exactly where it started.
      if (originalLabel !== dragOverGroupId || insertAt !== draggedIdx) {
        persistTodosToCloud(newTodos);
      }
    }
  }

  resetDrag();
};

const resetDrag = () => {
  dragState.active = false;
  dragState.dragItemId = null;
  dragState.dragOverItemId = null;
  dragState.dragOverPosition = null;
  dragState.dragOverGroupId = null;
  dragState.rejected = false;

  setDragActive(false);
  setDragItemId(null);
  setDragOverItemId(null);
  setDragOverPosition(null);
  setDragOverGroupId(null);
  setDragRejected(false);

  window.removeEventListener('mousemove', onMove);
  window.removeEventListener('mouseup', onEnd);
  window.removeEventListener('touchmove', onMove);
  window.removeEventListener('touchend', onEnd);
  
  document.body.style.userSelect = '';
  document.body.style.webkitUserSelect = '';
  document.body.style.overflow = '';
};
 

  const startHeaderEdit = () => { setHeaderEditForm(headerText); setIsEditingHeader(true); };
  const saveHeaderEdit = () => { const newHeader = { title: headerEditForm.title.trim() || 'Your Journey', subtitle: headerEditForm.subtitle.trim() || 'Every step builds momentum.' }; setHeaderText(newHeader); updateCloud({ header: newHeader }); setIsEditingHeader(false); };
  const cancelHeaderEdit = () => setIsEditingHeader(false);

  const currentJournalMonthKey = getMonthKey(journalMonthOffset);

  const lastJournalMonthKeyRef = useRef(currentJournalMonthKey);
  useEffect(() => {
    const incoming = monthlyJournal[currentJournalMonthKey] || defaultJournalEntry;
    const monthChanged = lastJournalMonthKeyRef.current !== currentJournalMonthKey;
    lastJournalMonthKeyRef.current = currentJournalMonthKey;
    if (monthChanged) {
      setJournalDraft(incoming);
      return;
    }
    setJournalDraft((prev) => {
      if (Date.now() < suppressRemoteJournalUntilRef.current || Date.now() < suppressRemoteAppUntilRef.current) {
        const prevAt = typeof prev.updatedAt === 'string' ? prev.updatedAt : '';
        const incomingAt = typeof incoming.updatedAt === 'string' ? incoming.updatedAt : '';
        const prevText = `${prev.expectations || ''}${prev.accomplished || ''}`.trim();
        const incomingText = `${incoming.expectations || ''}${incoming.accomplished || ''}`.trim();
        if (prevText && prevText !== incomingText) {
          const incomingNewer = incomingAt && (!prevAt || incomingAt > prevAt);
          if (!incomingNewer) return prev;
        }
      }
      return incoming;
    });
  }, [currentJournalMonthKey, monthlyJournal]);

  useEffect(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + mealDateOffset);
    const key = d.toISOString().split('T')[0];
    const entry = (lifestyleRef.current.stepLog || {})[key] || stepLog[key];
    const steps = Number(entry?.steps);
    setStepDraft(Number.isFinite(steps) && steps > 0 ? String(steps) : '');
  }, [mealDateOffset, stepLog]);

  const saveJournalEntry = async () => {
    touchJournalEdit(15000);
    const latest = journalRef.current && typeof journalRef.current === 'object'
      ? journalRef.current
      : monthlyJournal;
    const updatedJournal = {
      ...latest,
      [currentJournalMonthKey]: {
        expectations: journalDraft.expectations || '',
        accomplished: journalDraft.accomplished || '',
        updatedAt: new Date().toISOString()
      }
    };

    journalRef.current = updatedJournal;
    setMonthlyJournal(updatedJournal);
    setJournalDraft(updatedJournal[currentJournalMonthKey]);
    if (user) cacheCloudUpdatesLocally({ monthlyJournal: updatedJournal });
    const ok = await updateCloud({ monthlyJournal: updatedJournal });
    touchJournalEdit(ok ? 4000 : 20000);
    showNotification(ok
      ? `Journal saved for ${getMonthLabel(currentJournalMonthKey)}.`
      : `Journal saved locally — Firebase sync failed.`);
  };

  const saveIntegration = async (draft) => {
    const entry = integrationEntryFromDraft(draft, {
      id: draft?.id || newIntegrationId()
    });
    if (!entry) {
      showNotification('Add the book, the idea, and where it lives in your life.');
      return false;
    }
    touchIntegrationsEdit(15000);
    const safeMap = normalizeIntegrations({ ...integrationsRef.current, [entry.id]: entry });
    const stored = safeMap[entry.id];
    if (!stored) return false;
    integrationsRef.current = safeMap;
    setIntegrations(safeMap);
    const patch = { [`integrations.${stored.id}`]: stored };
    if (user) cacheCloudUpdatesLocally(patch);
    const ok = await updateCloud(patch);
    touchIntegrationsEdit(ok ? 4000 : 20000);
    showNotification(ok
      ? `Integrated: ${stored.principle}`
      : 'Saved on this device — Firebase sync failed.');
    // The idea is already in the list. Clearing the form must not depend on Firebase,
    // or a failed sync leaves the draft filled and the next save creates a second copy.
    return true;
  };

  const deleteIntegration = async (id) => {
    const current = integrationsRef.current?.[id];
    if (!current || current.deleted) return;
    touchIntegrationsEdit(15000);
    const tombstone = { ...current, deleted: true, updatedAt: new Date().toISOString() };
    const safeMap = normalizeIntegrations({ ...integrationsRef.current, [id]: tombstone });
    const stored = safeMap[id];
    if (!stored) return;
    integrationsRef.current = safeMap;
    setIntegrations(safeMap);
    const patch = { [`integrations.${id}`]: stored };
    if (user) cacheCloudUpdatesLocally(patch);
    const ok = await updateCloud(patch);
    touchIntegrationsEdit(ok ? 4000 : 20000);
    showNotification('Removed from Integrated.', false, () => {
      const restored = { ...current, deleted: false, updatedAt: new Date().toISOString() };
      const restoredMap = normalizeIntegrations({ ...integrationsRef.current, [id]: restored });
      const revived = restoredMap[id];
      if (!revived) return;
      integrationsRef.current = restoredMap;
      setIntegrations(restoredMap);
      const undoPatch = { [`integrations.${id}`]: revived };
      if (user) cacheCloudUpdatesLocally(undoPatch);
      updateCloud(undoPatch);
    });
  };

  // Lay a shell write or a step write onto whatever is in the ref right now.
  // A title save must not drop a step check that landed a moment earlier, and a
  // step check must not drop a title save. The cloud patch stays limited to the
  // fields this action actually changed.
  const goalLayeredOnLatest = (nextGoal, patch) => {
    const latest = goalsRef.current?.[nextGoal.id];
    if (!latest) return nextGoal;
    const itemWrites = Object.entries(patch || {}).filter(([key]) => key.includes('.items.'));
    if (itemWrites.length === 0) {
      return {
        ...latest,
        id: nextGoal.id,
        title: nextGoal.title,
        notes: nextGoal.notes,
        targetDate: nextGoal.targetDate,
        updatedAt: nextGoal.updatedAt,
        deleted: nextGoal.deleted,
        items: latest.items || {}
      };
    }
    const items = { ...(latest.items || {}) };
    itemWrites.forEach(([, item]) => {
      if (item?.id) items[item.id] = item;
    });
    return { ...latest, items };
  };

  const storeGoalLocally = (nextGoal, patch) => {
    const goalToStore = goalLayeredOnLatest(nextGoal, patch);
    const safeMap = normalizeGoals({ ...goalsRef.current, [goalToStore.id]: goalToStore });
    const stored = safeMap[goalToStore.id];
    if (!stored) return null;
    goalsRef.current = safeMap;
    setGoals(safeMap);
    return stored;
  };

  const commitGoals = async (nextGoal, patch, { quiet = false, message } = {}) => {
    if (!nextGoal) return false;
    touchGoalsEdit(15000);
    const stored = storeGoalLocally(nextGoal, patch);
    if (!stored) return false;
    if (user) cacheCloudUpdatesLocally(patch);
    const ok = await updateCloud(patch);
    touchGoalsEdit(ok ? 4000 : 20000);
    if (!quiet || !ok) {
      showNotification(ok
        ? (message || `Goal saved: ${stored.title}`)
        : 'Saved on this device — Firebase sync failed.');
    }
    return true;
  };

  const saveGoal = async (draft) => {
    const now = new Date().toISOString();
    const existing = draft?.id ? goalsRef.current?.[draft.id] : null;
    if (existing && !existing.deleted) {
      const next = updateGoalShell(existing, draft, now);
      if (!next) {
        showNotification('A goal needs a title.');
        return false;
      }
      if (next === existing) return true;
      return commitGoals(next, goalShellCloudPatch(next), { message: `Goal saved: ${next.title}` });
    }
    const created = goalFromDraft(draft, { id: draft?.id, now });
    if (!created) {
      showNotification('A goal needs a title.');
      return false;
    }
    return commitGoals(created, goalCreateCloudPatch(created), { message: `Goal saved: ${created.title}` });
  };

  const toggleGoalItem = async (goalId, itemId, done) => {
    const goal = goalsRef.current?.[goalId];
    const next = toggleChecklistItem(goal, itemId, done, new Date().toISOString());
    if (!next) return false;
    const item = next.items?.[itemId];
    if (!item) return false;
    return commitGoals(next, goalItemCloudPatch(goalId, item), { quiet: true });
  };

  const addGoalItem = async (goalId, text) => {
    const goal = goalsRef.current?.[goalId];
    const added = addChecklistItem(goal, text, new Date().toISOString());
    if (!added) return false;
    return commitGoals(added.goal, goalItemCloudPatch(goalId, added.item), { quiet: true });
  };

  const deleteGoalItem = async (goalId, itemId) => {
    const goal = goalsRef.current?.[goalId];
    const removed = deleteChecklistItem(goal, itemId, new Date().toISOString());
    if (!removed) return;
    const captured = goal.items?.[itemId];
    const patch = goalItemCloudPatch(goalId, removed.item);
    const stored = await commitGoals(removed.goal, patch, {
      quiet: true,
      message: 'Step removed.'
    });
    if (!stored) return;
    showNotification('Step removed.', false, () => {
      const latest = goalsRef.current?.[goalId] || removed.goal;
      const revived = restoreChecklistItem(latest, captured, new Date().toISOString());
      if (!revived?.item) return;
      touchGoalsEdit(15000);
      const undoPatch = goalItemCloudPatch(goalId, revived.item);
      storeGoalLocally(revived.goal, undoPatch);
      if (user) cacheCloudUpdatesLocally(undoPatch);
      updateCloud(undoPatch);
    });
  };

  const deleteGoal = async (id) => {
    const current = goalsRef.current?.[id];
    const tombstone = deleteGoalShell(current, new Date().toISOString());
    if (!tombstone) return;
    const patch = goalShellCloudPatch(tombstone);
    const stored = await commitGoals(tombstone, patch, { quiet: true });
    if (!stored) return;
    showNotification('Removed from Goals.', false, () => {
      const latest = goalsRef.current?.[id] || current;
      const restored = restoreGoalShell(latest, new Date().toISOString());
      if (!restored) return;
      touchGoalsEdit(15000);
      const undoPatch = goalShellCloudPatch(restored);
      storeGoalLocally(restored, undoPatch);
      if (user) cacheCloudUpdatesLocally(undoPatch);
      updateCloud(undoPatch);
    });
  };

const toggleHabit = (id) => {
  let statsToUpdate = { level, xp, xpNeeded };
  const newHabits = currentHabits().map(habit => {
    if (habit.id !== id) return habit;

    const isDaily = habit.frequency !== 'weekly';
    const today = todayDateStr;

    if (isDaily) {
      const isDoneToday = habit.lastCompletedDate === today;
      if (isDoneToday) {
        const res = calculateLoseXp(25 + (habit.streak * 2), statsToUpdate.level, statsToUpdate.xp, statsToUpdate.xpNeeded);
        statsToUpdate = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded };
        const newHistory = habit.history?.filter(d => d !== today) || [];
        const newStreak = Math.max(0, habit.streak - 1);
        const prevDate = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
        return { ...habit, streak: newStreak, lastCompletedDate: prevDate, history: newHistory };
      } else {
        let newStreak = habit.streak;
        const lastDate = habit.lastCompletedDate ? new Date(habit.lastCompletedDate) : null;
        const todayDate = new Date(today);
        if (lastDate) {
          const diffDays = Math.ceil(Math.abs(todayDate - lastDate) / (1000 * 60 * 60 * 24));
          if (diffDays === 1) newStreak += 1;
          else if (diffDays > 1) newStreak = 1;
        } else {
          newStreak = 1;
        }
        const xpAmount = 25 + (newStreak * 2);
        const res = calculateGainXp(xpAmount, statsToUpdate.level, statsToUpdate.xp, statsToUpdate.xpNeeded);
        statsToUpdate = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded };
        if (res.leveledUp) showNotification(`Level Up! You are now Level ${res.newLevel}! 🎉`, true);
        else showNotification(`+${xpAmount} XP! ${getRandomEncouragement()}`);
        return { ...habit, streak: newStreak, lastCompletedDate: today, history: [...(habit.history || []), today] };
      }
    } else {
      // Weekly habit logic
      const currentWeekStart = getWeekStartDate(today);
      const { weekStartDate, completionsCount } = habit.weeklyProgress || {};
      let newCompletionsCount = completionsCount;
      let newWeekStart = weekStartDate;
      if (weekStartDate !== currentWeekStart) {
        newWeekStart = currentWeekStart;
        newCompletionsCount = 0;
      }
      const alreadyDoneToday = habit.history?.includes(today);
      if (alreadyDoneToday) {
        const updatedHistory = habit.history.filter(d => d !== today);
        const updatedCompletions = Math.max(0, newCompletionsCount - 1);
        const penaltyXp = 15;
        const lossRes = calculateLoseXp(penaltyXp, statsToUpdate.level, statsToUpdate.xp, statsToUpdate.xpNeeded);
        statsToUpdate = { level: lossRes.newLevel, xp: lossRes.newXp, xpNeeded: lossRes.newXpNeeded };
        showNotification(`-${penaltyXp} XP (weekly training undone)`);
        return { ...habit, history: updatedHistory, weeklyProgress: { weekStartDate: newWeekStart, completionsCount: updatedCompletions } };
      } else {
        const updatedHistory = [...(habit.history || []), today];
        const newCount = newCompletionsCount + 1;
        let xpAmount = 0;
        if (newCount === habit.weeklyTarget && newCount > completionsCount) {
          xpAmount = 50 + (habit.weeklyTarget * 5);
          const gainRes = calculateGainXp(xpAmount, statsToUpdate.level, statsToUpdate.xp, statsToUpdate.xpNeeded);
          statsToUpdate = { level: gainRes.newLevel, xp: gainRes.newXp, xpNeeded: gainRes.newXpNeeded };
          if (gainRes.leveledUp) showNotification(`Level Up! You are now Level ${gainRes.newLevel}! 🎉`, true);
          else showNotification(`+${xpAmount} XP! Weekly goal achieved!`);
        } else if (newCount <= habit.weeklyTarget) {
          xpAmount = 5;
          const gainRes = calculateGainXp(xpAmount, statsToUpdate.level, statsToUpdate.xp, statsToUpdate.xpNeeded);
          statsToUpdate = { level: gainRes.newLevel, xp: gainRes.newXp, xpNeeded: gainRes.newXpNeeded };
          showNotification(`+${xpAmount} XP (training session)`);
        }
        return { ...habit, history: updatedHistory, weeklyProgress: { weekStartDate: newWeekStart, completionsCount: newCount } };
      }
    }
  });
  setLevel(statsToUpdate.level);
  setXp(statsToUpdate.xp);
  setXpNeeded(statsToUpdate.xpNeeded);
  persistHabitsToCloud(newHabits, { stats: statsToUpdate });
};

  const deleteHabit = (id) => {
    const source = currentHabits();
    const targetIdStr = String(id);
    const itemToDelete = source.find(h => String(h?.id) === targetIdStr);
    if (!itemToDelete) return;
    const isDoneToday = itemToDelete.lastCompletedDate === todayDateStr;
    let statsToUpdate = { level, xp, xpNeeded };
    if (isDoneToday) {
      const res = calculateLoseXp(25 + (itemToDelete.streak * 2), level, xp, xpNeeded);
      statsToUpdate = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded };
      setLevel(statsToUpdate.level);
      setXp(statsToUpdate.xp);
      setXpNeeded(statsToUpdate.xpNeeded);
    }
    const newHabits = source.filter(habit => String(habit?.id) !== targetIdStr);
    persistHabitsToCloud(newHabits, { stats: statsToUpdate });
    showNotification(`Habit deleted.`, false, () => {
      let undoStats = { level: statsToUpdate.level, xp: statsToUpdate.xp, xpNeeded: statsToUpdate.xpNeeded };
      if (isDoneToday) {
        const res = calculateGainXp(25 + (itemToDelete.streak * 2), undoStats.level, undoStats.xp, undoStats.xpNeeded);
        undoStats = { level: res.newLevel, xp: res.newXp, xpNeeded: res.newXpNeeded };
        setLevel(undoStats.level);
        setXp(undoStats.xp);
        setXpNeeded(undoStats.xpNeeded);
      }
      const restoredHabits = [...newHabits, itemToDelete];
      persistHabitsToCloud(restoredHabits, { stats: undoStats });
    });
  };

  // --- RENDERING VIEWS ---

  if (loadingData) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center gap-4 text-indigo-500">
        <Loader2 className="w-10 h-10 animate-spin" />
        <p className="text-zinc-500 font-medium animate-pulse">Syncing with your Journey...</p>
      </div>
    );
  }

  // --- LOGIN SCREEN ---
  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-sm border border-zinc-100 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-indigo-50 text-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
            <Sparkles className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Life Tracker</h1>
          <p className="text-zinc-500 mb-8 text-sm leading-relaxed">Sign in to safely sync your quests, stats, and dominos across all your devices.</p>
          
          <div className="space-y-4">
            <button 
              onClick={handleGoogleLogin}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 px-4 rounded-xl transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-3"
            >
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- MAIN APP SCREEN ---
  const xpPercentage = Math.min(100, Math.max(0, (xp / xpNeeded) * 100));

  // Helper for Daily Schedule block info
  const getBlockInfo = (index, totalBlocks, distribution) => {
    const single = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night' }[distribution];
    if (single) return { period: single, count: index + 1 };

    // Split into thirds using the chosen 3-period code (e.g. M/M/N).
    const codes = parseSplitCodes(distribution) || ['M', 'A', 'N'];
    const perPeriod = Math.ceil(totalBlocks / 3);
    const periodAt = (i) => PERIOD_BY_CODE[codes[i < perPeriod ? 0 : i < perPeriod * 2 ? 1 : 2]];
    const period = periodAt(index);
    // Count continuously across contiguous same-period thirds (M/M reads Morning 1..N).
    let start = index;
    while (start > 0 && periodAt(start - 1) === period) start--;
    return { period, count: index - start + 1 };
  };

  const getEndTimeFromStart = (startTime) => {
    if (!startTime) return '--:--';
    const [h, m] = startTime.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return '--:--';
    const endTotalMin = h * 60 + m + 30;
    const eH = Math.floor(endTotalMin / 60) % 24;
    const eM = endTotalMin % 60;
    return `${String(eH).padStart(2, '0')}:${String(eM).padStart(2, '0')}`;
  };

  // Next 3 days in advance (tomorrow → +3)
  const getNextThreeDays = () => {
    return [1, 2, 3].map((offset) => {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() + offset);
      const dateKey = d.toISOString().split('T')[0];
      const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
      const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const relative = offset === 1 ? 'Tomorrow' : `In ${offset} days`;
      return { dateKey, weekday, label, relative, offset };
    });
  };

  const emptyDayPlan = (dateKey) => ({
    focus: '', notes: '', segments: defaultPlanSegments(dateKey), aiSource: '', aiUpdatedAt: null
  });
  const getDayPlan = (dateKey) => dayPlans[dateKey] || emptyDayPlan(dateKey);

  // All day-plan writers compute the next value synchronously from the live ref and
  // commit it directly. Deriving it inside a setState updater is unreliable (React
  // may defer the updater) and scheduling a cloud save from in there is a
  // render-phase side effect.
  const commitDayPlans = (nextDayPlans) => {
    // Captured before the mutation below, like the meal/money/step writers. This is
    // the only point that still holds the pre-edit plans — scheduleDayPlansCloudSave
    // runs after this assignment, and its timer fires later still.
    const prevDayPlans = lifestyleRef.current.dayPlans;
    lifestyleRef.current = { ...lifestyleRef.current, dayPlans: nextDayPlans };
    setDayPlans(nextDayPlans);
    scheduleDayPlansCloudSave(nextDayPlans, prevDayPlans);
  };

  const getDayPlanFromRef = (dateKey) =>
    (lifestyleRef.current.dayPlans || {})[dateKey] || emptyDayPlan(dateKey);

  const updateDayPlanField = (dateKey, field, value) => {
    touchLifestyleEdit();
    const current = getDayPlanFromRef(dateKey);
    commitDayPlans({
      ...(lifestyleRef.current.dayPlans || {}),
      [dateKey]: { ...current, [field]: value }
    });
  };

  const updatePlanSegmentText = (dateKey, segId, text) => {
    touchLifestyleEdit();
    const current = getDayPlanFromRef(dateKey);
    const segments = (current.segments || []).map((s) => (s.id === segId ? { ...s, text } : s));
    commitDayPlans({
      ...(lifestyleRef.current.dayPlans || {}),
      [dateKey]: { ...current, segments }
    });
  };

  const reorderPlanSegments = (dateKey, fromIndex, toIndex) => {
    touchLifestyleEdit();
    const current = getDayPlanFromRef(dateKey);
    const segments = arrayMove(current.segments || [], fromIndex, toIndex);
    commitDayPlans({
      ...(lifestyleRef.current.dayPlans || {}),
      [dateKey]: { ...current, segments }
    });
  };

  // Grow or shrink a block by 30 min. Growing reclaims 30 min from the tail and
  // shrinking gives 30 min back to the tail, so the day always ends at 11 PM.
  // Repeated growth merges consecutive slots into arbitrarily bigger blocks
  // (30 min → 1 hr → 1.5 hr → …), floor 30 min.
  const resizePlanSegment = (dateKey, segId, deltaMin) => {
    touchLifestyleEdit();
    const current = getDayPlanFromRef(dateKey);
    const segments = [...(current.segments || [])];
    const idx = segments.findIndex((s) => s.id === segId);
    if (idx < 0) return;

    if (deltaMin > 0) {
      // Nothing to reclaim from if this block already owns the tail.
      if (idx === segments.length - 1) return;
      segments[idx] = { ...segments[idx], duration: segments[idx].duration + PLAN_SLOT_MIN };
      const last = segments[segments.length - 1];
      if (last.duration > PLAN_SLOT_MIN) {
        segments[segments.length - 1] = { ...last, duration: last.duration - PLAN_SLOT_MIN };
      } else {
        segments.pop();
      }
    } else {
      if (segments[idx].duration <= PLAN_SLOT_MIN) return; // floor: 30 min
      segments[idx] = { ...segments[idx], duration: segments[idx].duration - PLAN_SLOT_MIN };
      segments.push({ id: makeSegId(), text: '', duration: PLAN_SLOT_MIN });
    }

    commitDayPlans({
      ...(lifestyleRef.current.dayPlans || {}),
      [dateKey]: { ...current, segments }
    });
  };

  // --- MEAL TRACKER HELPERS ---
  const getMealDateKey = (offset = mealDateOffset) => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    return d.toISOString().split('T')[0];
  };

  // The single gate between stored shape and what the user sees, so tombstones stay
  // invisible without every caller having to know they exist.
  const getMealsForDate = (dateKey) => (mealLog[dateKey] || []).filter((m) => m?.deleted !== true);

  const getDailyCalories = (dateKey) =>
    getMealsForDate(dateKey).reduce((sum, m) => sum + (Number(m.calories) || 0), 0);

  const getDaySteps = (dateKey) => {
    const fromRef = lifestyleRef.current.stepLog || {};
    const entry = fromRef[dateKey] || stepLog[dateKey];
    const steps = Number(entry?.steps);
    return Number.isFinite(steps) && steps > 0 ? steps : 0;
  };

  const getStepCalories = (dateKey) => stepsToCalories(getDaySteps(dateKey));

  const getNetDailyCalories = (dateKey) =>
    getDailyCalories(dateKey) - getStepCalories(dateKey);

  const saveDaySteps = async (dateKey, raw) => {
    touchLifestyleEdit(15000);
    const trimmed = String(raw ?? '').trim();
    const steps = trimmed === ''
      ? 0
      : Math.max(0, Math.min(200000, Math.round(Number(trimmed) || 0)));
    const prevStepLog = lifestyleRef.current.stepLog || stepLog;
    const base = { ...prevStepLog };
    if (steps <= 0) {
      delete base[dateKey];
    } else {
      base[dateKey] = { steps, updatedAt: new Date().toISOString() };
    }
    const safe = normalizeStepLog(base);
    lifestyleRef.current = { ...lifestyleRef.current, stepLog: safe };
    setStepLog(safe);
    setStepDraft(steps > 0 ? String(steps) : '');
    cacheLifestyleNow();
    const ok = await persistLifestyleToCloud(
      { stepLog: safe },
      ['stepLog'],
      { stepLog: prevStepLog }
    );
    if (steps > 0) {
      const burned = stepsToCalories(steps);
      showNotification(
        ok
          ? `${steps.toLocaleString()} steps · −${burned} kcal · synced`
          : `${steps.toLocaleString()} steps saved locally · cloud sync failed`
      );
    } else {
      showNotification(ok ? 'Steps cleared · synced' : 'Steps cleared locally · cloud sync failed');
    }
    return ok;
  };

  // --- MONEY LOG HELPERS ---
  const getMoneyDateKey = (offset = moneyDateOffset) => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    return d.toISOString().split('T')[0];
  };

  const getMoneyItemsForDate = (dateKey) => {
    const entry = moneyLog[dateKey];
    const items = Array.isArray(entry?.items) ? entry.items : [];
    return [0, 1, 2].map((i) => (typeof items[i] === 'string' ? items[i] : ''));
  };

  const updateMoneyItem = (dateKey, index, text) => {
    if (index < 0 || index > 2) return;
    touchLifestyleEdit(15000);
    const base = lifestyleRef.current.moneyLog || {};
    const current = base[dateKey] || { items: ['', '', ''], updatedAt: null };
    const items = [0, 1, 2].map((i) => (
      i === index ? text : (typeof current.items?.[i] === 'string' ? current.items[i] : '')
    ));
    const nextMoneyLog = {
      ...base,
      [dateKey]: {
        items,
        updatedAt: new Date().toISOString()
      }
    };
    scheduleMoneyLogCloudSave(nextMoneyLog);
  };

  const saveMoneyLogNow = async (dateKey) => {
    touchLifestyleEdit(15000);
    if (moneyLogSaveTimerRef.current) {
      clearTimeout(moneyLogSaveTimerRef.current);
      moneyLogSaveTimerRef.current = null;
    }
    const ok = await persistMoneyLogToCloud(lifestyleRef.current.moneyLog || moneyLog);
    const filled = getMoneyItemsForDate(dateKey).filter((t) => t.trim()).length;
    showNotification(
      ok
        ? `Money log saved · ${filled}/3 moves · synced`
        : `Money log saved locally · cloud sync failed`
    );
    return ok;
  };

  const liveCalorieMemory = () =>
    mergeCalorieMemory(lifestyleRef.current.calorieMemory, calorieMemory);

  const liveMealLog = () =>
    mergeMealLog(lifestyleRef.current.mealLog, mealLog);

  // Saved user calories first (exact / similar), then SG hawker table.
  const estimateMealCalories = (description, mealType, portion) =>
    resolveMealCalories(
      description,
      mealType,
      portion,
      liveCalorieMemory(),
      liveMealLog()
    );

  const applyMealName = (name) => {
    const est = name.trim()
      ? estimateMealCalories(name, mealForm.mealType, mealForm.portion || 'regular')
      : null;
    const reuse = est && (est.source === 'saved' || est.source === 'similar');

    setMealForm({
      ...mealForm,
      name,
      caloriesOverride: reuse && !mealForm.isManual ? est.calories : mealForm.caloriesOverride,
      protein: reuse && !mealForm.isManual && est.protein !== null && est.protein !== undefined ? String(est.protein) : mealForm.protein,
      carbs: reuse && !mealForm.isManual && est.carbs !== null && est.carbs !== undefined ? String(est.carbs) : mealForm.carbs,
      fat: reuse && !mealForm.isManual && est.fat !== null && est.fat !== undefined ? String(est.fat) : mealForm.fat
    });
  };

  const applySavedMeal = (entry) => {
    if (!entry) return;
    setMealForm({
      name: entry.name,
      mealType: entry.mealType || mealForm.mealType || 'lunch',
      portion: entry.portion || 'regular',
      caloriesOverride: clampShownKcal(entry.calories),
      protein: entry.protein !== null && entry.protein !== undefined ? String(entry.protein) : '',
      carbs: entry.carbs !== null && entry.carbs !== undefined ? String(entry.carbs) : '',
      fat: entry.fat !== null && entry.fat !== undefined ? String(entry.fat) : '',
      isManual: true // Changed to true: prevent auto-suggest from overwriting if user edits the name
    });
  };

  const clampShownKcal = (n) => {
    const v = Math.round(Number(n));
    return Number.isFinite(v) ? Math.max(0, Math.min(8000, v)) : 0;
  };

  const commitCalorieMemory = (nextMemory) => {
    const safe = normalizeCalorieMemory(nextMemory);
    lifestyleRef.current = { ...lifestyleRef.current, calorieMemory: safe };
    setCalorieMemory(safe);
    cacheLifestyleNow();
    touchLifestyleEdit();
    return safe;
  };

  const addMeal = async (e) => {
    e?.preventDefault?.();
    const name = mealForm.name.trim();
    if (!name || estimatingCalories) return;

    setEstimatingCalories(true);
    touchLifestyleEdit();
    try {
      const estimate = estimateMealCalories(name, mealForm.mealType, mealForm.portion || 'regular');
      const overridden = Number.isFinite(Number(mealForm.caloriesOverride));
      const calories = overridden
        ? Math.max(0, Math.round(Number(mealForm.caloriesOverride)))
        : estimate.calories;
      const caloriesMin = overridden
        ? Math.max(0, Math.round(calories * 0.97))
        : (Number.isFinite(estimate.kcalMin) ? estimate.kcalMin : null);
      const caloriesMax = overridden
        ? Math.max(0, Math.round(calories * 1.03))
        : (Number.isFinite(estimate.kcalMax) ? estimate.kcalMax : null);
      const proteinInput = String(mealForm.protein ?? '').trim();
      const protein = (proteinInput !== '' && Number.isFinite(Number(proteinInput)) && Number(proteinInput) >= 0)
        ? clampMacro(proteinInput)
        : (estimate?.protein != null ? clampMacro(estimate.protein) : null);

      const carbsInput = String(mealForm.carbs ?? '').trim();
      const carbs = (carbsInput !== '' && Number.isFinite(Number(carbsInput)) && Number(carbsInput) >= 0)
        ? clampMacro(carbsInput)
        : (estimate?.carbs != null ? clampMacro(estimate.carbs) : null);

      const fatInput = String(mealForm.fat ?? '').trim();
      const fat = (fatInput !== '' && Number.isFinite(Number(fatInput)) && Number(fatInput) >= 0)
        ? clampMacro(fatInput)
        : (estimate?.fat != null ? clampMacro(estimate.fat) : null);
      const dateKey = getMealDateKey();
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name,
        calories,
        protein,
        carbs,
        fat,
        mealType: mealForm.mealType,
        createdAt: new Date().toISOString(),
        caloriesSource: overridden ? 'manual' : (estimate.source || 'sg-db'),
        caloriesNote: overridden
          ? `Saved your ${calories} kcal for next time`
          : (estimate.note || ''),
        caloriesMin,
        caloriesMax
      };

      // Compute synchronously from the live ref — reading a value assigned inside a
      // setState updater is unreliable (React may defer it), which previously meant
      // a stale meal log got written to Firebase and the new meal was lost.
      const baseMealLog = lifestyleRef.current.mealLog || {};
      const nextMealLog = {
        ...baseMealLog,
        [dateKey]: [...(baseMealLog[dateKey] || []), entry]
      };
      const baseMemory = lifestyleRef.current.calorieMemory || calorieMemory;
      const nextMemory = { ...baseMemory };
      const memKey = name; // Use exact name as key
      nextMemory[memKey] = {
        ...(nextMemory[memKey] || {}),
        name,
        calories,
        protein,
        carbs,
        fat,
        kcalMin: caloriesMin,
        kcalMax: caloriesMax,
        portion: mealForm.portion || 'regular',
        mealType: mealForm.mealType,
        edited: overridden,
        timesUsed: ((nextMemory[memKey]?.timesUsed) || 0) + 1
      };
      commitCalorieMemory(nextMemory);
      const ok = await persistMealLogToCloud(nextMealLog, { calorieMemory: nextMemory });
      setMealForm({ name: '', mealType: mealForm.mealType, portion: mealForm.portion || 'regular', caloriesOverride: null, protein: '', carbs: '', fat: '', isManual: false });

      const rangeLabel = Number.isFinite(caloriesMin) && Number.isFinite(caloriesMax)
        ? ` (${caloriesMin}–${caloriesMax})`
        : '';
      const macroLabel = protein || carbs || fat
        ? ` · ${protein ? `${protein}g P ` : ''}${carbs ? `${carbs}g C ` : ''}${fat ? `${fat}g F` : ''}`.trim()
        : '';
      showNotification(
        ok
          ? `Logged ${entry.name} · ${entry.calories} kcal${macroLabel}${rangeLabel} · synced`
          : `Logged ${entry.name} · ${entry.calories} kcal${macroLabel}${rangeLabel} · saved locally · cloud sync failed`
      );
    } catch (err) {
      console.error(err);
      showNotification('Could not estimate calories. Try a clearer meal description.');
    } finally {
      setEstimatingCalories(false);
    }
  };

  const updateMealCalories = async (dateKey, mealId, nextCalories, nextName, nextProtein = undefined, nextCarbs = undefined, nextFat = undefined) => {
    const calories = Math.max(0, Math.round(Number(nextCalories) || 0));
    touchLifestyleEdit();
    const baseMealLog = lifestyleRef.current.mealLog || {};
    const dayMeals = baseMealLog[dateKey] || [];
    const target = dayMeals.find((m) => String(m.id) === String(mealId));
    const name = typeof nextName === 'string' ? nextName.trim() : (target?.name || '');
    if (!name) {
      showNotification('Give this meal a name before saving.');
      return;
    }
    const pInput = String(nextProtein ?? '').trim();
    const protein = nextProtein !== undefined
      ? (pInput !== '' && Number.isFinite(Number(pInput)) && Number(pInput) >= 0 ? clampMacro(pInput) : null)
      : (target?.protein ?? null);

    const cInput = String(nextCarbs ?? '').trim();
    const carbs = nextCarbs !== undefined
      ? (cInput !== '' && Number.isFinite(Number(cInput)) && Number(cInput) >= 0 ? clampMacro(cInput) : null)
      : (target?.carbs ?? null);

    const fInput = String(nextFat ?? '').trim();
    const fat = nextFat !== undefined
      ? (fInput !== '' && Number.isFinite(Number(fInput)) && Number(fInput) >= 0 ? clampMacro(fInput) : null)
      : (target?.fat ?? null);

    const nextMealLog = {
      ...baseMealLog,
      [dateKey]: dayMeals.map((meal) => (
        String(meal.id) === String(mealId)
          ? {
              ...meal,
              name,
              calories,
              protein,
              carbs,
              fat,
              caloriesMin: Math.max(0, Math.round(calories * 0.97)),
              caloriesMax: Math.max(0, Math.round(calories * 1.03)),
              caloriesSource: 'manual',
              caloriesNote: `Saved your ${calories} kcal for next time`
            }
          : meal
      ))
    };
    const baseMemory = lifestyleRef.current.calorieMemory || calorieMemory;
    const nextMemory = { ...baseMemory };
    const oldKey = target?.name || name;
    if (oldKey && oldKey !== name) {
      delete nextMemory[oldKey];
    }
    nextMemory[name] = {
      ...(nextMemory[name] || {}),
      name,
      calories,
      protein,
      carbs,
      fat,
      kcalMin: Math.max(0, Math.round(calories * 0.97)),
      kcalMax: Math.max(0, Math.round(calories * 1.03)),
      mealType: target?.mealType || 'lunch',
      portion: 'regular',
      edited: true
    };
    commitCalorieMemory(nextMemory);
    setEditingMeal(null);
    const ok = await persistMealLogToCloud(nextMealLog, { calorieMemory: nextMemory }, baseMemory);
    showNotification(ok
      ? `Updated ${name} · ${calories} kcal · synced`
      : `Updated ${name} · ${calories} kcal locally · cloud sync failed`);
  };

  const saveRememberedCalories = async (key, calories, name, protein = undefined, carbs = undefined, fat = undefined) => {
    const nextName = typeof name === 'string' ? name.trim() : '';
    if (!nextName) {
      showNotification('Give this saved meal a name before saving.');
      return;
    }
    const kcal = Math.max(0, Math.round(Number(calories) || 0));
    if (kcal <= 0) {
      showNotification('Enter a calorie count greater than 0.');
      return;
    }
    const p = protein !== undefined && protein !== '' && Number.isFinite(Number(protein)) && Number(protein) >= 0 ? clampMacro(protein) : null;
    const c = carbs !== undefined && carbs !== '' && Number.isFinite(Number(carbs)) && Number(carbs) >= 0 ? clampMacro(carbs) : null;
    const f = fat !== undefined && fat !== '' && Number.isFinite(Number(fat)) && Number(fat) >= 0 ? clampMacro(fat) : null;

    const baseMemory = lifestyleRef.current.calorieMemory || calorieMemory;
    const nextMemory = { ...baseMemory };
    if (key && key !== nextName) {
      delete nextMemory[key];
    }
    nextMemory[nextName] = {
      ...(nextMemory[nextName] || {}),
      name: nextName,
      calories: kcal,
      protein: p,
      carbs: c,
      fat: f,
      edited: true
    };
    commitCalorieMemory(nextMemory);
    setEditingMemoryKey(null);
    setEditingMemoryCalories('');
    setEditingMemoryName('');
    setEditingMemoryProtein('');
    setEditingMemoryCarbs('');
    setEditingMemoryFat('');
    const ok = await persistLifestyleToCloud(
      { calorieMemory: nextMemory },
      ['calorieMemory'],
      { calorieMemory: baseMemory }
    );
    const macroStr = p !== null || c !== null || f !== null ? ` (${p !== null ? `${p}g P ` : ''}${c !== null ? `${c}g C ` : ''}${f !== null ? `${f}g F` : ''}`.trim() + ')' : '';
    showNotification(ok ? `Saved “${nextName}” · ${kcal} kcal${macroStr}` : 'Saved locally — Firebase sync failed');
  };

  const deleteRememberedCalories = async (key) => {
    // Held before commitCalorieMemory overwrites lifestyleRef, so the write can still
    // tell which cloud key this forgot.
    const prevMemory = lifestyleRef.current.calorieMemory || calorieMemory;
    const nextMemory = forgetRememberedCalories(prevMemory, key);
    commitCalorieMemory(nextMemory);
    if (editingMemoryKey === key) {
      setEditingMemoryKey(null);
      setEditingMemoryCalories('');
      setEditingMemoryName('');
    }
    touchLifestyleEdit();
    const ok = await persistLifestyleToCloud(
      { calorieMemory: nextMemory },
      ['calorieMemory'],
      { calorieMemory: prevMemory }
    );
    showNotification(ok ? 'Forgot that meal · synced' : 'Forgot that meal locally · cloud sync failed');
  };

  const downloadCalorieMemoryJson = () => {
    const payload = calorieMemoryAsFoodsJson(lifestyleRef.current.calorieMemory || calorieMemory);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `life-tracker-calorie-memory-${todayDateStr}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showNotification('Downloaded saved calorie JSON — keep it as a backup or merge into the food list.');
  };

  const overwriteWithGlmCorrectedMeals = async () => {
    setMergingGlmMeals(true);
    try {
      // Pull the server copy first. The button used to merge GLM into whatever this
      // tab happened to hold, which on a cache-less iPhone was the GLM seed itself —
      // a guaranteed no-op that still claimed success.
      let cloudMemory = {};
      let cloudReadOk = false;
      if (user) {
        try {
          const snap = await getDocFromServer(
            doc(db, 'users', user.uid, 'rpg_tracker', 'main')
          );
          cloudMemory = normalizeCalorieMemory(snap.data()?.calorieMemory || {});
          cloudReadOk = true;
        } catch (err) {
          console.warn('GLM merge could not read the server copy:', err);
        }
      }

      const before = normalizeCalorieMemory(
        lifestyleRef.current.calorieMemory || calorieMemory
      );
      const unioned = mergeCalorieMemory(before, cloudMemory);

      // Fill gaps and repair all-zero macros from the GLM table; never override a
      // real user value.
      const glmSeed = buildGlmCorrectedCalorieMemory();
      const next = { ...unioned };
      let added = 0;
      let repaired = 0;
      Object.entries(glmSeed).forEach(([key, seed]) => {
        const cur = next[key];
        if (!cur) {
          next[key] = seed;
          added += 1;
          return;
        }
        const hasNoMacros = (cur.protein == null || cur.protein === 0)
          && (cur.carbs == null || cur.carbs === 0)
          && (cur.fat == null || cur.fat === 0);
        if (hasNoMacros && (seed.protein || seed.carbs || seed.fat)) {
          next[key] = {
            ...cur,
            protein: seed.protein ?? 0,
            carbs: seed.carbs ?? 0,
            fat: seed.fat ?? 0,
            seeded: false,
            edited: true,
            updatedAt: new Date().toISOString()
          };
          repaired += 1;
        }
      });

      const nextMemory = normalizeCalorieMemory(next);
      const recovered = Math.max(
        0,
        Object.keys(nextMemory).length - Object.keys(before).length - added
      );

      commitCalorieMemory(nextMemory);
      cacheLifestyleNow();
      touchLifestyleEdit(15000);
      const ok = await persistLifestyleToCloud({ calorieMemory: nextMemory }, ['calorieMemory']);

      const parts = [];
      if (recovered) parts.push(`${recovered} recovered from cloud`);
      if (added) parts.push(`${added} GLM added`);
      if (repaired) parts.push(`${repaired} macros repaired`);
      const total = Object.keys(nextMemory).length;

      if (!cloudReadOk) {
        showNotification(`⚠️ Could not reach Firebase — merged locally only (${total} saved meals)`);
      } else if (!parts.length) {
        showNotification(`Already up to date — ${total} saved meals, nothing to merge`);
      } else if (ok) {
        showNotification(`✨ ${parts.join(' · ')} → ${total} saved meals`);
      } else {
        showNotification(`${parts.join(' · ')} → ${total} saved meals · Firebase sync failed`);
      }
    } finally {
      setMergingGlmMeals(false);
    }
  };

  // --- AI DAY PLAN SUGGESTIONS (Gemini 2.5 Flash → DeepSeek Flash) ---
  // Fixed 6:30 AM → 11:00 PM grid of 30-min slots. The AI fills only a handful of
  // them on purpose — see PLAN_MAX_FILLED_BLOCKS.
  //
  // How many blocks the AI is allowed to fill out of 33. A packed grid is what kills
  // 松弛感 and gets the plan abandoned by 10 AM, so the ceiling is the feature.
  const PLAN_MAX_FILLED_BLOCKS = 9;

  // The Domino Run goals the day plan has to serve. Anything the AI schedules that
  // does not move one of these is what gets flagged "swap" in the timeline.
  const buildGoalContext = () =>
    dominoCategories
      .map(({ id }) => {
        const row = dominos[id] || {};
        const parts = [];
        if (row.goal?.trim()) parts.push(`goal: ${row.goal.trim()}`);
        if (row.deadline?.trim()) parts.push(`deadline: ${row.deadline.trim()}`);
        if (row.month?.trim()) parts.push(`this month: ${row.month.trim()}`);
        if (row.week?.trim()) parts.push(`this week: ${row.week.trim()}`);
        if (row.day?.trim()) parts.push(`today's domino: ${row.day.trim()}`);
        if (!parts.length) return null;
        return `- ${categoryLabels[id] || id} — ${parts.join(' · ')}`;
      })
      .filter(Boolean)
      .join('\n');

  const buildPlanSlots = () => {
    const slots = [];
    for (let t = PLAN_START_MIN; t < PLAN_END_MIN; t += PLAN_SLOT_MIN) {
      slots.push({
        index: slots.length,
        timeLabel: `${fmtPlanTime(t)}–${fmtPlanTime(t + PLAN_SLOT_MIN)}`
      });
    }
    return slots;
  };

  const suggestDayPlanWithAi = async (dateKey, dayMeta, { refresh = false } = {}) => {
    if (suggestingPlan) return;
    if (!hasGeminiKey() && !deepseekApiKey.trim()) {
      showNotification('⚠️ Add a Gemini API key (preferred) or DeepSeek key in the footer to get AI day plans.');
      return;
    }

    setSuggestingPlan(true);
    try {
      const slots = buildPlanSlots();
      const currentPlan = getDayPlan(dateKey);
      const pendingQuests = todos
        .filter(t => !t.completed)
        .slice(0, 12)
        .map(t => `- [${t.label}] ${t.text}${t.timeOfDay ? ` (${t.timeOfDay})` : ''}${t.dayOfWeek ? ` ${t.dayOfWeek}` : ''}`)
        .join('\n') || 'None listed';
      const habitLines = habits
        .slice(0, 10)
        .map(h => `- ${h.text}${h.frequency === 'weekly' ? ` (weekly x${h.weeklyTarget || 3})` : ' (daily)'}`)
        .join('\n') || 'None listed';

      // Fixed commitments defined in the Schedule tab, mapped to their block index.
      const fixedCommitments = Object.values(scheduleData || {})
        .filter((b) => b && typeof b.time === 'string' && /^\d{1,2}:\d{2}$/.test(b.time))
        .map((b) => {
          const [h, m] = b.time.split(':').map(Number);
          const startMin = h * 60 + m;
          const todo = todos.find((t) => String(t.id) === String(b.todoId));
          return {
            startMin,
            slotIdx: Math.floor((startMin - PLAN_START_MIN) / PLAN_SLOT_MIN),
            label: todo?.text || 'scheduled block'
          };
        })
        .filter((c) => c.slotIdx >= 0 && c.slotIdx < slots.length)
        .sort((a, b) => a.startMin - b.startMin);
      const commitmentLines = fixedCommitments.length
        ? fixedCommitments.map((c) => `- block ${c.slotIdx} (${fmtPlanTime(c.startMin)}): ${c.label}`).join('\n')
        : 'None';

      const blockLines = slots.map((s) => `${s.index}. ${s.timeLabel}`).join('\n');
      const goalContext = buildGoalContext();

      const prompt = `You are a ruthless 80/20 planning coach for an RPG-style life tracker.
Plan ONE day on a fixed 30-minute grid from 6:30 AM to 11:00 PM. The grid has ${slots.length} blocks — you will leave MOST of them empty on purpose.

Day: ${dayMeta.weekday}, ${dayMeta.label} (${dateKey}) — ${dayMeta.relative}
User level: ${level}
Existing day focus (may replace if weak): ${currentPlan.focus || '(none)'}
Existing notes/constraints: ${currentPlan.notes || '(none)'}
${refresh ? 'User asked to REFRESH — give a clearly different plan than last time. Vary which quests get the focus blocks.' : ''}
Weight / physique goal: ${healthProfile.weightGoal}
Body stats: ${healthProfile.heightCm} cm · ${healthProfile.weightKg} kg · ${healthProfile.age} yrs · ${healthProfile.sex} · activity: ${healthProfile.activityLevel}
Calorie target: ${healthProfile.dailyCalorieTarget} kcal/day${healthProfile.weightGoal === 'v_taper' && healthProfile.autoCalorieTarget ? ` (auto: maintenance ≈ ${computeVTaperPlan(healthProfile).tdee} kcal − ${V_TAPER_DEFICIT_KCAL} kcal deficit)` : ''}
Health notes: ${healthProfile.conditions || '(none)'}
Vision (short): ${(visionBoardText || '').slice(0, 280) || '(none)'}

THE GOALS THAT MATTER (from the user's Domino Run — this is the only scoreboard):
${goalContext || '(the user has not defined any Domino Run goals yet)'}

Pending quests:
${pendingQuests}

Habits to protect:
${habitLines}

Fixed schedule commitments (already set by the user — keep each at its block index and plan around them):
${commitmentLines}

30-minute time blocks available:
${blockLines}

HARD RULES:
- Fill AT MOST ${PLAN_MAX_FILLED_BLOCKS} blocks. Every other block index must be left out of "blocks" entirely. Empty space is the point: the user wants 松弛感 (a relaxed, unhurried day) while a few focused actions compound toward the goals above.
- Of those, AT MOST 3 are goal-advancing focus blocks. Pick the quests with the highest leverage on the goals above and drop the rest — less is more.
- Always include: the fixed schedule commitments at their exact block indexes, real meal blocks, and one wind-down block at night.
- Do NOT invent filler like "check email", "review notes", "plan tomorrow", or generic "work". If a block is not clearly worth the user's day, leave it empty.
- Keep each block's "text" under 80 characters.

AI QUEST BLOCKS:
- Any block you derive from the "Pending quests" list above MUST have its text prefixed exactly "${AI_QUEST_PREFIX} " and MUST carry "verdict" and "remark".
- verdict "do" = this quest genuinely moves one of the goals above. remark = which goal it feeds and why, in under 90 characters.
- verdict "swap" = this quest does NOT move any goal (or the user has no goals defined). remark = under 90 characters, naming the better alternative to spend that block on instead, starting with "Better: ".
- Prefer "do" quests. Only schedule an unaligned quest if it is genuinely urgent, and then mark it "swap".
- Blocks that are NOT derived from a pending quest (meals, movement, wind-down, fixed commitments) must have no prefix, no verdict and no remark.

Return ONLY valid JSON (no markdown):
{
  "focus": "the one win that moves a goal today",
  "notes": "short energy / constraint note",
  "blocks": {
    "4": { "text": "${AI_QUEST_PREFIX} draft the 3 landing pages", "verdict": "do", "remark": "Feeds the Financial monthly domino." },
    "11": { "text": "Lunch + 15 min walk" }
  }
}
Use string keys matching the block indexes above (0 to ${slots.length - 1}). Include ONLY the blocks you are filling.`;

      const { text, source } = await callAiWithFallback(prompt, {
        temperature: refresh ? 0.85 : 0.55,
        maxOutputTokens: 3072,
        system: 'Return only compact JSON day plans with focus, notes, and blocks.'
      });

      const parsed = extractJsonObject(text);
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('AI returned invalid day plan JSON');
      }

      // AI plans the full grid fresh → reset to 33 × 30-min blocks, fill by index.
      // A block may come back as a bare string (older prompt shape) or as
      // { text, verdict, remark } for the quest-derived ones.
      const nextSegments = slots.map((s) => {
        const raw =
          parsed.blocks?.[String(s.index)] ??
          parsed.blocks?.[s.index] ??
          '';
        const obj = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
        const text = (obj
          ? (typeof obj.text === 'string' ? obj.text : '')
          : (typeof raw === 'string' ? raw : String(raw || ''))
        ).trim();
        // A verdict on a non-quest block would render a badge the user cannot explain,
        // so the prefix gates it rather than trusting the model to be consistent.
        const isQuest = isAiQuestBlock(text);
        const verdict = isQuest && (obj?.verdict === 'do' || obj?.verdict === 'swap') ? obj.verdict : '';
        const remark = isQuest && typeof obj?.remark === 'string' ? obj.remark.trim() : '';
        return {
          id: makeSegId(),
          text,
          duration: PLAN_SLOT_MIN,
          verdict,
          remark
        };
      });

      const filled = nextSegments.filter((s) => s.text).length;
      if (filled === 0) throw new Error('AI returned empty block plans');
      // The ceiling is a feature, not a suggestion — if the model overfills, keep the
      // earliest blocks so meals/commitments survive and the day still breathes.
      if (filled > PLAN_MAX_FILLED_BLOCKS) {
        let kept = 0;
        nextSegments.forEach((seg) => {
          if (!seg.text) return;
          kept += 1;
          if (kept > PLAN_MAX_FILLED_BLOCKS) {
            seg.text = '';
            seg.verdict = '';
            seg.remark = '';
          }
        });
      }

      const aiUpdatedAt = new Date().toISOString();
      const nextPlan = {
        focus: typeof parsed.focus === 'string' && parsed.focus.trim()
          ? parsed.focus.trim()
          : (currentPlan.focus || `Win the day — ${dayMeta.weekday}`),
        notes: typeof parsed.notes === 'string' ? parsed.notes.trim() : (currentPlan.notes || ''),
        segments: nextSegments,
        aiSource: source,
        aiUpdatedAt
      };

      // nextDayPlans is purely additive over the live ref, so this write can only
      // delete a day that a debounced edit cancelled below had already cleared —
      // which is exactly what that batch's baseline describes.
      const prevAiDayPlans = lifestyleRef.current.dayPlans;
      const nextDayPlans = { ...(lifestyleRef.current.dayPlans || {}), [dateKey]: nextPlan };
      lifestyleRef.current = { ...lifestyleRef.current, dayPlans: nextDayPlans };
      setDayPlans(nextDayPlans);

      // Flush any debounced plan edits, then persist AI plan to Firebase immediately
      if (dayPlansSaveTimerRef.current) {
        clearTimeout(dayPlansSaveTimerRef.current);
        dayPlansSaveTimerRef.current = null;
      }
      const aiPlanBaseline = dayPlansDeleteBaselineRef.current ?? prevAiDayPlans;
      dayPlansDeleteBaselineRef.current = null;
      const ok = await persistLifestyleToCloud(
        { dayPlans: nextDayPlans },
        null,
        { dayPlans: aiPlanBaseline }
      );

      setPlanAiMeta(prev => ({
        ...prev,
        [dateKey]: { source, updatedAt: aiUpdatedAt }
      }));

      const sourceLabel = source === 'gemini' ? 'Gemini 2.5 Flash' : 'DeepSeek Flash';
      showNotification(refresh
        ? `Refreshed ${dayMeta.relative}'s plan (${sourceLabel})${ok ? ' · synced' : ' · cloud sync failed'}`
        : `AI planned ${dayMeta.relative} (${sourceLabel})${ok ? ' · synced' : ' · cloud sync failed'}`);
    } catch (err) {
      console.error('Day plan AI error:', err);
      if (err?.message === 'NO_AI_KEYS') {
        showNotification('⚠️ Add a Gemini API key (preferred) or DeepSeek key in the footer.');
      } else if (String(err?.message || '').includes('401') || String(err?.message || '').includes('NO_GEMINI')) {
        showNotification('⚠️ AI auth failed. Check your Gemini / DeepSeek API keys.');
      } else {
        showNotification('Could not generate day plan. Try again or check API keys.');
      }
    } finally {
      setSuggestingPlan(false);
    }
  };

  const deleteMeal = async (dateKey, mealId) => {
    const baseMealLog = lifestyleRef.current.mealLog || {};
    const day = baseMealLog[dateKey] || [];
    const wasThere = day.some((m) => String(m?.id) === String(mealId));
    const nextMealLog = {
      ...baseMealLog,
      // A tombstone, not a filter. Dropping the meal locally is what let the other
      // device's copy re-upload it and undo the deletion on the next snapshot.
      [dateKey]: [
        ...day.filter(m => String(m.id) !== String(mealId)),
        ...(wasThere ? [{ id: String(mealId), deleted: true, updatedAt: new Date().toISOString() }] : [])
      ]
    };
    const ok = await persistMealLogToCloud(nextMealLog);
    showNotification(ok ? 'Meal deleted · synced' : 'Meal deleted locally · cloud sync failed');
  };

  // Silent save path for inline profile edits (selects, body-stat inputs on blur).
  // Resolves to the persist result (true = synced to Firebase).
  const commitHealthProfile = (overrides = {}) => {
    touchLifestyleEdit(15000);
    const incomingConditions = overrides.conditions !== undefined
      ? overrides.conditions
      : (typeof healthConditionsDraft === 'string' ? healthConditionsDraft : (healthProfile.conditions || ''));
    const next = resolveAutoVTaperTarget({
      ...healthProfile,
      ...overrides,
      conditions: incomingConditions
    });
    setHealthProfile(next);
    setHealthConditionsDraft(next.conditions || '');
    lifestyleRef.current = { ...lifestyleRef.current, healthProfile: next };
    cacheLifestyleNow();
    return persistLifestyleToCloud({ healthProfile: next }, ['healthProfile']);
  };

  const saveHealthProfile = async (overrides = {}) => {
    const ok = await commitHealthProfile({
      ...overrides,
      conditions: typeof healthConditionsDraft === 'string' ? healthConditionsDraft : (healthProfile.conditions || '')
    });
    cacheLifestyleNow();
    showNotification(ok ? 'Health profile saved to Firebase' : 'Health profile updated locally — Firebase sync failed');
  };

  // Finishes editing a numeric profile field: drops the raw draft, then clamps
  // and saves the value (normalizeHealthProfile enforces min/max). Empty or
  // invalid text just reverts to the stored value; unchanged values skip the
  // save so blurring a field never fires a pointless sync + toast.
  const commitProfileNumber = (key, raw) => {
    setProfileNumberDrafts(drafts => {
      if (!(key in drafts)) return drafts;
      const next = { ...drafts };
      delete next[key];
      return next;
    });
    const text = String(raw ?? '').trim();
    if (text === '') return;
    const num = Number(text);
    if (!Number.isFinite(num)) return;
    const shaped = key === 'weightKg' ? Math.round(num * 10) / 10 : Math.round(num);
    if (key === 'calories') {
      if (healthProfile.weightGoal === 'v_taper') return;
      if (shaped === healthProfile.dailyCalorieTarget) return;
      saveHealthProfile({
        dailyCalorieTarget: shaped,
        autoCalorieTarget: false,
        conditions: healthConditionsDraft
      });
      return;
    }
    if (shaped === healthProfile[key]) return;
    saveHealthProfile({ [key]: shaped });
  };

  const getNutritionSuggestions = (conditionsText, caloriesEaten, target, weightGoal, vTaperPlan = null) => {
    const text = (conditionsText || '').toLowerCase();
    const remaining = target - caloriesEaten;
    const tips = [];

    // Weight / calorie guidance
    if (weightGoal === 'lose') {
      tips.push({
        type: remaining < 0 ? 'warn' : 'info',
        title: 'Weight management',
        body: remaining < 0
          ? `You're ${Math.abs(remaining)} kcal over your ${target} target. Prefer protein + vegetables for any remaining meals to avoid extra surplus.`
          : `Stay near a mild deficit. About ${remaining} kcal left today — prioritize lean protein, fiber, and volume (veggies) so you feel full without overshooting.`
      });
    } else if (weightGoal === 'gain') {
      tips.push({
        type: 'info',
        title: 'Weight management',
        body: remaining > 200
          ? `${remaining} kcal remaining. Add nutrient-dense calories (nuts, olive oil, whole grains, dairy if tolerated) rather than only sugary snacks.`
          : `You're close to or over ${target} kcal. If gaining muscle is the goal, pair surplus with protein (~1.6–2.2g/kg) and training.`
      });
    } else if (weightGoal === 'v_taper') {
      const deficitGoal = vTaperPlan?.deficit || V_TAPER_DEFICIT_KCAL;
      const deficitSoFar = Number.isFinite(Number(vTaperPlan?.tdee))
        ? Math.round(vTaperPlan.tdee - caloriesEaten)
        : null;
      const proteinTarget = vTaperPlan?.macros?.protein || 120;
      tips.push({
        type: remaining < -100 ? 'warn' : 'info',
        title: 'V-taper physique goal',
        body: remaining < -100
          ? `You're ${Math.abs(remaining)} kcal over your ${target} cut target${deficitSoFar !== null && deficitSoFar < 0 ? ` — ${Math.abs(deficitSoFar)} kcal past maintenance (≈ ${vTaperPlan.tdee})` : ''}. For a V-taper (wide back/shoulders, tight waist), trim oils, fries, sugary drinks and late snacks to get back to a −${deficitGoal} kcal daily deficit.`
          : `About ${Math.max(0, remaining)} kcal left${deficitSoFar !== null ? ` · deficit so far ${Math.max(0, deficitSoFar)} of ${deficitGoal} kcal (maintenance ≈ ${vTaperPlan?.tdee})` : ''}. Aim for ~${proteinTarget}g protein (0.82g/lb) with lean meats, eggs, and Greek yogurt to preserve back/shoulder muscle while leaning down.`
      });
      tips.push({
        type: 'health',
        title: 'Kinobody Satiety & 4-Meal Pacing',
        body: 'Space your target calories across Breakfast (~25%), Lunch (~30%), Afternoon Snack (~15%), and Dinner (~30%). Prioritize boiled or air-fried potatoes (highest satiety score), lean meats, eggs, and Greek yogurt to hit your ~121g protein target with ease.'
      });
      if (vTaperPlan?.isCrashDiet || target < 1400) {
        tips.push({
          type: 'warn',
          title: 'Kinobody Crash Diet Warning (<1,400 kcal)',
          body: 'Targets below 1,400 kcal cause muscle wasting, metabolic slowdown, and binge rebound. Stick to the 400 kcal deficit sweet spot (~1,800 kcal) for lean V-taper gains.'
        });
      }
    } else {
      tips.push({
        type: remaining < -150 ? 'warn' : 'info',
        title: 'Weight management',
        body: remaining < -150
          ? `About ${Math.abs(remaining)} kcal over target — watch liquid calories and late snacks to stay weight-stable.`
          : `Roughly ${Math.max(0, remaining)} kcal left of ${target}. Steady protein + produce at each meal helps maintain weight without rebound hunger.`
      });
    }

    const rule = (keywords, title, body) => {
      if (keywords.some(k => text.includes(k))) {
        tips.push({ type: 'health', title, body });
      }
    };

    rule(['diabet', 'blood sugar', 'insulin', 'prediabet', 'a1c', 'glucose'],
      'Blood sugar support',
      'Favor lower-GI carbs with protein/fat at every meal. Limit sugary drinks and large refined-carb portions. Aim for consistent meal timing.');

    rule(['hypertens', 'high blood pressure', 'blood pressure', 'bp '],
      'Blood pressure support',
      'Emphasize potassium-rich produce, limit added salt and ultra-processed foods, and watch restaurant sodium. DASH-style plates work well.');

    rule(['cholesterol', 'ldl', 'triglyceride', 'hyperlipid'],
      'Heart / lipid support',
      'Increase soluble fiber (oats, beans, fruit). Limit saturated fats; prefer olive oil, fish, nuts, and legumes over fried foods.');

    rule(['pcos'],
      'PCOS-friendly tips',
      'Prioritize protein and fiber, reduce liquid sugar, and keep refined carbs moderate. Steady meals help energy and appetite regulation.');

    rule(['hypothyroid', 'thyroid', 'hashimoto'],
      'Thyroid-aware tips',
      'Keep calories adequate (severe under-eating can slow you down). Ensure iodine/selenium from food if appropriate, and space high-fiber/soy away from thyroid meds if prescribed.');

    rule(['ibs', 'irritable bowel', 'bloating', 'fodmap'],
      'Gut comfort',
      'If bloating is common, try smaller meals, chew slowly, and note trigger foods (often onion, garlic, beans, sugar alcohols). A low-FODMAP trial may help under guidance.');

    rule(['celiac', 'gluten', 'wheat allergy'],
      'Gluten-free guidance',
      'Choose naturally gluten-free staples (rice, potatoes, corn, quinoa, meat, dairy, produce). Check sauces and processed snacks for hidden gluten.');

    rule(['lactose', 'dairy intoler', 'milk intoler'],
      'Dairy alternatives',
      'Use lactose-free milk/yogurt or fortified plant milks. Hard cheeses and yogurt are often better tolerated if mild lactose intolerance.');

    rule(['gerd', 'acid reflux', 'heartburn', 'reflux'],
      'Reflux-friendly choices',
      'Avoid large late meals, spicy/fried food, chocolate, mint, and excess caffeine/alcohol. Elevate dinner earlier and keep portions moderate.');

    rule(['gout', 'uric acid'],
      'Gout-aware tips',
      'Limit alcohol (especially beer), sugary drinks, and very high-purine organ meats. Stay hydrated; moderate portions of red meat and seafood.');

    rule(['kidney', 'renal', 'ckd'],
      'Kidney-aware note',
      'Protein, potassium, phosphorus, and sodium needs are highly individual with kidney disease — prefer simple home-cooked meals and follow your clinician’s limits.');

    rule(['anemia', 'iron deficien', 'low iron'],
      'Iron support',
      'Include iron-rich foods (lean red meat, legumes, fortified grains) with vitamin C. Avoid tea/coffee with iron-heavy meals if absorption is a concern.');

    rule(['migraine'],
      'Migraine-aware tips',
      'Keep meals regular (skipping meals can trigger headaches). Note personal triggers (aged cheese, alcohol, aspartame, excess caffeine swings).');

    rule(['pregnant', 'pregnancy'],
      'Pregnancy nutrition note',
      'Avoid under-eating. Focus on protein, iron, folate-rich foods, and safe food handling. Discuss supplements and calorie needs with your clinician.');

    if (!text.trim()) {
      tips.push({
        type: 'info',
        title: 'Personalize your plan',
        body: 'Save your health conditions in the box above so suggestions can better match your needs (e.g. diabetes, PCOS, high blood pressure).'
      });
    } else if (tips.filter(t => t.type === 'health').length === 0) {
      tips.push({
        type: 'info',
        title: 'General health plate',
        body: 'Build most meals as: ½ vegetables, ¼ protein, ¼ whole carbs + a little healthy fat. Stay hydrated and keep ultra-processed snacks as exceptions.'
      });
    }

    tips.push({
      type: 'disclaimer',
      title: 'Not medical advice',
      body: 'These tips are general wellness heuristics based on your notes — not a diagnosis or treatment plan. Follow your doctor or dietitian for personal medical guidance.'
    });

    return tips;
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-800 font-sans selection:bg-indigo-100 flex flex-col items-center py-8 sm:py-12 px-4 sm:px-6 md:px-8 overflow-x-hidden">
      
      {/* Toast Notification */}
      <div className={`fixed top-6 transition-all duration-500 transform z-50 ${notificationObj.visible ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0 pointer-events-none'}`}>
        {notificationObj.visible && (
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-lg backdrop-blur-md border ${notificationObj.isLevelUp ? 'bg-indigo-600/90 border-indigo-500 text-white' : 'bg-white/95 border-zinc-200 text-zinc-800'}`}>
            {notificationObj.isLevelUp ? <Trophy className="w-5 h-5 text-yellow-300" /> : <Sparkles className="w-5 h-5 text-indigo-500" />}
            <span className="font-medium tracking-wide">{notificationObj.message}</span>
            {notificationObj.onUndo && (
              <button 
                onClick={() => { notificationObj.onUndo(); setNotificationObj(prev => ({ ...prev, visible: false })); }}
                className="ml-4 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 border border-zinc-300 text-zinc-700 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-colors shadow-sm"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Undo
              </button>
            )}
          </div>
        )}
      </div>

      <div className="w-full max-w-4xl">
        
        {/* NEW: DATABASE ERROR BANNER */}
        {dbError && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-6 rounded-2xl shadow-sm flex items-start gap-3 animate-in slide-in-from-top-2">
            <CloudOff className="w-6 h-6 flex-shrink-0 text-red-500 mt-0.5" />
            <div>
              <h3 className="font-bold text-red-800 text-lg">Cloud Save Failed!</h3>
              <p className="text-sm mt-1"><strong>Firebase error:</strong> {dbError}</p>
              <p className="text-xs mt-2 font-medium text-red-600/80 uppercase tracking-wider">
                ⚠️ Your data is currently blocked from saving. If you close this tab, your data will reset.
              </p>
            </div>
          </div>
        )}

        {user && !dismissExportRemind && (!lastExportAt || (Date.now() - new Date(lastExportAt).getTime() > EXPORT_REMIND_MS)) && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 p-4 mb-6 rounded-2xl shadow-sm flex items-start gap-3">
            <Download className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm">Download a backup this week</h3>
              <p className="text-xs mt-1 text-amber-800/90">
                Firebase keeps the live copy only. Export a JSON file to your computer so a bad save cannot wipe everything.
                {lastExportAt ? ` Last export: ${new Date(lastExportAt).toLocaleDateString()}.` : ' No export recorded on this device yet.'}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleExport}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold"
                >
                  Export now
                </button>
                <button
                  type="button"
                  onClick={() => setDismissExportRemind(true)}
                  className="px-3 py-1.5 bg-white border border-amber-200 hover:bg-amber-100 rounded-lg text-xs font-semibold"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RPG Dashboard Header */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-zinc-100 mb-6">
          <div className="flex justify-end mb-2">
            <button onClick={handleConsultQuestMaster} disabled={isConsulting} className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
              {isConsulting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {isConsulting ? 'Consulting...' : 'Consult Quest Master'}
            </button>
          </div>

          {questMasterAdvice && (
            <div className="mb-6 bg-indigo-600 text-white p-4 rounded-2xl rounded-tr-sm shadow-md animate-in slide-in-from-top-2 relative">
              <button onClick={() => setQuestMasterAdvice(null)} className="absolute top-2 right-2 text-indigo-200 hover:text-white"><X className="w-4 h-4" /></button>
              <div className="flex gap-3">
                <Wand2 className="w-5 h-5 text-indigo-200 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium leading-relaxed pr-4">{questMasterAdvice}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 group/header">
            {isEditingHeader ? (
              <div className="flex-1 flex flex-col gap-3 animate-in fade-in zoom-in duration-200 w-full">
                <input type="text" value={headerEditForm.title} onChange={(e) => setHeaderEditForm({...headerEditForm, title: e.target.value})} className="w-full text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" placeholder="Main Quote / Title" autoFocus />
                
                {/* 1. TEXTAREA FOR UNIVERSAL LAWS */}
                <textarea 
                   value={headerEditForm.subtitle} 
                   onChange={(e) => setHeaderEditForm({...headerEditForm, subtitle: e.target.value})} 
                   className="w-full text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-y min-h-[100px]" 
                   placeholder="Universal Laws / Reminders (Use 'Enter' for new bullet points)"
                />
                
                <div className="flex gap-1.5 justify-end">
                  <button onClick={cancelHeaderEdit} className="px-3 py-1.5 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors border border-zinc-200 flex items-center gap-1 text-sm font-bold"><X className="w-4 h-4" /> Cancel</button>
                  <button onClick={saveHeaderEdit} className="px-3 py-1.5 text-white bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors border border-indigo-600 flex items-center gap-1 text-sm font-bold"><Save className="w-4 h-4" /> Save</button>
                </div>
              </div>
            ) : (
              <div onClick={startHeaderEdit} className="flex-1 cursor-pointer relative pr-8 group-hover/header:translate-x-1 transition-transform">
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{headerText.title}</h1>
                
                {/* LIST DISPLAY FOR UNIVERSAL LAWS */}
                {headerText.subtitle ? (
                  <ol className="text-zinc-500 text-sm mt-2 pl-5 list-decimal space-y-1">
                    {headerText.subtitle.split('\n').map((line, idx) => line.trim() ? <li key={idx}>{line.trim()}</li> : null)}
                  </ol>
                ) : (
                  <p className="text-zinc-500 text-sm mt-1">Every step builds momentum.</p>
                )}

                <button className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-zinc-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg opacity-100 xl:opacity-0 xl:group-hover/header:opacity-100 transition-all" title="Edit Quote"><Pencil className="w-4 h-4" /></button>
              </div>
            )}
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 border-4 border-indigo-100 text-indigo-600 font-bold text-xl shadow-inner relative group cursor-default flex-shrink-0 mt-1 sm:mt-0">
              Lvl {level}
              <div className="absolute -top-10 bg-zinc-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">Total XP: {level * xpNeeded + xp}</div>
            </div>
          </div>
          
          {/* XP Bar */}
          <div className="relative">
            <div className="flex justify-between text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
              <span>XP: {xp}</span><span>Next Level: {xpNeeded}</span>
            </div>
            <div className="h-3 w-full bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full transition-all duration-700 ease-out relative" style={{ width: `${xpPercentage}%` }}>
                <div className="absolute inset-0 bg-white/20 w-full h-full animate-pulse rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* VISION BOARD SECTION */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-zinc-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 bg-purple-100 rounded-lg text-purple-600"><Eye className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-zinc-800 leading-tight">Vision Board</h2>
              <p className="text-[11px] sm:text-xs text-zinc-500">Write out your ultimate future vision. Dream big.</p>
            </div>
          </div>
          <textarea
            ref={visionRef}
            value={visionBoardText}
            onChange={(e) => setVisionBoardText(e.target.value)}
            onKeyDown={handleVisionKeyDown}
            placeholder={'My ultimate vision is...\n\nTip: start a line with "1. " and press Enter — the next number is added for you.'}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl p-4 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-y min-h-[450px] mb-3"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={applyVisionNumbering}
              title="Number the selected lines (or un-number them)"
              className="px-3 py-2 bg-white hover:bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-xl text-xs font-semibold transition-colors shadow-sm flex items-center gap-1.5"
            >
              <ListOrdered className="w-4 h-4" /> Number lines
            </button>
            <button
              onClick={() => { updateCloud({ visionBoardText }); showNotification('Vision saved!'); }}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save Vision
            </button>
          </div>
        </div>

        {/* DOMINO RUN SECTION */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-zinc-100 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="p-1.5 bg-orange-100 rounded-lg text-orange-600"><Target className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-zinc-800 leading-tight">The Domino Run</h2>
              <p className="text-[11px] sm:text-xs text-zinc-500">State each goal clearly — AI sets a realistic deadline, then the monthly, weekly and daily domino.</p>
            </div>
          </div>

          {dominoCategories.map((catObj, catIdx) => (
            <div key={catObj.id} className={`w-full relative z-10 ${catIdx !== dominoCategories.length - 1 ? 'mb-8 pb-4 border-b border-zinc-100/80' : 'mb-4'}`}>
              <div className="flex items-center justify-between w-full mb-2 px-1 sm:px-2 group/cat relative z-20 pointer-events-auto">
                <div className="flex items-center gap-2 sm:gap-2">
                  <button onClick={() => toggleCategoryCollapse(catObj.id)} className="p-3 sm:p-0.5 -ml-3 sm:ml-0 hover:bg-zinc-100 rounded-xl sm:rounded text-zinc-500 sm:text-zinc-400 hover:text-zinc-700 transition-colors">
                    {collapsedCategories[catObj.id] ? <ChevronRight className="w-6 h-6 sm:w-4 sm:h-4" /> : <ChevronDown className="w-6 h-6 sm:w-4 sm:h-4" />}
                  </button>
                  {catObj.icon}
                  {editingCategory === catObj.id ? (
                    <input autoFocus value={categoryEditForm} onChange={(e) => setCategoryEditForm(e.target.value)} onBlur={() => saveCategoryEdit(catObj.id)} onKeyDown={(e) => e.key === 'Enter' && saveCategoryEdit(catObj.id)} className="font-bold text-base sm:text-sm text-zinc-700 bg-white border-b border-indigo-300 focus:outline-none focus:border-indigo-500 px-1 py-0.5 w-full max-w-[150px] sm:max-w-xs" />
                  ) : (
                    <>
                      <span className="font-bold text-base sm:text-sm text-zinc-700 cursor-pointer py-2 sm:py-0" onClick={() => startCategoryEdit(catObj.id, categoryLabels[catObj.id])}>{categoryLabels[catObj.id]}</span>
                      <button onClick={() => startCategoryEdit(catObj.id, categoryLabels[catObj.id])} className="opacity-100 xl:opacity-0 xl:group-hover/cat:opacity-100 p-2 sm:p-0 text-zinc-400 hover:text-indigo-500 transition-opacity"><Pencil className="w-4 h-4 sm:w-3 sm:h-3" /></button>
                    </>
                  )}
                </div>
                {!collapsedCategories[catObj.id] && (
                  <button onClick={() => handleAIFillDominos(catObj.id)} disabled={generatingDominos === catObj.id} className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50">
                    {generatingDominos === catObj.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{generatingDominos === catObj.id ? 'Judging deadline...' : 'AI: set deadline & run'}</span><span className="sm:hidden">{generatingDominos === catObj.id ? '...' : 'AI Deadline'}</span>
                  </button>
                )}
              </div>

              {/* The goal is stated by the user; only the deadline is the AI's call. */}
              {!collapsedCategories[catObj.id] && (
                <div className="mx-1 sm:mx-2 mb-2 bg-zinc-50 border border-zinc-200 rounded-2xl p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                      Define this goal clearly
                    </label>
                    {dominos[catObj.id]?.deadline ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                        <Target className="w-3 h-3" /> {dominos[catObj.id].deadline}
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-400">No deadline yet — hit AI</span>
                    )}
                  </div>
                  <textarea
                    value={goalDraftFor(catObj.id)}
                    onChange={(e) => setGoalDraft(catObj.id, e.target.value)}
                    onBlur={() => commitDominoGoal(catObj.id)}
                    rows={2}
                    placeholder="e.g. Grow the SEO site to SGD 3,000/month recurring so I can quit the day job"
                    className="w-full bg-white border border-zinc-200 rounded-xl p-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500/25 resize-none"
                    disabled={generatingDominos === catObj.id}
                  />
                  <p className="text-[10px] text-zinc-400 mt-1.5">
                    Be specific and measurable. AI judges how long it really takes, then fills the run below.
                  </p>
                </div>
              )}

              {!collapsedCategories[catObj.id] && (
                <div className="relative w-full z-10">
                  <div className="w-full pb-6 pt-4">
                    <div className="flex flex-col items-center justify-center w-full px-2 sm:px-8 mx-auto">
                      {dominoConfig.map((step, idx) => {
                        const isFilled = !!dominos[catObj.id]?.[step.key];
                        const isSelected = selectedDomino?.cat === catObj.id && selectedDomino?.key === step.key;
                        const isCompleted = completedDominos[catObj.id]?.[step.key];
                        const mobileSize = Math.max(24, Math.floor(step.size * 0.7));
                        const innerContent = (
                          <>
                            {isFilled && !isSelected && <CheckCircle2 className={`w-1/3 h-1/3 ${isCompleted ? 'text-emerald-500 opacity-100' : 'text-indigo-400 opacity-60'}`} />}
                            {isSelected && <Pencil className="w-1/3 h-1/3 text-orange-500" />}
                            {!isFilled && !isSelected && <div className="w-1.5 h-1.5 rounded-full bg-zinc-200 group-hover:bg-indigo-300 transition-colors" />}
                          </>
                        );
                        const circleClasses = `rounded-full border-[3px] flex items-center justify-center transition-all duration-300 ${isSelected ? 'border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.4)] bg-orange-50 scale-110' : isCompleted ? 'border-emerald-500 bg-emerald-50 shadow-[0_0_10px_rgba(16,185,129,0.3)]' : isFilled ? 'border-indigo-500 bg-indigo-50 group-hover:bg-indigo-100' : 'border-zinc-200 bg-zinc-50 group-hover:border-indigo-300'}`;
                        
                        // Intelligent Tooltip Anchoring for iPads/Tablets so they never cut off at the screen edges
                        const alignClass = idx === 0 ? "left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0" : idx === dominoConfig.length - 1 ? "left-1/2 -translate-x-1/2 md:left-auto md:right-0 md:translate-x-0" : "left-1/2 -translate-x-1/2";
                        const caretClass = idx === 0 ? "left-1/2 -translate-x-1/2 md:left-6 md:translate-x-0" : idx === dominoConfig.length - 1 ? "left-1/2 -translate-x-1/2 md:left-auto md:right-6 md:translate-x-0" : "left-1/2 -translate-x-1/2";

                        return (
                          <React.Fragment key={step.key}>
                            <div onClick={() => handleSelectDomino(catObj.id, step.key)} className="flex-shrink-0 flex flex-col items-center gap-1.5 sm:gap-2 group cursor-pointer relative">
                              {isFilled && !isSelected && (
                                <div className={`absolute bottom-full mb-3 ${alignClass} bg-black text-white p-4 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 w-[85vw] sm:w-80 max-w-[350px] z-[100] pointer-events-none group-hover:pointer-events-auto shadow-2xl border border-zinc-700 flex flex-col gap-2`}>
                                  <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider text-left flex-shrink-0">{dominoLabels[catObj.id]?.[step.key]} Goal</span>
                                  <div className="overflow-y-auto max-h-[50vh] md:max-h-[350px]" style={{ scrollbarWidth: 'thin', scrollbarColor: '#52525b transparent' }}>
                                    <span className="text-sm font-medium leading-relaxed break-words text-left whitespace-pre-wrap block">{dominos[catObj.id]?.[step.key]}</span>
                                  </div>
                                  <div className={`absolute top-full ${caretClass} border-[6px] border-transparent border-t-black`} />
                                </div>
                              )}
                              <div className={`sm:hidden ${circleClasses}`} style={{ width: `${mobileSize}px`, height: `${mobileSize}px` }}>{innerContent}</div>
                              <div className={`hidden sm:flex ${circleClasses}`} style={{ width: `${step.size}px`, height: `${step.size}px` }}>{innerContent}</div>
                              <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wide whitespace-nowrap transition-colors ${isSelected ? 'text-orange-600' : isCompleted ? 'text-emerald-600' : isFilled ? 'text-indigo-600' : 'text-zinc-400 group-hover:text-zinc-600'}`}>{dominoLabels[catObj.id]?.[step.key]}</span>
                            </div>
                            {idx < dominoConfig.length - 1 && (
                              <div className={`w-1 h-4 sm:h-6 my-1 rounded-full transition-all duration-300 ${completedDominos[catObj.id]?.[step.key] && completedDominos[catObj.id]?.[dominoConfig[idx+1].key] ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]' : dominos[catObj.id]?.[step.key] && dominos[catObj.id]?.[dominoConfig[idx+1].key] ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.3)]' : 'bg-zinc-100'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {selectedDomino?.cat === catObj.id && (
                <div className="mt-4 p-4 sm:p-5 bg-orange-50/80 border border-orange-100 rounded-2xl animate-in slide-in-from-top-2 relative z-20 pointer-events-auto mx-1 sm:mx-0">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3 pr-8 sm:pr-0">
                    <div className="flex flex-col gap-1 w-full sm:max-w-sm border-b border-orange-200 pb-2">
                      <span className="text-[10px] text-orange-600/70 font-semibold uppercase tracking-wider">Domino Label</span>
                      <input value={editingDominoLabel} onChange={(e) => setEditingDominoLabel(e.target.value)} className="text-sm sm:text-base font-bold text-orange-800 bg-transparent focus:outline-none focus:ring-0 p-0 placeholder-orange-300" placeholder="Custom Label (e.g., Q3 Goal)" />
                    </div>
                    <button onClick={() => setSelectedDomino(null)} className="absolute top-4 right-4 sm:static flex-shrink-0 text-orange-400 hover:text-orange-600 transition-colors bg-white rounded-md p-1 border border-orange-200 shadow-sm"><X className="w-4 h-4 sm:w-3.5 sm:h-3.5" /></button>
                  </div>
                  <label className="text-[10px] text-orange-600/70 font-semibold uppercase tracking-wider block mb-1">What's your ONE THING for this goal?</label>
                  <textarea autoFocus value={editingDominoText} onChange={(e) => setEditingDominoText(e.target.value)} placeholder="Type your goal here to stand the domino up..." className="w-full bg-white border border-orange-200 rounded-xl p-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-orange-500/30 resize-none shadow-inner" rows={2} />
                  <div className="flex flex-row justify-between items-center mt-3 gap-2">
                     <button onClick={toggleDominoCompletion} className={`flex-1 sm:flex-none text-xs font-bold px-3 py-2.5 sm:py-2 rounded-lg transition-colors shadow-sm flex items-center justify-center gap-1.5 ${completedDominos[selectedDomino.cat][selectedDomino.key] ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}><CheckCircle2 className="w-4 h-4" /> {completedDominos[selectedDomino.cat][selectedDomino.key] ? 'Done' : 'Mark Done'}</button>
                     <button onClick={saveDominoAndClose} className="flex-1 sm:flex-none text-xs font-bold px-4 py-2.5 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors shadow-sm flex items-center justify-center gap-1.5"><Save className="w-4 h-4" /> Save</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Tab Navigation — drag/swipe to reorder; order saved to Firebase */}
        {(() => {
          const tabMeta = {
            todos: { label: 'Quests', icon: <ListTodo className="w-4 h-4" /> },
            habits: { label: 'Training', icon: <Repeat className="w-4 h-4" /> },
            calendar: { label: 'Momentum', icon: <CalendarDays className="w-4 h-4" /> },
            schedule: { label: 'Schedule', icon: <Clock className="w-4 h-4" /> },
            sprint: { label: 'Calendar', icon: <CalendarRange className="w-4 h-4" /> },
            plan: { label: 'Plan', icon: <MapIcon className="w-4 h-4" /> },
            nutrition: { label: 'Meals', icon: <UtensilsCrossed className="w-4 h-4" /> },
            money: { label: 'Money', icon: <DollarSign className="w-4 h-4" /> },
            journal: { label: 'Journal', icon: <BookOpen className="w-4 h-4" /> },
            integrated: { label: 'Integrated', icon: <Library className="w-4 h-4" /> },
            goals: { label: 'Goals', icon: <Target className="w-4 h-4" /> }
          };

          const orderedTabs = normalizeTabOrder(tabOrder);

          const handleTabDragStart = (event) => {
            setDraggingTabId(event.active.id);
          };

          const handleTabDragEnd = (event) => {
            const { active, over } = event;
            setDraggingTabId(null);
            if (!over || active.id === over.id) return;

            setTabOrder((prev) => {
              const current = normalizeTabOrder(prev);
              const oldIndex = current.indexOf(active.id);
              const newIndex = current.indexOf(over.id);
              if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return current;
              const next = arrayMove(current, oldIndex, newIndex);
              updateCloud({ tabOrder: next });
              return next;
            });
          };

          const handleTabDragCancel = () => setDraggingTabId(null);

          const activeMeta = draggingTabId ? tabMeta[draggingTabId] : null;

          return (
            <div className="mb-6">
              <DndContext
                sensors={tabSensors}
                collisionDetection={closestCenter}
                onDragStart={handleTabDragStart}
                onDragEnd={handleTabDragEnd}
                onDragCancel={handleTabDragCancel}
              >
                <SortableContext items={orderedTabs} strategy={horizontalListSortingStrategy}>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2 bg-zinc-200/50 p-1.5 rounded-2xl">
                    {orderedTabs.map((tabId) => {
                      const meta = tabMeta[tabId];
                      if (!meta) return null;
                      return (
                        <SortableTabButton
                          key={tabId}
                          id={tabId}
                          label={meta.label}
                          icon={meta.icon}
                          isActive={activeTab === tabId}
                          onSelect={setActiveTab}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
                <DragOverlay dropAnimation={null}>
                  {activeMeta ? (
                    <div className="flex flex-col items-center justify-center gap-1 py-2.5 sm:py-3 px-2 rounded-xl text-[10px] sm:text-xs font-semibold bg-white text-zinc-900 shadow-xl ring-2 ring-indigo-300 cursor-grabbing min-w-[4.5rem]">
                      {activeMeta.icon}
                      <span className="leading-tight">{activeMeta.label}</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
              <p className="text-[10px] text-zinc-400 text-center mt-1.5">
                Drag or swipe tabs to reorder · order syncs to Firebase
              </p>
            </div>
          );
        })()}

        {/* Content Area */}
        <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-zinc-100 min-h-[400px] flex flex-col">
          {!['calendar', 'journal', 'schedule', 'sprint', 'plan', 'nutrition', 'money', 'integrated', 'goals'].includes(activeTab) && (
            <form onSubmit={handleAddItem} className="mb-8">
              <div className="relative flex items-center">
                <input type="text" placeholder={activeTab === 'todos' ? "Add a new quest..." : "Establish a new habit..."} value={newItemText} onChange={(e) => setNewItemText(e.target.value)} className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-4 pl-5 pr-14 text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
                <button type="submit" disabled={!newItemText.trim()} className="absolute right-2 p-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 disabled:opacity-50 disabled:hover:bg-indigo-500 transition-colors"><Plus className="w-5 h-5" /></button>
              </div>
              
              {/* Added "Today" Label Button here */}
              {activeTab === 'todos' && (
                <div className="flex flex-wrap items-center gap-2 mt-3 pl-2">
                  <button type="button" onClick={() => setNewTaskLabel('today')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors border ${newTaskLabel === 'today' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>Today</button>
                  <button type="button" onClick={() => setNewTaskLabel('should')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors border ${newTaskLabel === 'should' ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>Should Do</button>
                  <button type="button" onClick={() => setNewTaskLabel('could')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors border ${newTaskLabel === 'could' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>Could Do</button>
                  <div className="h-4 w-px bg-zinc-300 mx-1 hidden sm:block"></div>
                  <input type="text" placeholder="Custom tag (e.g. work)" value={customTag} onChange={(e) => setCustomTag(e.target.value.replace(/^#/, ''))} className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg py-1.5 px-3 text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 transition-all w-28 sm:w-40" />
                  <select value={newTaskDayOfWeek} onChange={(e) => setNewTaskDayOfWeek(e.target.value)} className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg py-1.5 px-2 text-zinc-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 transition-all cursor-pointer"><option value="">Any Day</option><option value="Mon">Mon</option><option value="Tue">Tue</option><option value="Wed">Wed</option><option value="Thu">Thu</option><option value="Fri">Fri</option><option value="Sat">Sat</option><option value="Sun">Sun</option></select>
                  <select value={newTaskTimeOfDay} onChange={(e) => setNewTaskTimeOfDay(e.target.value)} className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg py-1.5 px-2 text-zinc-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 transition-all cursor-pointer"><option value="">Any Time</option><option value="morning">Morning</option><option value="afternoon">Afternoon</option><option value="evening">Evening</option><option value="night">Night</option></select>
                  <div className="relative flex items-center w-full sm:w-auto mt-2 sm:mt-0"><Clock className="w-3.5 h-3.5 absolute left-2.5 text-zinc-400 pointer-events-none" /><input type="number" placeholder="Mins limit (opt)" value={newTaskAllocatedTime} onChange={(e) => setNewTaskAllocatedTime(e.target.value)} min="1" className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg py-1.5 pl-7 pr-2 text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 transition-all w-32" /></div>
                </div>
              )}
              {activeTab === 'todos' && (
                <CloudSyncLine
                  className="mt-3 px-1"
                  status={cloudSaveState.status}
                  detail={cloudSaveState.message}
                  domain="quests"
                  activeDomain={cloudSaveState.domain}
                  saving="Syncing quests to Firebase…"
                  error="Quests saved on this device — Firebase sync failed"
                  idle="Quests synced to Firebase"
                />
              )}
              {activeTab === 'habits' && (
  <div className="flex flex-wrap items-center gap-3 mt-3 pl-2">
    <span className="text-xs font-semibold text-zinc-500">Frequency:</span>
    <label className="flex items-center gap-1 text-xs">
      <input
        type="radio"
        name="habitFrequency"
        value="daily"
        checked={habitFrequency === 'daily'}
        onChange={() => setHabitFrequency('daily')}
      />
      Daily
    </label>
    <label className="flex items-center gap-1 text-xs">
      <input
        type="radio"
        name="habitFrequency"
        value="weekly"
        checked={habitFrequency === 'weekly'}
        onChange={() => setHabitFrequency('weekly')}
      />
      Weekly
    </label>
    {habitFrequency === 'weekly' && (
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">Target:</span>
        <select
          value={habitWeeklyTarget}
          onChange={(e) => setHabitWeeklyTarget(Number(e.target.value))}
          className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2"
        >
          {[1,2,3,4,5,6,7].map(n => (
            <option key={n} value={n}>{n} {n === 1 ? 'time' : 'times'}/week</option>
          ))}
        </select>
      </div>
    )}
  </div>
)}
            </form>
          )}

          <div className="flex-1 overflow-y-auto pr-0 sm:pr-2 space-y-3 pb-12" 
               onDragOver={(e) => { e.preventDefault(); }} 
               onDrop={(e) => {
                 // Used specifically for HABIT drag and drop (Todos now use group logic below)
                 if (activeTab === 'habits' && draggedItemIdx !== null) { 
                   e.preventDefault();
                   const targetIndex = habits.length - 1; 
                   if (draggedItemIdx === targetIndex) { setDraggedItemIdx(null); setDragOverItemIdx(null); return; } 
                   const newHabits = [...currentHabits()]; 
                   const draggedItem = newHabits[draggedItemIdx]; 
                   newHabits.splice(draggedItemIdx, 1); 
                   newHabits.push(draggedItem); 
                   persistHabitsToCloud(newHabits); 
                   setDraggedItemIdx(null); 
                   setDragOverItemIdx(null);
                 } 
               }}>
            
            {/* --- MASTER GROUPS FOR QUESTS (TODAY, SHOULD DO, COULD DO) --- */}
            {activeTab === 'todos' && (
              <div className="space-y-6">
                {[
                  { id: 'today', title: "Today's Priority", bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', emptyMsg: "No priorities set for today." },
                  { id: 'week', title: "This Week's Priority", bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', emptyMsg: "No priorities set for this week. Promote a 'Should Do' task here." },
                  { id: 'should', title: "Should Do", bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', emptyMsg: "No 'Should Do' tasks." },
                  { id: 'could', title: "Could Do", bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', emptyMsg: "No 'Could Do' tasks." }
                ].map(group => {
                  const groupTodos = todos.filter(t => t.label === group.id);
                  const isCollapsed = collapsedTodos[group.id];

                  return (
                    <div
                      key={group.id}
                      data-group-id={group.id}
                      className={`w-full rounded-2xl transition-all ${
                        dragActive && dragOverGroupId === group.id
                          ? dragRejected
                            ? 'ring-2 ring-red-300 ring-offset-4 bg-red-50/40'
                            : 'ring-2 ring-indigo-300 ring-offset-4'
                          : ''
                      }`}
                    >
                      <div 
                        onClick={() => { const next = { ...collapsedTodos, [group.id]: !collapsedTodos[group.id] }; setCollapsedTodos(next); updateCloud({ collapsedTodos: next }); }}
                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer hover:opacity-90 transition-opacity mb-3 border ${group.bg} ${group.border}`}
                      >
                        <div className="flex items-center gap-2">
                           {isCollapsed ? <ChevronRight className={`w-5 h-5 ${group.text}`} /> : <ChevronDown className={`w-5 h-5 ${group.text}`} />}
                           <h3 className={`font-bold ${group.text}`}>{group.title} <span className="opacity-70 text-xs ml-1">({groupTodos.length})</span></h3>
                        </div>
                      </div>

                      {!isCollapsed && (
                        <div className="space-y-3">
                          {groupTodos.length === 0 ? (
                            <div className="text-zinc-400 text-sm p-4 text-center italic border border-dashed border-zinc-200 rounded-xl">{group.emptyMsg}</div>
                          ) : (
                            groupTodos.map((todo, index) => {
                              const isEditing = editingTodoId !== null && editingTodoId !== undefined && String(editingTodoId) === String(todo.id);
                              const isBreakingDown = breakingDownId === todo.id;
                              // Gate on editForm.label or saved label so toggling away and back stays consistent.
                              const canBeWeekly = ['should', 'week'].includes(editForm.label || todo.label);

                              return (
                                <div
                                   key={todo.id}
                                   data-todo-id={todo.id}
                                   onMouseDown={isEditing ? undefined : (e) => startDrag(e, todo.id)}
                                   onTouchStart={isEditing ? undefined : (e) => startDrag(e, todo.id)}
                                   className={`group flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${
                                     dragItemId === todo.id && dragActive ? 'opacity-40 scale-[0.98] border-indigo-300 border-dashed bg-indigo-50/50' :
                                     dragOverItemId === todo.id && dragActive ? `bg-white shadow-md border-indigo-500 ${dragOverPosition === 'above' ? 'border-t-4' : 'border-b-4'}` :
                                     isEditing ? 'bg-white border-indigo-300 shadow-md ring-4 ring-indigo-50' :
                                     todo.completed ? 'bg-zinc-50 border-zinc-100 opacity-60' :
                                     'bg-white border-zinc-200 hover:border-indigo-200 hover:shadow-sm'
                                   }`}
                                   style={isEditing ? undefined : { touchAction: 'none' }}
                                 >
                                   {isEditing ? (
                                     <div onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="flex flex-col w-full gap-3 animate-in fade-in zoom-in duration-200">
                                       <input type="text" value={editForm.text} onChange={(e) => setEditForm({...editForm, text: e.target.value})} className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" autoFocus onKeyDown={(e) => e.key === 'Enter' && saveEdit()} />
                                       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                         <div className="flex flex-wrap items-center gap-2">
                                           <button type="button" onClick={() => setEditForm({...editForm, label: 'today'})} className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold transition-colors border ${editForm.label === 'today' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>Today</button>
                                           <button type="button" disabled={!canBeWeekly} title={canBeWeekly ? undefined : "Only 'Should Do' tasks can be promoted to This Week's Priority"} onClick={() => setEditForm({...editForm, label: 'week'})} className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold transition-colors border ${!canBeWeekly ? 'bg-transparent text-zinc-300 border-zinc-100 cursor-not-allowed' : editForm.label === 'week' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>This Week</button>
                                           <button type="button" onClick={() => setEditForm({...editForm, label: 'should'})} className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold transition-colors border ${editForm.label === 'should' ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>Should Do</button>
                                           <button type="button" onClick={() => setEditForm({...editForm, label: 'could'})} className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold transition-colors border ${editForm.label === 'could' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-transparent text-zinc-400 border-zinc-200 hover:bg-zinc-50'}`}>Could Do</button>
                                           <div className="h-3 w-px bg-zinc-300 mx-0.5 hidden sm:block"></div>
                                           <input type="text" placeholder="Tag" value={editForm.customTag} onChange={(e) => setEditForm({...editForm, customTag: e.target.value.replace(/^#/, '')})} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} className="text-[10px] bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 w-20 sm:w-24" />
                                           <select value={editForm.dayOfWeek} onChange={(e) => setEditForm({...editForm, dayOfWeek: e.target.value})} className="text-[10px] bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-zinc-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 cursor-pointer"><option value="">Any Day</option><option value="Mon">Mon</option><option value="Tue">Tue</option><option value="Wed">Wed</option><option value="Thu">Thu</option><option value="Fri">Fri</option><option value="Sat">Sat</option><option value="Sun">Sun</option></select>
                                           <select value={editForm.timeOfDay} onChange={(e) => setEditForm({...editForm, timeOfDay: e.target.value})} className="text-[10px] bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-zinc-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 cursor-pointer"><option value="">Any Time</option><option value="morning">Morning</option><option value="afternoon">Afternoon</option><option value="evening">Evening</option><option value="night">Night</option></select>
                                           <div className="relative flex items-center w-full sm:w-auto mt-1 sm:mt-0"><Clock className="w-3 h-3 absolute left-2 text-zinc-400 pointer-events-none" /><input type="number" placeholder="Mins limit" value={editForm.allocatedTime} onChange={(e) => setEditForm({...editForm, allocatedTime: e.target.value})} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} min="1" className="text-[10px] bg-zinc-50 border border-zinc-200 rounded-lg py-1 pl-6 pr-2 text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 w-full sm:w-24" /></div>
                                         </div>
                                         <div className="flex gap-1.5 self-end sm:self-auto w-full sm:w-auto justify-end border-t sm:border-0 border-zinc-100 pt-2 sm:pt-0 mt-1 sm:mt-0">
                                           <button type="button" onClick={cancelEdit} className="flex-1 sm:flex-none p-1.5 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors border border-zinc-200 flex justify-center"><X className="w-4 h-4" /></button>
                                           <button type="button" onClick={saveEdit} className="flex-1 sm:flex-none p-1.5 text-white bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors border border-indigo-600 flex justify-center"><Save className="w-4 h-4" /></button>
                                         </div>
                                       </div>
                                     </div>
                                   ) : (
                                     <>
                                       <div className="flex items-start sm:items-center gap-3 flex-1 text-left pr-0 sm:pr-4 w-full">
                                         <div className="cursor-grab active:cursor-grabbing p-1 text-zinc-300 hover:text-zinc-500 -ml-2 rounded-lg hover:bg-zinc-100 transition-colors hidden sm:block"><GripVertical className="w-4 h-4" /></div>
                                         
                                         {/* NUMBER LIST PREFIX ADDED HERE */}
                                         <span className="font-bold text-zinc-400 text-sm mt-0.5 sm:mt-0 w-4 text-right flex-shrink-0 select-none">
                                           {index + 1}.
                                         </span>

                                         <button onClick={() => toggleTodo(todo.id)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="focus:outline-none flex-shrink-0 mt-0.5 sm:mt-0"><div className={`text-indigo-500 transition-transform duration-300 ${todo.completed ? 'scale-110' : 'hover:scale-110'}`}>{todo.completed ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6 text-zinc-300" />}</div></button>
                                         <div className="flex flex-col gap-1.5 flex-1 overflow-hidden">
                                           <span className={`text-base font-medium leading-snug break-words transition-all ${todo.completed ? 'line-through text-zinc-400' : 'text-zinc-700'}`}>
                                             {todo.dayOfWeek && (
                                               <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider ${todo.completed ? 'bg-zinc-100 text-zinc-400' : 'bg-purple-100 text-purple-600'}`}>
                                                 ({todo.dayOfWeek})
                                               </span>
                                             )}
                                             {todo.text}
                                           </span>
                                           <div className="flex flex-wrap items-center gap-2">
                                             {todo.label && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md w-max ${todo.completed ? 'bg-zinc-100 text-zinc-400' : todo.label === 'should' ? 'bg-orange-100 text-orange-600' : todo.label === 'today' ? 'bg-red-100 text-red-600' : todo.label === 'week' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>{todo.label}</span>}
                                             {todo.customTag && <span className={`text-[10px] font-semibold tracking-wider px-2 py-0.5 rounded-md w-max border ${todo.completed ? 'bg-zinc-50 text-zinc-400 border-zinc-200' : 'bg-zinc-100 text-zinc-600 border-zinc-300'}`}>#{todo.customTag}</span>}
                                             {todo.timeOfDay && (<span className={`text-[10px] font-semibold tracking-wider px-2 py-0.5 rounded-md w-max border flex items-center gap-1 capitalize ${todo.completed ? 'bg-zinc-50 text-zinc-400 border-zinc-200' : 'bg-zinc-100 text-zinc-600 border-zinc-300'}`}>{todo.timeOfDay === 'morning' && <Sunrise className="w-3 h-3" />}{todo.timeOfDay === 'afternoon' && <Sun className="w-3 h-3" />}{todo.timeOfDay === 'evening' && <Sunset className="w-3 h-3" />}{todo.timeOfDay === 'night' && <Moon className="w-3 h-3" />}{todo.timeOfDay}</span>)}
                                           </div>
                                         </div>
                                       </div>
                                       
                                       <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto mt-4 sm:mt-0 pl-10 sm:pl-0 border-t sm:border-0 border-zinc-100 pt-3 sm:pt-0">
                                         <div className={`flex items-center transition-all ${todo.completed ? 'opacity-50' : ''}`}>
                                           <button onClick={(e) => toggleTimer(e, todo.id)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} disabled={todo.completed} className={`p-1.5 rounded-full transition-all mr-2 ${todo.isRunning ? 'bg-red-100 text-red-600 animate-pulse' : 'hover:bg-zinc-100 text-zinc-400 hover:text-emerald-500'} ${todo.completed ? 'cursor-not-allowed hidden' : ''}`} title={todo.isRunning ? "Pause Timer" : "Start Timer"}>{todo.isRunning ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}</button>
                                           {(todo.timeSpent > 0 || todo.isRunning || todo.allocatedTime > 0) && (<span className={`text-xs font-mono font-semibold min-w-[3rem] text-center flex items-center gap-0.5 ${todo.isRunning ? 'text-red-500' : 'text-zinc-500'}`}>{formatTime(todo.timeSpent)}{todo.allocatedTime > 0 && <span className={`ml-0.5 ${todo.timeSpent > todo.allocatedTime * 60 ? 'text-red-400' : 'text-zinc-400'}`}>/ {todo.allocatedTime}m</span>}</span>)}
                                         </div>
                                         <div className="flex items-center gap-1 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-all focus-within:opacity-100">
                                           {!todo.completed && (<button onClick={() => handleMagicBreakdown(todo)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} disabled={isBreakingDown} className="p-2 text-zinc-400 hover:text-yellow-500 hover:bg-yellow-50 rounded-lg transition-all" title="Use AI to Break Down Quest">{isBreakingDown ? <Loader2 className="w-4 h-4 animate-spin text-yellow-500" /> : <Wand2 className="w-4 h-4" />}</button>)}
                                           <button onClick={() => startEditing(todo)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="p-2 text-zinc-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-all" title="Edit Quest"><Pencil className="w-4 h-4" /></button>
                                           <button onClick={() => deleteTodo(todo.id)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Delete Quest"><Trash2 className="w-4 h-4" /></button>
                                         </div>
                                       </div>
                                     </>
                                   )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* --- HABITS LIST LOGIC --- */}
            {activeTab === 'habits' && (
              <>
              <CloudSyncLine
                className="px-1 mb-2"
                status={cloudSaveState.status}
                detail={cloudSaveState.message}
                domain="training"
                activeDomain={cloudSaveState.domain}
                saving="Syncing training to Firebase…"
                error="Training saved on this device — Firebase sync failed"
                idle="Training synced to Firebase"
              />
              {habits.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-zinc-400 space-y-3 py-10"><Flame className="w-10 h-10 opacity-20" /><p>What daily habits do you want to forge?</p></div>
              ) : (
                habits.map((habit, index) => {
                  const isDragging = draggedItemIdx === index; 
                  const isDoneToday = habit.history?.includes(todayDateStr) || false;
                  const isDragOver = dragOverItemIdx === index;
                  const isEditingHabit = editingHabitId !== null && editingHabitId !== undefined && String(editingHabitId) === String(habit.id);
                  const isEditingStreak = editingStreakHabitId !== null && editingStreakHabitId !== undefined && String(editingStreakHabitId) === String(habit.id);

                  return (
                    <div key={habit.id} draggable={!isEditingHabit && !isEditingStreak} onDragStart={(e) => handleDragStart(e, index)} onDragEnter={(e) => handleDragEnter(e, index)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDrop(e, index)} onDragEnd={handleDragEnd} className={`group flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${isDragging ? 'opacity-40 scale-[0.98] border-indigo-300 border-dashed bg-indigo-50/50' : isDragOver ? 'border-indigo-500 shadow-md bg-white translate-y-0.5' : isEditingHabit ? 'bg-white border-indigo-300 shadow-md ring-4 ring-indigo-50' : isDoneToday ? 'bg-indigo-50/50 border-indigo-100' : 'bg-white border-zinc-200 hover:border-indigo-200 hover:shadow-sm'}`}>
                      {isEditingHabit ? (
                        <div onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="flex flex-col w-full gap-3 animate-in fade-in zoom-in duration-200">
  <input
    autoFocus
    type="text"
    value={habitEditForm}
    onChange={(e) => setHabitEditForm(e.target.value)}
    onKeyDown={(e) => e.key === 'Enter' && saveHabitEdit()}
    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
  />
  <div className="flex flex-wrap items-center gap-3">
    <span className="text-xs font-semibold text-zinc-500">Frequency:</span>
    <label className="flex items-center gap-1 text-xs">
      <input
        type="radio"
        name="editHabitFrequency"
        value="daily"
        checked={editingHabitFrequency === 'daily'}
        onChange={() => setEditingHabitFrequency('daily')}
      />
      Daily
    </label>
    <label className="flex items-center gap-1 text-xs">
      <input
        type="radio"
        name="editHabitFrequency"
        value="weekly"
        checked={editingHabitFrequency === 'weekly'}
        onChange={() => setEditingHabitFrequency('weekly')}
      />
      Weekly
    </label>
    {editingHabitFrequency === 'weekly' && (
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">Target:</span>
        <select
          value={editingHabitWeeklyTarget}
          onChange={(e) => setEditingHabitWeeklyTarget(Number(e.target.value))}
          className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2"
        >
          {[1,2,3,4,5,6,7].map(n => (
            <option key={n} value={n}>{n} {n === 1 ? 'time' : 'times'}/week</option>
          ))}
        </select>
      </div>
    )}
  </div>
  <div className="flex gap-1.5 self-end w-full sm:w-auto justify-end">
    <button type="button" onClick={cancelHabitEdit} className="flex-1 sm:flex-none p-1.5 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg border border-zinc-200"><X className="w-4 h-4" /></button>
    <button type="button" onClick={saveHabitEdit} className="flex-1 sm:flex-none p-1.5 text-white bg-indigo-500 hover:bg-indigo-600 rounded-lg border border-indigo-600"><Save className="w-4 h-4" /></button>
  </div>
</div>
                      ) : (
                        <>
                          <div className="flex items-start sm:items-center gap-3 flex-1 text-left pr-0 sm:pr-4 w-full">
                            <div className="cursor-grab active:cursor-grabbing p-1 text-zinc-300 hover:text-zinc-500 -ml-2 rounded-lg hover:bg-zinc-100 transition-colors hidden sm:block"><GripVertical className="w-4 h-4" /></div>
                           <div className="flex flex-col flex-1">
                              <span className="text-base font-medium text-zinc-700 mb-1 leading-snug">{habit.text}</span>
                              {habit.frequency === 'daily' && (
                                isEditingStreak ? (
                                  <div className="flex flex-wrap items-center gap-1.5 text-xs" onPointerDown={(e) => e.stopPropagation()}>
                                    <Flame className="w-3.5 h-3.5 fill-orange-500 text-orange-500" />
                                    <input
                                      type="number"
                                      min="0"
                                      value={streakEditForm}
                                      onChange={(e) => setStreakEditForm(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveHabitStreakEdit();
                                        if (e.key === 'Escape') cancelHabitStreakEdit();
                                      }}
                                      autoFocus
                                      className="w-16 text-xs font-bold text-orange-700 bg-white border border-orange-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                                    />
                                    <span className="font-semibold text-orange-600">day streak</span>
                                    <button type="button" onClick={cancelHabitStreakEdit} className="p-1 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-md border border-zinc-200" title="Cancel streak edit"><X className="w-3.5 h-3.5" /></button>
                                    <button type="button" onClick={saveHabitStreakEdit} className="p-1 text-white bg-orange-500 hover:bg-orange-600 rounded-md border border-orange-600" title="Save streak"><Save className="w-3.5 h-3.5" /></button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-500">
                                    <Flame className={`w-3.5 h-3.5 ${habit.streak > 0 ? 'fill-orange-500' : 'opacity-40 text-zinc-400 fill-none'}`} />
                                    <span className={habit.streak > 0 ? 'text-orange-500' : 'text-zinc-400'}>{habit.streak} day streak</span>
                                    <span className="ml-2 text-zinc-400 text-[10px] font-normal">Daily</span>
                                    <button
                                      type="button"
                                      onClick={() => startEditingHabitStreak(habit)}
                                      className="p-1 text-zinc-400 hover:text-orange-500 hover:bg-orange-50 rounded-md"
                                      title="Edit day streak"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )
                              )}
                              {habit.frequency === 'weekly' && (
                                isEditingStreak ? (
                                  <div className="flex flex-wrap items-center gap-1.5 text-xs" onPointerDown={(e) => e.stopPropagation()}>
                                    <input
                                      type="number"
                                      min="0"
                                      max="14"
                                      value={streakEditForm}
                                      onChange={(e) => setStreakEditForm(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveHabitStreakEdit();
                                        if (e.key === 'Escape') cancelHabitStreakEdit();
                                      }}
                                      autoFocus
                                      className="w-16 text-xs font-bold text-indigo-700 bg-white border border-indigo-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                    <span className="font-semibold text-indigo-600">/ {habit.weeklyTarget} times this week</span>
                                    <button type="button" onClick={cancelHabitStreakEdit} className="p-1 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-md border border-zinc-200" title="Cancel weekly count edit"><X className="w-3.5 h-3.5" /></button>
                                    <button type="button" onClick={saveHabitStreakEdit} className="p-1 text-white bg-indigo-500 hover:bg-indigo-600 rounded-md border border-indigo-600" title="Save weekly count"><Save className="w-3.5 h-3.5" /></button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="font-semibold text-indigo-600">
                                      {habit.weeklyProgress?.completionsCount || 0} / {habit.weeklyTarget} times this week
                                    </span>
                                    <span className="text-zinc-400 text-[10px] font-normal">Weekly</span>
                                    <button
                                      type="button"
                                      onClick={() => startEditingHabitStreak(habit)}
                                      className="p-1 text-zinc-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-md"
                                      title="Edit times this week"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-3 mt-4 sm:mt-0 w-full sm:w-auto border-t sm:border-0 border-zinc-100 pt-3 sm:pt-0">
                            <button type="button" onClick={() => toggleHabit(habit.id)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className={`flex-1 sm:flex-none justify-center px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-1.5 ${isDoneToday ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 shadow-inner' : 'bg-zinc-100 text-zinc-700 hover:bg-indigo-500 hover:text-white hover:shadow-md'}`}>{isDoneToday && <RotateCcw className="w-3.5 h-3.5" />}{isDoneToday ? 'Undo' : 'Train'}</button>
                            <div className="flex items-center gap-1 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-all focus-within:opacity-100">
                              <button type="button" onClick={() => startEditingHabit(habit)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="p-2 text-zinc-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-all" title="Edit Habit"><Pencil className="w-4 h-4" /></button>
                              <button type="button" onClick={() => deleteHabit(habit.id)} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Delete Habit"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
              </>
            )}

            {activeTab === 'calendar' && (
              <div className="space-y-6">
                <div className="text-center mb-6"><h2 className="text-lg font-bold text-zinc-800">The Chain of Momentum</h2><p className="text-zinc-500 text-sm">Don't break the chain. Build your legacy.</p></div>
                {habits.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-zinc-400 space-y-3 py-10"><CalendarDays className="w-10 h-10 opacity-20" /><p>Add some habits to start tracking your momentum!</p></div>
                ) : (
                  <>
                    <div className="flex items-center justify-between bg-zinc-100/50 p-2 rounded-xl border border-zinc-200 mb-4">
                      <button onClick={() => setCalendarOffset(prev => prev + 1)} className="p-2 hover:bg-white rounded-lg text-zinc-600 hover:text-indigo-600 transition-colors shadow-sm border border-transparent hover:border-zinc-200" title="Previous 21 Days"><ChevronLeft className="w-5 h-5" /></button>
                      <div className="text-sm font-semibold text-zinc-700">{formatDate(getDaysWindow(21, calendarOffset)[0])} - {formatDate(getDaysWindow(21, calendarOffset)[20])}</div>
                      <button onClick={() => setCalendarOffset(prev => Math.max(0, prev - 1))} disabled={calendarOffset === 0} className={`p-2 rounded-lg transition-colors border ${calendarOffset === 0 ? 'text-zinc-300 border-transparent cursor-not-allowed' : 'hover:bg-white text-zinc-600 hover:text-indigo-600 shadow-sm border-transparent hover:border-zinc-200'}`} title="Next 21 Days"><ChevronRight className="w-5 h-5" /></button>
                    </div>
                    {habits.map(habit => {
                      const currentWindow = getDaysWindow(21, calendarOffset);
                      return (
                        <div key={habit.id} className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
  <span className="font-bold text-zinc-800">{habit.text}</span>
  
  {editingStreakHabitId === habit.id ? (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        max={habit.frequency === 'weekly' ? 14 : undefined}
        value={streakEditForm}
        onChange={(e) => setStreakEditForm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveHabitStreakEdit();
          if (e.key === 'Escape') cancelHabitStreakEdit();
        }}
        autoFocus
        className={`w-20 text-xs font-bold bg-white border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 ${
          habit.frequency === 'weekly'
            ? 'text-indigo-700 border-indigo-200 focus:ring-indigo-500/20'
            : 'text-orange-700 border-orange-200 focus:ring-orange-500/20'
        }`}
      />

      <span className={`text-xs font-bold ${habit.frequency === 'weekly' ? 'text-indigo-600' : 'text-orange-600'}`}>
        {habit.frequency === 'weekly' ? `/ ${habit.weeklyTarget} this week` : 'Day Streak'}
      </span>

      <button
        onClick={cancelHabitStreakEdit}
        className="p-1.5 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors border border-zinc-200"
        title={habit.frequency === 'weekly' ? 'Cancel weekly count edit' : 'Cancel streak edit'}
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <button
        onClick={saveHabitStreakEdit}
        className={`p-1.5 text-white rounded-lg transition-colors border ${
          habit.frequency === 'weekly'
            ? 'bg-indigo-500 hover:bg-indigo-600 border-indigo-600'
            : 'bg-orange-500 hover:bg-orange-600 border-orange-600'
        }`}
        title={habit.frequency === 'weekly' ? 'Save weekly count' : 'Save streak'}
      >
        <Save className="w-3.5 h-3.5" />
      </button>
    </div>
  ) : habit.frequency === 'weekly' ? (
    <div className="flex items-center gap-2">
      <span className="text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-lg">
        {habit.weeklyProgress?.completionsCount || 0} / {habit.weeklyTarget} this week
      </span>
      <button
        onClick={() => startEditingHabitStreak(habit)}
        className="p-1.5 text-zinc-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
        title="Edit times this week"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <span className="text-xs font-bold text-orange-600 bg-orange-50 border border-orange-100 px-2 py-1 rounded-lg">
        {habit.streak} Day Streak
      </span>
      <button
        onClick={() => startEditingHabitStreak(habit)}
        className="p-1.5 text-zinc-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
        title="Edit day streak"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  )}
</div>
                          <div className="relative">
                            <div className="flex items-center w-full pb-4 pt-2 px-1">
                              {currentWindow.map((dateStr, index, arr) => {
                                const isCompleted = habit.history?.includes(dateStr); const isToday = dateStr === todayDateStr; const nextCompleted = index < arr.length - 1 && habit.history?.includes(arr[index + 1]);
                                return (
                                  <div key={dateStr} className={`flex items-center group relative cursor-default ${index < arr.length - 1 ? 'flex-1' : ''}`}>
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-[10px] py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">{dateStr} {isToday ? '(Today)' : ''}</div>
                                    <div className={`flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-[2px] transition-all duration-300 z-10 ${isCompleted ? 'bg-indigo-500 border-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.4)]' : isToday ? 'bg-zinc-100 border-indigo-300 border-dashed' : 'bg-zinc-50 border-zinc-200'}`} />
                                    {index < arr.length - 1 && (<div className={`flex-1 h-1 sm:h-1.5 transition-all duration-300 -ml-0.5 -mr-0.5 sm:-ml-1 sm:-mr-1 z-0 ${isCompleted && nextCompleted ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.3)]' : 'bg-zinc-100'}`} />)}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
            
            {/* DAILY SCHEDULE TAB */}
            {activeTab === 'schedule' && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-lg font-bold text-zinc-800">Daily Schedule</h2>
                  <p className="text-zinc-500 text-sm">Select your total duration and distribute it across the day.</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-center bg-zinc-50 p-4 rounded-2xl border border-zinc-200">
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <label className="text-xs font-semibold text-zinc-600 flex-shrink-0">Duration:</label>
                    <select 
                      value={scheduleConfig.durationHours}
                      onChange={(e) => {
                        const newConfig = { ...scheduleConfig, durationHours: Number(e.target.value) };
                        setScheduleConfig(newConfig);
                        updateCloud({ scheduleConfig: newConfig });
                      }}
                      className="flex-1 sm:flex-none min-w-0 max-w-full bg-white border border-zinc-200 rounded-lg py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    >
                      <option value={1}>1 Hour (2 Blocks)</option>
                      <option value={1.5}>1.5 Hours (3 Blocks)</option>
                      <option value={2}>2 Hours (4 Blocks)</option>
                      <option value={2.5}>2.5 Hours (5 Blocks)</option>
                      <option value={3}>3 Hours (6 Blocks)</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <label className="text-xs font-semibold text-zinc-600 flex-shrink-0">Distribution:</label>
                    <select 
                      value={scheduleConfig.distribution}
                      onChange={(e) => {
                        const newConfig = { ...scheduleConfig, distribution: e.target.value };
                        setScheduleConfig(newConfig);
                        updateCloud({ scheduleConfig: newConfig });
                      }}
                      className="flex-1 sm:flex-none min-w-0 max-w-full bg-white border border-zinc-200 rounded-lg py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    >
                      <optgroup label="All in one period">
                        <option value="morning">All in Morning</option>
                        <option value="afternoon">All in Afternoon</option>
                        <option value="evening">All in Evening</option>
                        <option value="night">All in Night</option>
                      </optgroup>
                      <optgroup label="Split across thirds">
                        {SPLIT_DISTRIBUTIONS.map((code) => (
                          <option key={code} value={`split:${code}`}>{splitLabel(code)}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  {Array.from({ length: scheduleConfig.durationHours * 2 }).map((_, index) => {
                    const blockData = scheduleData[index] || { time: '', todoId: '' };
                    const selectedTodo = todos.find(t => String(t.id) === String(blockData.todoId));
                    const blockInfo = getBlockInfo(index, scheduleConfig.durationHours * 2, scheduleConfig.distribution);
                    
                    const periodIcon = {
                      'Morning': <Sunrise className="w-3.5 h-3.5 text-amber-500" />,
                      'Afternoon': <Sun className="w-3.5 h-3.5 text-orange-500" />,
                      'Evening': <Sunset className="w-3.5 h-3.5 text-purple-500" />,
                      'Night': <Moon className="w-3.5 h-3.5 text-indigo-500" />
                    }[blockInfo.period];

                    const getEndTime = (startTime) => {
                      if (!startTime) return '--:--';
                      const [h, m] = startTime.split(':').map(Number);
                      const endTotalMin = h * 60 + m + 30;
                      const eH = Math.floor(endTotalMin / 60) % 24;
                      const eM = endTotalMin % 60;
                      return `${String(eH).padStart(2, '0')}:${String(eM).padStart(2, '0')}`;
                    };

                    return (
                      <div key={index} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-2xl shadow-sm border transition-all ${blockData.todoId ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-zinc-200'}`}>
                        <div className="flex-shrink-0 w-full sm:w-28 text-center sm:text-left">
                          <div className="flex items-center gap-1.5 justify-center sm:justify-start text-indigo-600 mb-0.5">
                            {periodIcon}
                            <span className="text-xs font-bold uppercase tracking-wider">{blockInfo.period} {blockInfo.count}</span>
                          </div>
                          <div className="text-[10px] text-zinc-400">30 Minute Block</div>
                        </div>
                        
                        <div className="flex-1 w-full space-y-2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                            <div className="flex flex-col gap-1">
                              <input 
                                type="time"
                                value={blockData.time}
                                onChange={(e) => {
                                  const newData = { ...scheduleData, [index]: { ...blockData, time: e.target.value } };
                                  setScheduleData(newData);
                                  updateCloud({ scheduleData: newData });
                                }}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                              />
                              <span className="text-[10px] text-zinc-400 px-1">to {getEndTime(blockData.time)}</span>
                            </div>
                            
                            <select
                              value={blockData.todoId}
                              onChange={(e) => {
                                const newData = { ...scheduleData, [index]: { ...blockData, todoId: e.target.value } };
                                setScheduleData(newData);
                                updateCloud({ scheduleData: newData });
                              }}
                              className="w-full bg-white border border-zinc-200 rounded-xl py-2 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 h-[38px]"
                            >
                              <option value="">Select a Today's Priority...</option>
                              {todos.filter(t => t.label === 'today').map(todo => (
                                <option key={todo.id} value={todo.id}>
                                  {todo.text} {todo.completed ? '(Done)' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {selectedTodo && (
                            <div className="flex items-center gap-2 text-xs">
                              {selectedTodo.customTag && <span className="bg-zinc-100 text-zinc-600 border border-zinc-300 px-2 py-0.5 rounded-md">#{selectedTodo.customTag}</span>}
                              {selectedTodo.allocatedTime > 0 && <span className="bg-zinc-100 text-zinc-600 border border-zinc-300 px-2 py-0.5 rounded-md flex items-center gap-1"><Clock className="w-3 h-3" /> Alloc: {selectedTodo.allocatedTime}m</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CALENDAR — multi-week sprint, daily routine, nightly scorecard */}
            {activeTab === 'sprint' && (
              <SprintBoardTab
                board={sprintBoard}
                onChange={commitSprintBoard}
                onImport={replaceSprintBoard}
                notify={showNotification}
                syncLine={(
                  <CloudSyncLine
                    className="mt-2"
                    status={cloudSaveState.status}
                    detail={cloudSaveState.message}
                    domain="calendar"
                    activeDomain={cloudSaveState.domain}
                    saving="Syncing calendar to Firebase…"
                    error="Calendar saved on this device — Firebase sync failed"
                    idle="Calendar synced to Firebase"
                  />
                )}
              />
            )}

            {/* 3-DAY ADVANCE PLANNER */}
            {activeTab === 'plan' && (
              <div className="space-y-6">
                <div className="text-center mb-2">
                  <h2 className="text-lg font-bold text-zinc-800">3-Day Advance Planner</h2>
                  <p className="text-zinc-500 text-sm">
                    AI picks the few blocks that move your Domino Run goals and leaves the rest of the day open. Synced to Firebase when signed in.
                  </p>
                  <CloudSyncLine
                    className="mt-2"
                    status={cloudSaveState.status}
                    detail={cloudSaveState.message}
                    domain="plan"
                    activeDomain={cloudSaveState.domain}
                    saving="Syncing plan to Firebase…"
                    error="Plan saved on this device — Firebase sync failed"
                    idle="Plan synced to Firebase"
                  />
                </div>

                {(() => {
                  const nextDays = getNextThreeDays();
                  const selected = nextDays[planDayIndex] || nextDays[0];
                  const plan = getDayPlan(selected.dateKey);
                  const segTimes = withPlanTimes(plan.segments);
                  const filledBlocks = segTimes.filter((s) => s.text && s.text.trim()).length;
                  // Reflect fixed commitments defined in the Schedule tab onto the timeline.
                  const scheduleCommitments = Object.values(scheduleData || {})
                    .filter((b) => b && typeof b.time === 'string' && /^\d{1,2}:\d{2}$/.test(b.time))
                    .map((b) => {
                      const [h, m] = b.time.split(':').map(Number);
                      const todo = todos.find((t) => String(t.id) === String(b.todoId));
                      return { startMin: h * 60 + m, label: todo?.text || '' };
                    });
                  const aiMeta = planAiMeta[selected.dateKey];
                  const hasPlanContent = !!(plan.focus?.trim() || filledBlocks > 0);
                  const hasDominoGoals = dominoCategories.some(({ id }) => dominos[id]?.goal?.trim());

                  return (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        {nextDays.map((day, idx) => {
                          const dayPlan = getDayPlan(day.dateKey);
                          const dayFilled = (dayPlan.segments || []).filter((s) => s.text && s.text.trim()).length;
                          const isActive = planDayIndex === idx;
                          return (
                            <button
                              key={day.dateKey}
                              type="button"
                              onClick={() => setPlanDayIndex(idx)}
                              className={`p-2 sm:p-3 rounded-2xl border text-left transition-all min-w-0 ${
                                isActive
                                  ? 'bg-indigo-50 border-indigo-300 shadow-sm ring-1 ring-indigo-200'
                                  : 'bg-zinc-50 border-zinc-200 hover:bg-white hover:border-zinc-300'
                              }`}
                            >
                              <div className={`text-[10px] font-bold uppercase tracking-wider truncate ${isActive ? 'text-indigo-600' : 'text-zinc-400'}`}>
                                {day.relative}
                              </div>
                              <div className="text-xs sm:text-sm font-bold text-zinc-800 mt-0.5 break-words">{day.label}</div>
                              <div className="text-[10px] text-zinc-500 mt-1">
                                {dayPlan.focus?.trim()
                                  ? dayPlan.focus.trim().slice(0, 28) + (dayPlan.focus.length > 28 ? '…' : '')
                                  : dayFilled > 0
                                    ? `${dayFilled} block${dayFilled === 1 ? '' : 's'} planned`
                                    : 'No plan yet'}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 sm:p-5 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h3 className="text-base font-bold text-zinc-800">{selected.weekday}</h3>
                            <p className="text-xs text-zinc-500">{selected.label} · {selected.dateKey}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wider bg-white border border-zinc-200 text-zinc-600 px-2.5 py-1 rounded-full">
                              6:30 AM–11:00 PM · {filledBlocks}/{segTimes.length} filled
                            </span>
                            {aiMeta?.source && (
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                                aiMeta.source === 'gemini'
                                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                  : 'bg-violet-50 text-violet-700 border-violet-200'
                              }`}>
                                {aiMeta.source === 'gemini' ? 'Gemini 2.5 Flash' : 'DeepSeek Flash'}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            disabled={suggestingPlan}
                            onClick={() => suggestDayPlanWithAi(selected.dateKey, selected, { refresh: false })}
                            className="flex-1 px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center justify-center gap-2"
                          >
                            {suggestingPlan ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> AI planning…</>
                            ) : (
                              <><Wand2 className="w-4 h-4" /> {hasPlanContent ? 'Re-suggest with AI' : 'Suggest day with AI'}</>
                            )}
                          </button>
                          <button
                            type="button"
                            disabled={suggestingPlan || !hasPlanContent}
                            onClick={() => suggestDayPlanWithAi(selected.dateKey, selected, { refresh: true })}
                            className="px-4 py-2.5 bg-white hover:bg-zinc-100 disabled:opacity-50 text-zinc-700 border border-zinc-200 rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center justify-center gap-2"
                            title="Generate a different AI plan for this day"
                          >
                            <RotateCcw className="w-4 h-4" /> Refresh suggestion
                          </button>
                        </div>
                        <p className="text-[11px] text-zinc-500">
                          Uses Gemini 2.5 Flash first
                          {hasGeminiKey() ? ` ✓ (${geminiKeys().length} key${geminiKeys().length === 1 ? '' : 's'})` : ' (add key in footer)'}
                          , then DeepSeek Flash fallback
                          {deepseekApiKey.trim() ? ' ✓' : ''}.
                          Suggests at most {PLAN_MAX_FILLED_BLOCKS} blocks (≤3 goal-focus) and leaves the rest open on purpose.
                        </p>
                        {!hasDominoGoals && (
                          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                            <span>No Domino Run goals defined yet — AI has no scoreboard, so every quest it schedules will come back marked <strong>Swap this</strong>. Set a goal in the Domino Run first.</span>
                          </p>
                        )}

                        <div>
                          <label className="block text-xs font-bold text-zinc-700 mb-1.5">Day focus (one main win)</label>
                          <input
                            type="text"
                            value={plan.focus}
                            onChange={(e) => updateDayPlanField(selected.dateKey, 'focus', e.target.value)}
                            placeholder="e.g. Ship planner feature, deep work on thesis…"
                            className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            disabled={suggestingPlan}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-zinc-700 mb-1.5">Notes / constraints</label>
                          <textarea
                            value={plan.notes}
                            onChange={(e) => updateDayPlanField(selected.dateKey, 'notes', e.target.value)}
                            placeholder="Meetings, energy, errands, what to protect…"
                            rows={2}
                            className="w-full bg-white border border-zinc-200 rounded-xl p-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-none"
                            disabled={suggestingPlan}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-bold text-zinc-800 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-indigo-500" />
                            Your day · 6:30 AM–11:00 PM
                          </h3>
                          <span className="text-[10px] text-zinc-400 hidden sm:block">
                            Drag to reorder · ▲▼ to resize (merge slots into bigger blocks)
                          </span>
                        </div>

                        <DndContext
                          sensors={tabSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            const { active, over } = event;
                            if (!over || active.id === over.id) return;
                            const ids = segTimes.map((s) => s.id);
                            const from = ids.indexOf(active.id);
                            const to = ids.indexOf(over.id);
                            if (from < 0 || to < 0) return;
                            reorderPlanSegments(selected.dateKey, from, to);
                          }}
                        >
                          <SortableContext items={segTimes.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-2">
                              {segTimes.map((seg, index) => (
                                <SortablePlanBlock
                                  key={seg.id}
                                  seg={seg}
                                  isLast={index === segTimes.length - 1}
                                  disabled={suggestingPlan}
                                  commitments={scheduleCommitments.filter(
                                    (c) => c.startMin >= seg.startMin && c.startMin < seg.endMin
                                  )}
                                  onText={(id, text) => updatePlanSegmentText(selected.dateKey, id, text)}
                                  onExtend={(id) => resizePlanSegment(selected.dateKey, id, PLAN_SLOT_MIN)}
                                  onShrink={(id) => resizePlanSegment(selected.dateKey, id, -PLAN_SLOT_MIN)}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>

                      <p className="text-[11px] text-zinc-400 text-center">
                        Fixed day, 6:30 AM to 11:00 PM. AI fills only the few blocks that build momentum toward your Domino Run goals — the blank ones are deliberate. Blocks marked <strong>AI Quest:</strong> come from your quest list and carry the AI's verdict. Drag to reorder or resize in 30-min steps (the day still ends at 11 PM).
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

            {/* FOOD / MEAL CALORIES TRACKER */}
            {activeTab === 'nutrition' && (
              <div className="space-y-6">
                <div className="text-center mb-2">
                  <h2 className="text-lg font-bold text-zinc-800">Meals & Calories</h2>
                  <p className="text-zinc-500 text-sm">
                    Log meals, stay on target, and get tips tailored to your health notes. Edited calorie counts are remembered for the same or similar meals.
                  </p>
                  <CloudSyncLine
                    className="mt-2"
                    status={cloudSaveState.status}
                    detail={cloudSaveState.message}
                    domain="meals"
                    activeDomain={cloudSaveState.domain}
                    saving="Syncing meals to Firebase…"
                    error="Meals saved on this device — Firebase sync failed"
                    idle="Meals synced to Firebase"
                  />
                </div>

                {(() => {
                  const mealDateKey = getMealDateKey();
                  const dayMeals = getMealsForDate(mealDateKey);
                  const caloriesEaten = getDailyCalories(mealDateKey);
                  const daySteps = getDaySteps(mealDateKey);
                  const stepCalories = getStepCalories(mealDateKey);
                  const caloriesToday = getNetDailyCalories(mealDateKey);
                  const target = healthProfile.dailyCalorieTarget || 2000;
                  const remaining = target - caloriesToday;
                  const pct = Math.min(100, Math.max(0, Math.round((Math.max(0, caloriesToday) / target) * 100)));
                  const over = remaining < 0;
                  const sortedMeals = [...dayMeals].sort((a, b) => {
                    if (mealSort === 'high') return (Number(b.calories) || 0) - (Number(a.calories) || 0);
                    if (mealSort === 'low') return (Number(a.calories) || 0) - (Number(b.calories) || 0);
                    return 0;
                  });
                  const mealDateLabel = (() => {
                    const d = new Date();
                    d.setHours(12, 0, 0, 0);
                    d.setDate(d.getDate() + mealDateOffset);
                    if (mealDateOffset === 0) return 'Today';
                    if (mealDateOffset === -1) return 'Yesterday';
                    if (mealDateOffset === 1) return 'Tomorrow';
                    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  })();
                  const vTaperPlan = healthProfile.weightGoal === 'v_taper'
                    ? computeVTaperPlan(healthProfile)
                    : null;
                  const vTaperDeficit = vTaperPlan ? Math.round(vTaperPlan.tdee - caloriesToday) : 0;
                  const suggestions = getNutritionSuggestions(
                    healthProfile.conditions,
                    caloriesToday,
                    target,
                    healthProfile.weightGoal,
                    vTaperPlan
                  );
                  const mealTypeColors = {
                    breakfast: 'bg-amber-50 text-amber-700 border-amber-200',
                    lunch: 'bg-sky-50 text-sky-700 border-sky-200',
                    dinner: 'bg-violet-50 text-violet-700 border-violet-200',
                    snack: 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  };

                  return (
                    <>
                      {/* Health profile */}
                      <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-4 sm:p-5">
                        <div className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => setShowHealthProfile((v) => !v)}
                            className="flex items-center gap-2 text-left flex-1 hover:opacity-80 transition-opacity"
                          >
                            <div className="p-1.5 bg-rose-100 rounded-lg text-rose-600"><Heart className="w-4 h-4" /></div>
                            <div>
                              <h3 className="text-sm font-bold text-zinc-800">Health profile</h3>
                              <p className="text-[11px] text-zinc-500">Saved to your account — used for meal suggestions</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowHealthProfile((v) => !v)}
                            className="p-1.5 text-zinc-400 hover:text-zinc-600 rounded-lg transition-colors"
                            aria-label={showHealthProfile ? 'Collapse health profile' : 'Expand health profile'}
                          >
                            {showHealthProfile ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                          </button>
                        </div>

                        {showHealthProfile && (
                          <div className="mt-4 space-y-4 pt-4 border-t border-rose-100/70">

                        <div>
                          <label className="block text-xs font-bold text-zinc-700 mb-1.5">
                            Existing conditions / dietary needs
                          </label>
                          <textarea
                            value={healthConditionsDraft}
                            onChange={(e) => {
                              const val = e.target.value;
                              setHealthConditionsDraft(val);
                              touchLifestyleEdit();
                              lifestyleRef.current = {
                                ...lifestyleRef.current,
                                healthProfile: {
                                  ...lifestyleRef.current.healthProfile,
                                  conditions: val
                                }
                              };
                            }}
                            onBlur={(e) => commitHealthProfile({ conditions: e.target.value })}
                            placeholder="e.g. Type 2 diabetes, high blood pressure, PCOS, lactose intolerance, GERD… Include anything a coach should know."
                            rows={3}
                            className="w-full bg-white border border-zinc-200 rounded-xl p-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 resize-none"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            {healthProfile.weightGoal === 'v_taper' ? (
                              <div>
                                <div className="flex items-center justify-between gap-1 mb-1.5">
                                  <label className="block text-xs font-bold text-zinc-700">Daily calorie target</label>
                                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Lock className="w-2.5 h-2.5 text-emerald-600" /> Strictly Kinobody
                                  </span>
                                </div>
                                <div className="w-full bg-zinc-100/90 border border-zinc-200 rounded-xl py-2.5 px-3 text-sm font-bold text-zinc-800 tabular-nums flex items-center justify-between cursor-not-allowed select-none">
                                  <span>{vTaperPlan ? vTaperPlan.target : healthProfile.dailyCalorieTarget} kcal</span>
                                  <span className="text-[11px] font-semibold text-emerald-600">Auto-calculated</span>
                                </div>
                                <p className="text-[11px] text-zinc-500 mt-1.5">
                                  Strictly locked: Maintenance ({vTaperPlan ? vTaperPlan.tdee : 2215} kcal) − {vTaperPlan ? vTaperPlan.deficit : 400} deficit. Updates automatically with your body weight.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <label className="block text-xs font-bold text-zinc-700 mb-1.5">Daily calorie target</label>
                                <input
                                  type="number"
                                  min="800"
                                  max="6000"
                                  step="50"
                                  value={profileNumberDrafts.calories ?? healthProfile.dailyCalorieTarget}
                                  onChange={(e) => setProfileNumberDrafts(d => ({ ...d, calories: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  onBlur={() => commitProfileNumber('calories', profileNumberDrafts.calories)}
                                  className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                />
                                <p className="text-[11px] text-zinc-400 mt-1.5">Set a custom calorie target for your weight goal.</p>
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-zinc-700 mb-1.5">Weight goal</label>
                            <select
                              value={healthProfile.weightGoal}
                              onChange={(e) => commitHealthProfile({ weightGoal: e.target.value })}
                              className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                            >
                              <option value="lose">Avoid gain / lose gently</option>
                              <option value="maintain">Maintain weight</option>
                              <option value="gain">Gain (muscle / bulk)</option>
                              <option value="v_taper">V Taper physique (Kinobody)</option>
                            </select>
                          </div>
                        </div>

                        {healthProfile.weightGoal === 'v_taper' && (
                          <div>
                            <label className="block text-xs font-bold text-zinc-700 mb-1.5">Deficit strategy</label>
                            <select
                              value={healthProfile.customDeficit ?? 400}
                              onChange={(e) => commitHealthProfile({ customDeficit: Number(e.target.value) })}
                              className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                            >
                              <option value={400}>Standard Lean Cut (−400 kcal/day · ~18% deficit)</option>
                              <option value={600}>Aggressive Cut (−600 kcal/day · ~27% deficit)</option>
                              <option value={250}>Mild Cut (−250 kcal/day)</option>
                            </select>
                          </div>
                        )}

                        {/* Body stats — editable, power the V-taper calorie deficit math */}
                        <div>
                          <div className="flex items-baseline justify-between gap-2 mb-1.5">
                            <label className="block text-xs font-bold text-zinc-700">
                              Body stats <span className="font-normal text-zinc-400">— height &amp; weight set your V-taper deficit</span>
                            </label>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Height (cm)</label>
                              <input
                                type="number"
                                min="100"
                                max="250"
                                step="1"
                                inputMode="numeric"
                                value={profileNumberDrafts.heightCm ?? healthProfile.heightCm}
                                onChange={(e) => setProfileNumberDrafts(d => ({ ...d, heightCm: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                onBlur={() => commitProfileNumber('heightCm', profileNumberDrafts.heightCm)}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Weight (kg)</label>
                              <input
                                type="number"
                                min="30"
                                max="300"
                                step="0.1"
                                inputMode="decimal"
                                value={profileNumberDrafts.weightKg ?? healthProfile.weightKg}
                                onChange={(e) => setProfileNumberDrafts(d => ({ ...d, weightKg: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                onBlur={() => commitProfileNumber('weightKg', profileNumberDrafts.weightKg)}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Age</label>
                              <input
                                type="number"
                                min="13"
                                max="100"
                                step="1"
                                inputMode="numeric"
                                value={profileNumberDrafts.age ?? healthProfile.age}
                                onChange={(e) => setProfileNumberDrafts(d => ({ ...d, age: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                onBlur={() => commitProfileNumber('age', profileNumberDrafts.age)}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Sex</label>
                              <select
                                value={healthProfile.sex}
                                onChange={(e) => commitHealthProfile({ sex: e.target.value })}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                              >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Activity level</label>
                              <select
                                value={healthProfile.activityLevel}
                                onChange={(e) => commitHealthProfile({ activityLevel: e.target.value })}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                              >
                                <option value="sedentary">Sedentary — desk, little exercise</option>
                                <option value="light">Light — 1–3 workouts/wk</option>
                                <option value="moderate">Moderate — 3–5 workouts/wk</option>
                                <option value="active">Active — 6–7 workouts/wk</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {vTaperPlan && (
                          <div className="bg-white border border-rose-200 rounded-xl p-3 sm:p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-bold text-zinc-800 flex items-center gap-1.5">
                                <Flame className="w-3.5 h-3.5 text-rose-500" /> V-taper calorie &amp; macro plan
                              </span>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                                <Lock className="w-2.5 h-2.5 text-emerald-600" /> Strictly Kinobody ({vTaperPlan.target} kcal)
                              </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                              <div className="bg-zinc-50 rounded-lg py-1.5 px-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Body Weight</div>
                                <div className="text-sm font-bold text-zinc-800 tabular-nums">{vTaperPlan.weightLbs} lbs</div>
                              </div>
                              <div className="bg-zinc-50 rounded-lg py-1.5 px-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Maintenance (15×)</div>
                                <div className="text-sm font-bold text-zinc-800 tabular-nums">{vTaperPlan.tdee} kcal</div>
                              </div>
                              <div className="bg-zinc-50 rounded-lg py-1.5 px-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Daily Deficit</div>
                                <div className="text-sm font-bold text-rose-600 tabular-nums">−{vTaperPlan.deficit} kcal ({vTaperPlan.deficitPct}%)</div>
                              </div>
                              <div className="bg-zinc-50 rounded-lg py-1.5 px-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Target Cut</div>
                                <div className="text-sm font-bold text-emerald-600 tabular-nums">{vTaperPlan.target} kcal</div>
                              </div>
                            </div>

                            {/* Target Macros Breakdown */}
                            <div className="bg-rose-50/60 border border-rose-100 rounded-xl p-2.5 space-y-1.5">
                              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-rose-700">
                                <span>Target Macros (Kinobody Standard)</span>
                                <span className="font-semibold text-rose-500 normal-case tracking-normal">0.82g protein/lb bodyweight</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-white rounded-lg py-1.5 px-2 border border-rose-200/60 shadow-xs">
                                  <span className="text-zinc-400 block text-[10px] font-bold uppercase">🥩 Protein</span>
                                  <span className="text-zinc-900 font-bold text-sm tabular-nums">{vTaperPlan.macros.protein}g</span>
                                </div>
                                <div className="bg-white rounded-lg py-1.5 px-2 border border-rose-200/60 shadow-xs">
                                  <span className="text-zinc-400 block text-[10px] font-bold uppercase">🥑 Fats (~28%)</span>
                                  <span className="text-zinc-900 font-bold text-sm tabular-nums">{vTaperPlan.macros.fat}g</span>
                                </div>
                                <div className="bg-white rounded-lg py-1.5 px-2 border border-rose-200/60 shadow-xs">
                                  <span className="text-zinc-400 block text-[10px] font-bold uppercase">🍚 Carbs</span>
                                  <span className="text-zinc-900 font-bold text-sm tabular-nums">{vTaperPlan.macros.carbs}g</span>
                                </div>
                              </div>
                            </div>

                            {/* 4-Meal Distribution Budgets (No Fasting) */}
                            <div className="bg-zinc-50 border border-zinc-200/80 rounded-xl p-2.5 space-y-1.5">
                              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                <span>Daily Meal Budget Breakdown (4 Meals · No Fasting)</span>
                                <span className="text-zinc-400 font-semibold lowercase tracking-normal">target: {vTaperPlan.target} kcal</span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-center text-xs">
                                <div className="bg-white border border-zinc-200 p-1.5 rounded-lg">
                                  <div className="text-[10px] text-zinc-400 font-bold uppercase">🍳 Breakfast (~25%)</div>
                                  <div className="font-bold text-zinc-700 text-xs">~{vTaperPlan.mealBudgets.breakfast} kcal</div>
                                </div>
                                <div className="bg-white border border-zinc-200 p-1.5 rounded-lg">
                                  <div className="text-[10px] text-zinc-400 font-bold uppercase">🥗 Lunch (~30%)</div>
                                  <div className="font-bold text-zinc-700 text-xs">~{vTaperPlan.mealBudgets.lunch} kcal</div>
                                </div>
                                <div className="bg-white border border-zinc-200 p-1.5 rounded-lg">
                                  <div className="text-[10px] text-zinc-400 font-bold uppercase">🍎 Snack (~15%)</div>
                                  <div className="font-bold text-zinc-700 text-xs">~{vTaperPlan.mealBudgets.snack} kcal</div>
                                </div>
                                <div className="bg-white border border-zinc-200 p-1.5 rounded-lg">
                                  <div className="text-[10px] text-zinc-400 font-bold uppercase">🥩 Dinner (~30%)</div>
                                  <div className="font-bold text-zinc-700 text-xs">~{vTaperPlan.mealBudgets.dinner} kcal</div>
                                </div>
                              </div>
                            </div>

                            {vTaperPlan.isCrashDiet && (
                              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-2.5 text-xs flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-bold">Kinobody Crash Diet Warning (&lt;1,400 kcal):</span> Targets below 1,400 kcal cause muscle wasting and testosterone drop. For 67 kg, 1,800 kcal (18% deficit) is the exact sweet spot.
                                </div>
                              </div>
                            )}

                            <p className="text-[11px] text-zinc-500 leading-relaxed">
                              Target {vTaperPlan.target} kcal = maintenance {vTaperPlan.tdee} kcal ({vTaperPlan.weightLbs} lbs × 15) − {vTaperPlan.deficit} kcal/day ≈ {vTaperPlan.weeklyFatLossKg} kg fat/week (Kinobody Standard). Preserves shoulder/back muscle while the waist tightens.
                            </p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <CloudSyncLine
                            className="mr-auto basis-full sm:basis-auto"
                            status={cloudSaveState.status}
                            detail={cloudSaveState.message}
                            saving="Syncing…"
                            error="Sync failed — retries on next change"
                            idle="Synced to Firebase"
                          />
                          <button
                            type="button"
                            onClick={() => saveHealthProfile()}
                            className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" /> Save health profile
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                      {/* Export logged meals for LLM audit */}
                      <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600"><Download className="w-4 h-4" /></div>
                            <div>
                              <h3 className="text-sm font-bold text-zinc-800">Export meal log</h3>
                              <p className="text-[11px] text-zinc-500">Download a JSON file with your meals, health profile &amp; body stats for LLM review</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const plan = healthProfile.weightGoal === 'v_taper'
                                ? computeVTaperPlan(healthProfile)
                                : null;
                              const targetKcal = plan ? plan.target : healthProfile.dailyCalorieTarget;
                              const targetP = plan ? plan.macros.protein : null;
                              const targetC = plan ? plan.macros.carbs : null;
                              const targetF = plan ? plan.macros.fat : null;

                              const payload = {
                                exportedAt: new Date().toISOString(),
                                app: 'Life Tracker',
                                version: 'v1.7.5',
                                nutritionSummary: {
                                  formula: 'kinobody',
                                  maintenanceTdeeKcal: plan ? plan.tdee : null,
                                  dailyCalorieTargetKcal: targetKcal,
                                  dailyDeficitKcal: plan ? plan.deficit : null,
                                  distributionStrategy: '4-Meal Non-Fasting Pacing',
                                  targetMacros: {
                                    proteinGrams: targetP,
                                    carbsGrams: targetC,
                                    fatGrams: targetF
                                  },
                                  mealBudgetsKcal: plan ? plan.mealBudgets : null
                                },
                                healthProfile: {
                                  conditions: healthProfile.conditions || '',
                                  heightCm: healthProfile.heightCm,
                                  weightKg: healthProfile.weightKg,
                                  age: healthProfile.age,
                                  sex: healthProfile.sex,
                                  activityLevel: healthProfile.activityLevel,
                                  weightGoal: healthProfile.weightGoal,
                                  customDeficit: healthProfile.customDeficit ?? 400,
                                  dailyCalorieTarget: healthProfile.dailyCalorieTarget
                                },
                                vTaperPlan: plan ? {
                                  formula: 'kinobody',
                                  weightLbs: plan.weightLbs,
                                  tdee: plan.tdee,
                                  deficit: plan.deficit,
                                  deficitPct: plan.deficitPct,
                                  target: plan.target,
                                  weeklyFatLossKg: plan.weeklyFatLossKg,
                                  macros: plan.macros,
                                  mealBudgets: plan.mealBudgets,
                                  isCrashDiet: plan.isCrashDiet
                                } : null,
                                savedMealsDatabase: listRememberedCalories(liveCalorieMemory()).map((entry) => ({
                                  name: entry.name,
                                  calories: entry.calories,
                                  protein: entry.protein ?? null,
                                  carbs: entry.carbs ?? null,
                                  fat: entry.fat ?? null,
                                  portion: entry.portion,
                                  mealType: entry.mealType,
                                  timesUsed: entry.timesUsed
                                })),
                                mealLog: (() => {
                                  const sorted = Object.entries(mealLog).sort(([a], [b]) => b.localeCompare(a));
                                  const mem = liveCalorieMemory();
                                  const out = {};
                                  sorted.forEach(([dateKey, rawMeals]) => {
                                    // Tombstones carry no name, which the protein
                                    // fallback below would look up as undefined.
                                    const meals = Array.isArray(rawMeals)
                                      ? rawMeals.filter((m) => m?.deleted !== true)
                                      : [];
                                    if (!meals.length) return;
                                    const dayCals = meals.reduce((s, m) => s + (Number(m.calories) || 0), 0);
                                    const dayProtein = clampMacro(meals.reduce((s, m) => {
                                      if (Number.isFinite(Number(m.protein))) return s + Number(m.protein);
                                      const fb = resolveMealCalories(m.name, m.mealType, 'regular', mem);
                                      return s + (Number.isFinite(Number(fb?.protein)) ? Number(fb.protein) : 0);
                                    }, 0)) || 0;
                                    const dayCarbs = clampMacro(meals.reduce((s, m) => {
                                      if (Number.isFinite(Number(m.carbs))) return s + Number(m.carbs);
                                      const fb = resolveMealCalories(m.name, m.mealType, 'regular', mem);
                                      return s + (Number.isFinite(Number(fb?.carbs)) ? Number(fb.carbs) : 0);
                                    }, 0)) || 0;
                                    const dayFat = clampMacro(meals.reduce((s, m) => {
                                      if (Number.isFinite(Number(m.fat))) return s + Number(m.fat);
                                      const fb = resolveMealCalories(m.name, m.mealType, 'regular', mem);
                                      return s + (Number.isFinite(Number(fb?.fat)) ? Number(fb.fat) : 0);
                                    }, 0)) || 0;
                                    const dayStepEntry = (lifestyleRef.current.stepLog || stepLog)[dateKey];
                                    const steps = Number(dayStepEntry?.steps) || 0;
                                    const stepBurn = stepsToCalories(steps);

                                    out[dateKey] = {
                                      totalCalories: dayCals,
                                      totalProteinGrams: dayProtein,
                                      totalCarbsGrams: dayCarbs,
                                      totalFatGrams: dayFat,
                                      steps,
                                      stepCaloriesBurned: stepBurn,
                                      netCalories: dayCals - stepBurn,
                                      dailyTargets: {
                                        calories: targetKcal,
                                        protein: targetP,
                                        carbs: targetC,
                                        fat: targetF
                                      },
                                      varianceFromTarget: {
                                        calories: dayCals - targetKcal,
                                        protein: targetP ? dayProtein - targetP : null,
                                        carbs: targetC ? dayCarbs - targetC : null,
                                        fat: targetF ? dayFat - targetF : null
                                      },
                                      meals: meals.map((m) => {
                                        const fb = (m.protein == null || m.carbs == null || m.fat == null)
                                          ? resolveMealCalories(m.name, m.mealType, 'regular', mem)
                                          : null;
                                        const p = Number.isFinite(Number(m.protein)) ? Number(m.protein) : (clampMacro(fb?.protein) ?? null);
                                        const c = Number.isFinite(Number(m.carbs)) ? Number(m.carbs) : (clampMacro(fb?.carbs) ?? null);
                                        const f = Number.isFinite(Number(m.fat)) ? Number(m.fat) : (clampMacro(fb?.fat) ?? null);
                                        return {
                                          id: m.id,
                                          name: m.name,
                                          calories: m.calories,
                                          protein: p,
                                          carbs: c,
                                          fat: f,
                                          macrosSource: (m.protein == null && (fb?.protein != null || fb?.carbs != null || fb?.fat != null)) ? 'fallback-saved' : 'logged',
                                          mealType: m.mealType,
                                          caloriesSource: m.caloriesSource,
                                          caloriesNote: m.caloriesNote || undefined,
                                          createdAt: m.createdAt || undefined
                                        };
                                      })
                                    };
                                  });
                                  return out;
                                })()
                              };
                              const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `life-tracker-meals-${new Date().toISOString().slice(0, 10)}.json`;
                              a.click();
                              URL.revokeObjectURL(url);
                              showNotification('Meal log exported — share the JSON with any LLM for a nutrition audit');
                            }}
                            className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" /> Export JSON
                          </button>
                        </div>
                      </div>

                      {/* Day navigator + calorie summary */}
                      <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setMealDateOffset(o => o - 1)}
                            className="p-2 hover:bg-white rounded-lg text-zinc-600 border border-transparent hover:border-zinc-200 transition-colors"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <div className="text-center">
                            <div className="text-sm font-bold text-zinc-800">{mealDateLabel}</div>
                            <div className="text-[11px] text-zinc-500">{mealDateKey}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setMealDateOffset(o => Math.min(o + 1, 7))}
                            className="p-2 hover:bg-white rounded-lg text-zinc-600 border border-transparent hover:border-zinc-200 transition-colors"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>

                        {mealDateOffset !== 0 && (
                          <button
                            type="button"
                            onClick={() => setMealDateOffset(0)}
                            className="w-full text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                          >
                            Jump to today
                          </button>
                        )}

                        <div className="flex items-end justify-between gap-3">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                              {stepCalories > 0 ? 'Net calories today' : 'Calories today'}
                            </div>
                            <div className="text-2xl font-bold text-zinc-900 tabular-nums">
                              {caloriesToday}
                              <span className="text-sm font-semibold text-zinc-400"> / {target}</span>
                            </div>
                            {stepCalories > 0 && (
                              <div className="text-[11px] text-zinc-500 mt-0.5">
                                {caloriesEaten} eaten − {stepCalories} from {daySteps.toLocaleString()} steps
                              </div>
                            )}
                            {vTaperPlan && (
                              <div className={`text-[11px] font-semibold mt-0.5 ${vTaperDeficit >= vTaperPlan.deficit ? 'text-emerald-600' : vTaperDeficit >= 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                                {vTaperDeficit >= 0
                                  ? `V-taper deficit today: ${vTaperDeficit} / ${vTaperPlan.deficit} kcal (maintenance ≈ ${vTaperPlan.tdee})`
                                  : `V-taper: ${Math.abs(vTaperDeficit)} kcal over maintenance (≈ ${vTaperPlan.tdee})`}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-bold tabular-nums ${over ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {over ? `${Math.abs(remaining)} over` : `${remaining} left`}
                            </div>
                            {vTaperPlan && !over && (
                              <div className="text-[10px] font-semibold text-emerald-600/70 mt-0.5">
                                incl. {vTaperPlan.deficit} kcal deficit ✓
                              </div>
                            )}
                            {vTaperPlan && over && (
                              <div className="text-[10px] font-semibold text-rose-500/70 mt-0.5">
                                {vTaperPlan.deficit} kcal deficit target exceeded
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                            Steps today <span className="font-semibold normal-case tracking-normal">(optional)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Footprints className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                              <input
                                type="number"
                                min="0"
                                max="200000"
                                step="100"
                                inputMode="numeric"
                                placeholder="e.g. 7000"
                                value={stepDraft}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setStepDraft(val);
                                  touchLifestyleEdit();
                                  const trimmed = String(val ?? '').trim();
                                  const stepsNum = trimmed === '' ? 0 : Math.max(0, Math.min(200000, Math.round(Number(trimmed) || 0)));
                                  const d = new Date();
                                  d.setHours(12, 0, 0, 0);
                                  d.setDate(d.getDate() + mealDateOffset);
                                  const key = d.toISOString().split('T')[0];
                                  const base = { ...(lifestyleRef.current.stepLog || stepLog) };
                                  if (stepsNum <= 0) {
                                    delete base[key];
                                  } else {
                                    base[key] = { steps: stepsNum, updatedAt: new Date().toISOString() };
                                  }
                                  lifestyleRef.current = { ...lifestyleRef.current, stepLog: base };
                                }}
                                onBlur={(e) => saveDaySteps(mealDateKey, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                  }
                                }}
                                className="w-full bg-white border border-zinc-200 rounded-xl py-2.5 pl-10 pr-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => saveDaySteps(mealDateKey, stepDraft)}
                              className="px-3 py-2.5 bg-white border border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50 text-zinc-700 rounded-xl text-xs font-semibold"
                            >
                              Apply
                            </button>
                          </div>
                          <p className="text-[10px] text-zinc-400 mt-1">
                            7,000 steps ≈ {stepsToCalories(7000)} kcal burned. Enter today’s steps to reassess calories today.
                          </p>
                        </div>

                        <div className="h-2.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${over ? 'bg-rose-500' : 'bg-emerald-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>

                        {/* Daily Macro Progress */}
                        {(() => {
                          const proteinEaten = clampMacro(dayMeals.reduce((s, m) => {
                            if (Number.isFinite(Number(m.protein))) return s + Number(m.protein);
                            const fb = resolveMealCalories(m.name, m.mealType, 'regular', liveCalorieMemory());
                            return s + (Number.isFinite(Number(fb?.protein)) ? Number(fb.protein) : 0);
                          }, 0)) || 0;
                          const carbsEaten = clampMacro(dayMeals.reduce((s, m) => {
                            if (Number.isFinite(Number(m.carbs))) return s + Number(m.carbs);
                            const fb = resolveMealCalories(m.name, m.mealType, 'regular', liveCalorieMemory());
                            return s + (Number.isFinite(Number(fb?.carbs)) ? Number(fb.carbs) : 0);
                          }, 0)) || 0;
                          const fatEaten = clampMacro(dayMeals.reduce((s, m) => {
                            if (Number.isFinite(Number(m.fat))) return s + Number(m.fat);
                            const fb = resolveMealCalories(m.name, m.mealType, 'regular', liveCalorieMemory());
                            return s + (Number.isFinite(Number(fb?.fat)) ? Number(fb.fat) : 0);
                          }, 0)) || 0;

                          const targetProtein = vTaperPlan?.macros?.protein || Math.round((healthProfile.weightKg || 67) * 2.20462 * 0.82);
                          const targetCarbs = vTaperPlan?.macros?.carbs || Math.max(0, Math.round((target - targetProtein * 4 - (target * 0.28)) / 4));
                          const targetFat = vTaperPlan?.macros?.fat || Math.round((target * 0.28) / 9);

                          const proteinPct = Math.min(100, Math.round((proteinEaten / (targetProtein || 1)) * 100));
                          const carbsPct = Math.min(100, Math.round((carbsEaten / (targetCarbs || 1)) * 100));
                          const fatPct = Math.min(100, Math.round((fatEaten / (targetFat || 1)) * 100));

                          return (
                            <div className="pt-3 border-t border-zinc-200/80 space-y-2">
                              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                <span>Daily Macro Progress</span>
                                <span className="font-semibold normal-case text-zinc-400">Target: 🥩 {targetProtein}g · 🍚 {targetCarbs}g · 🥑 {targetFat}g</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <div className="bg-white border border-rose-100 rounded-xl p-2.5 space-y-1.5 shadow-2xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">🥩 Protein</span>
                                    <span className="text-xs font-bold text-zinc-800 tabular-nums">{proteinEaten} / {targetProtein}g</span>
                                  </div>
                                  <div className="h-1.5 w-full bg-rose-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${proteinEaten >= targetProtein ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                      style={{ width: `${proteinPct}%` }}
                                    />
                                  </div>
                                </div>
                                <div className="bg-white border border-amber-100 rounded-xl p-2.5 space-y-1.5 shadow-2xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">🍚 Carbs</span>
                                    <span className="text-xs font-bold text-zinc-800 tabular-nums">{carbsEaten} / {targetCarbs}g</span>
                                  </div>
                                  <div className="h-1.5 w-full bg-amber-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${carbsEaten > targetCarbs ? 'bg-amber-600' : 'bg-amber-400'}`}
                                      style={{ width: `${carbsPct}%` }}
                                    />
                                  </div>
                                </div>
                                <div className="bg-white border border-sky-100 rounded-xl p-2.5 space-y-1.5 shadow-2xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">🥑 Fat</span>
                                    <span className="text-xs font-bold text-zinc-800 tabular-nums">{fatEaten} / {targetFat}g</span>
                                  </div>
                                  <div className="h-1.5 w-full bg-sky-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${fatEaten > targetFat ? 'bg-rose-400' : 'bg-sky-500'}`}
                                      style={{ width: `${fatPct}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Log meal form — saved calories first, then SG food table */}
                      <form onSubmit={addMeal} className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-3 shadow-sm">
                        {(() => {
                          const liveEstimate = mealForm.name.trim()
                            ? estimateMealCalories(mealForm.name, mealForm.mealType, mealForm.portion || 'regular')
                            : null;
                          const shownCalories = Number.isFinite(Number(mealForm.caloriesOverride))
                            ? mealForm.caloriesOverride
                            : (liveEstimate?.calories ?? '');
                          const source = Number.isFinite(Number(mealForm.caloriesOverride))
                            ? 'manual'
                            : (liveEstimate?.source || '');
                          const rememberedNames = rememberedMealNames(liveCalorieMemory());
                          // Deduplicate case-insensitive entries to avoid similar duplicates in suggestions
                          const seenSuggestions = new Set();
                          const foodSuggestions = [...rememberedNames, ...SG_FOOD_NAMES].filter((name) => {
                            if (!name || typeof name !== 'string') return false;
                            const lower = name.toLowerCase().trim();
                            if (seenSuggestions.has(lower)) return false;
                            seenSuggestions.add(lower);
                            return true;
                          });

                          const rawReuseHits = mealForm.name.trim()
                            ? suggestRememberedMeals(mealForm.name, liveCalorieMemory(), liveMealLog(), 5)
                            : [];
                          // Deduplicate reuse hits
                          const seenReuseHits = new Set();
                          const reuseHits = rawReuseHits.filter((hit) => {
                            const key = `${hit.name.toLowerCase().trim()}-${hit.calories}`;
                            if (seenReuseHits.has(key)) return false;
                            seenReuseHits.add(key);
                            return true;
                          });
                          const sourceMeta = {
                            saved: { label: 'Remembered', box: 'border-sky-200 bg-sky-50/80', text: 'text-sky-700' },
                            similar: { label: 'Similar meal', box: 'border-indigo-200 bg-indigo-50/80', text: 'text-indigo-700' },
                            manual: { label: 'Your number', box: 'border-sky-200 bg-sky-50/80', text: 'text-sky-700' },
                            'sg-db': { label: 'SG food list', box: 'border-emerald-200 bg-emerald-50/80', text: 'text-emerald-700' }
                          }[source] || { label: liveEstimate?.matched ? 'Estimated' : 'No exact dish match', box: 'border-amber-200 bg-amber-50/80', text: 'text-amber-700' };
                          return (
                            <>
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <Apple className="w-4 h-4 text-emerald-600" />
                            <div>
                              <h3 className="text-sm font-bold text-zinc-800">Log a meal</h3>
                              <p className="text-[11px] text-zinc-500">
                                Reuses your saved calories for the same or similar meal. Falls back to the built-in Singapore list ({SG_FOOD_NAMES.length} dishes).
                              </p>
                            </div>
                          </div>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                            source === 'saved' || source === 'similar' || source === 'manual'
                              ? 'bg-sky-50 text-sky-700 border-sky-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          }`}>
                            {source === 'saved' || source === 'manual' ? 'Remembered' : source === 'similar' ? 'Similar' : 'SG food list'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <input
                            type="text"
                            list="sg-foods-list"
                            value={mealForm.name}
                            onChange={(e) => applyMealName(e.target.value)}
                            onInput={(e) => applyMealName(e.target.value)}
                            placeholder="e.g. chicken rice, nasi lemak, laksa, protein shake"
                            className="sm:col-span-3 bg-zinc-50 border border-zinc-200 rounded-xl py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                            disabled={estimatingCalories}
                            autoComplete="off"
                          />
                          {reuseHits.length > 0 && (
                            <div className="sm:col-span-3 flex flex-wrap gap-1.5">
                              {reuseHits.map((hit) => (
                                <button
                                  key={`${hit.key}-${hit.calories}`}
                                  type="button"
                                  onClick={() => applySavedMeal(hit)}
                                  className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
                                >
                                  {hit.name} · {hit.calories} kcal
                                </button>
                              ))}
                            </div>
                          )}
                          <datalist id="sg-foods-list">
                            {foodSuggestions.map((n) => (
                              <option key={n} value={n} />
                            ))}
                          </datalist>
                          <select
                            value={mealForm.mealType}
                            onChange={(e) => setMealForm({ ...mealForm, mealType: e.target.value })}
                            className="bg-zinc-50 border border-zinc-200 rounded-xl py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                            disabled={estimatingCalories}
                          >
                            <option value="breakfast">Breakfast</option>
                            <option value="lunch">Lunch</option>
                            <option value="dinner">Dinner</option>
                            <option value="snack">Snack</option>
                          </select>
                          <select
                            value={mealForm.portion || 'regular'}
                            onChange={(e) => {
                              const nextPortion = e.target.value;
                              const est = mealForm.name.trim()
                                ? estimateMealCalories(mealForm.name, mealForm.mealType, nextPortion)
                                : null;
                              const reuse = est && (est.source === 'saved' || est.source === 'similar');

                              setMealForm({
                                ...mealForm,
                                portion: nextPortion,
                                caloriesOverride: reuse && !mealForm.isManual ? est.calories : mealForm.caloriesOverride,
                                protein: reuse && !mealForm.isManual && est.protein !== null && est.protein !== undefined ? String(est.protein) : mealForm.protein,
                                carbs: reuse && !mealForm.isManual && est.carbs !== null && est.carbs !== undefined ? String(est.carbs) : mealForm.carbs,
                                fat: reuse && !mealForm.isManual && est.fat !== null && est.fat !== undefined ? String(est.fat) : mealForm.fat
                              });
                            }}
                            className="bg-zinc-50 border border-zinc-200 rounded-xl py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                            disabled={estimatingCalories}
                          >
                            <option value="small">Small portion</option>
                            <option value="regular">Regular portion</option>
                            <option value="large">Large portion</option>
                            <option value="xl">XL / second helping</option>
                          </select>
                          <button
                            type="submit"
                            disabled={!mealForm.name.trim() || estimatingCalories}
                            className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center justify-center gap-2"
                          >
                            {estimatingCalories ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" /> Logging…
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4" /> Log meal
                              </>
                            )}
                          </button>
                        </div>

                        {/* Optional Macros Inputs */}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Protein (g)</label>
                            <input
                              type="number"
                              min="0"
                              max="500"
                              step="any"
                              placeholder="e.g. 35"
                              value={mealForm.protein}
                              onChange={(e) => setMealForm({ ...mealForm, protein: e.target.value, isManual: true })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                              disabled={estimatingCalories}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Carbs (g)</label>
                            <input
                              type="number"
                              min="0"
                              max="500"
                              step="any"
                              placeholder="e.g. 45"
                              value={mealForm.carbs}
                              onChange={(e) => setMealForm({ ...mealForm, carbs: e.target.value, isManual: true })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                              disabled={estimatingCalories}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Fat (g)</label>
                            <input
                              type="number"
                              min="0"
                              max="300"
                              step="any"
                              placeholder="e.g. 15"
                              value={mealForm.fat}
                              onChange={(e) => setMealForm({ ...mealForm, fat: e.target.value, isManual: true })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                              disabled={estimatingCalories}
                            />
                          </div>
                        </div>

                        {liveEstimate && (
                          <div className={`rounded-xl border px-3 py-2.5 ${sourceMeta.box}`}>
                            <div className={`text-[10px] font-bold uppercase tracking-wider ${sourceMeta.text}`}>
                              {sourceMeta.label}
                            </div>
                            <div className="flex flex-wrap items-end gap-2 mt-1">
                              <label className="flex items-baseline gap-1.5">
                                <input
                                  type="number"
                                  min="0"
                                  max="8000"
                                  step="1"
                                  value={shownCalories}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') {
                                      setMealForm({ ...mealForm, caloriesOverride: 0, isManual: true });
                                      return;
                                    }
                                    const n = Math.round(Number(raw));
                                    setMealForm({
                                      ...mealForm,
                                      caloriesOverride: Number.isFinite(n) ? Math.max(0, Math.min(8000, n)) : liveEstimate.calories,
                                      isManual: true
                                    });
                                  }}
                                  className={`w-28 bg-white border rounded-lg py-1 px-2 text-xl font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${sourceMeta.text}`}
                                />
                                <span className="text-sm font-semibold opacity-70">kcal</span>
                              </label>
                              {Number.isFinite(liveEstimate.kcalMin) && Number.isFinite(liveEstimate.kcalMax) && !Number.isFinite(Number(mealForm.caloriesOverride)) && (
                                <span className="text-xs font-semibold opacity-70">
                                  {liveEstimate.kcalMin}–{liveEstimate.kcalMax}
                                </span>
                              )}
                            </div>
                            <p className={`text-[11px] mt-0.5 break-words ${sourceMeta.text} opacity-80`}>
                              {Number.isFinite(Number(mealForm.caloriesOverride))
                                ? `Will save ${shownCalories} kcal for “${mealForm.name.trim()}” and reuse it next time.`
                                : liveEstimate.note}
                            </p>
                          </div>
                        )}
                        <p className="text-[10px] text-zinc-400">
                          Type a dish — calories appear immediately. Edit the number or macros to customize your meal. Combine items with “and”: “chicken rice and teh peng”.
                        </p>
                            </>
                          );
                        })()}
                      </form>

                      {/* Meal list */}
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-sm font-bold text-zinc-800">
                            Logged meals ({dayMeals.length})
                          </h3>
                          {dayMeals.length > 1 && (
                            <div className="flex items-center gap-1">
                              {[
                                { id: 'logged', label: 'Logged' },
                                { id: 'high', label: 'Highest kcal' },
                                { id: 'low', label: 'Lowest kcal' }
                              ].map((opt) => (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => setMealSort(opt.id)}
                                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
                                    mealSort === opt.id
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {dayMeals.length === 0 ? (
                          <div className="text-center py-8 text-zinc-400 text-sm border border-dashed border-zinc-200 rounded-2xl">
                            No meals logged for this day yet.
                          </div>
                        ) : (
                          sortedMeals.map((meal) => {
                            const isEditing = editingMeal?.dateKey === mealDateKey && String(editingMeal?.id) === String(meal.id);
                            const sourceStyle =
                              meal.caloriesSource === 'saved' || meal.caloriesSource === 'manual'
                                ? 'bg-sky-50 text-sky-700 border-sky-200'
                                : meal.caloriesSource === 'similar'
                                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                  : meal.caloriesSource === 'sg-db'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : meal.caloriesSource === 'gemini'
                                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                      : meal.caloriesSource === 'deepseek'
                                        ? 'bg-violet-50 text-violet-700 border-violet-200'
                                        : 'bg-zinc-50 text-zinc-500 border-zinc-200';
                            const sourceLabel =
                              meal.caloriesSource === 'saved' || meal.caloriesSource === 'manual'
                                ? 'Saved'
                                : meal.caloriesSource === 'similar'
                                  ? 'Similar'
                                  : meal.caloriesSource === 'sg-db'
                                    ? 'SG list'
                                    : meal.caloriesSource === 'gemini'
                                      ? 'Gemini'
                                      : meal.caloriesSource === 'deepseek'
                                        ? 'DeepSeek'
                                        : 'App est.';
                            return (
                            <div
                              key={meal.id}
                              className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 bg-white hover:border-zinc-300 transition-colors"
                            >
                              {isEditing ? (
                                <form
                                  className="flex-1 min-w-0 flex flex-wrap items-center gap-2"
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    updateMealCalories(mealDateKey, meal.id, editingMeal.calories, editingMeal.name, editingMeal.protein, editingMeal.carbs, editingMeal.fat);
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={editingMeal.name}
                                    onChange={(e) => setEditingMeal({ ...editingMeal, name: e.target.value })}
                                    className="flex-1 min-w-[10rem] bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-sm font-semibold text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                    placeholder="Meal name"
                                    aria-label="Meal name"
                                    autoFocus
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    max="8000"
                                    value={editingMeal.calories}
                                    onChange={(e) => setEditingMeal({ ...editingMeal, calories: e.target.value })}
                                    className="w-20 bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-sm font-bold tabular-nums text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                    placeholder="kcal"
                                    aria-label="Calories"
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    max="500"
                                    step="any"
                                    value={editingMeal.protein ?? ''}
                                    onChange={(e) => setEditingMeal({ ...editingMeal, protein: e.target.value })}
                                    className="w-16 bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-xs font-semibold tabular-nums text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                    placeholder="P (g)"
                                    aria-label="Protein (g)"
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    max="500"
                                    step="any"
                                    value={editingMeal.carbs ?? ''}
                                    onChange={(e) => setEditingMeal({ ...editingMeal, carbs: e.target.value })}
                                    className="w-16 bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-xs font-semibold tabular-nums text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                    placeholder="C (g)"
                                    aria-label="Carbs (g)"
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    max="300"
                                    step="any"
                                    value={editingMeal.fat ?? ''}
                                    onChange={(e) => setEditingMeal({ ...editingMeal, fat: e.target.value })}
                                    className="w-16 bg-zinc-50 border border-zinc-200 rounded-lg py-1 px-2 text-xs font-semibold tabular-nums text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                                    placeholder="F (g)"
                                    aria-label="Fat (g)"
                                  />
                                  <button type="submit" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Save name and calories">
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingMeal(null)}
                                    className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded-lg"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </form>
                              ) : (
                                <>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setEditingMeal({ dateKey: mealDateKey, id: meal.id, name: meal.name, calories: meal.calories, protein: meal.protein ?? '', carbs: meal.carbs ?? '', fat: meal.fat ?? '' })}
                                        className="font-semibold text-sm text-zinc-800 break-words text-left hover:text-indigo-700"
                                        title="Edit meal name"
                                      >
                                        {meal.name}
                                      </button>
                                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${mealTypeColors[meal.mealType] || mealTypeColors.snack}`}>
                                        {meal.mealType}
                                      </span>
                                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${sourceStyle}`}>
                                        {sourceLabel}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => applySavedMeal(meal)}
                                        className="text-[10px] font-semibold text-sky-700 hover:text-sky-800"
                                        title="Reuse this meal and its calories in the log form"
                                      >
                                        Reuse
                                      </button>
                                    </div>
                                    {(() => {
                                      const p = (meal.protein != null && meal.protein !== '') ? clampMacro(meal.protein) : null;
                                      const c = (meal.carbs != null && meal.carbs !== '') ? clampMacro(meal.carbs) : null;
                                      const f = (meal.fat != null && meal.fat !== '') ? clampMacro(meal.fat) : null;
                                      const fallback = (p === null && c === null && f === null)
                                        ? resolveMealCalories(meal.name, meal.mealType, 'regular', liveCalorieMemory())
                                        : null;
                                      const showP = p !== null ? p : (fallback?.protein != null ? clampMacro(fallback.protein) : null);
                                      const showC = c !== null ? c : (fallback?.carbs != null ? clampMacro(fallback.carbs) : null);
                                      const showF = f !== null ? f : (fallback?.fat != null ? clampMacro(fallback.fat) : null);

                                      if (showP === null && showC === null && showF === null) return null;
                                      return (
                                        <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px] font-medium text-zinc-600">
                                          {showP !== null ? <span className="bg-rose-50 border border-rose-200 text-rose-700 px-1.5 py-0.5 rounded">🥩 {showP}g P</span> : null}
                                          {showC !== null ? <span className="bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded">🍚 {showC}g C</span> : null}
                                          {showF !== null ? <span className="bg-sky-50 border border-sky-200 text-sky-700 px-1.5 py-0.5 rounded">🥑 {showF}g F</span> : null}
                                        </div>
                                      );
                                    })()}
                                    {meal.caloriesNote && (
                                      <p className="text-[10px] text-zinc-400 mt-0.5 break-words" title={meal.caloriesNote}>
                                        {meal.caloriesNote}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <div className="text-sm font-bold tabular-nums text-zinc-700">
                                      {meal.calories} <span className="text-[10px] font-semibold text-zinc-400">kcal</span>
                                    </div>
                                    {Number.isFinite(Number(meal.caloriesMin)) && Number.isFinite(Number(meal.caloriesMax)) && (
                                      <div className="text-[10px] text-zinc-400 tabular-nums">
                                        {meal.caloriesMin}–{meal.caloriesMax}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setEditingMeal({ dateKey: mealDateKey, id: meal.id, name: meal.name, calories: meal.calories, protein: meal.protein ?? '', carbs: meal.carbs ?? '', fat: meal.fat ?? '' })}
                                    className="p-1.5 text-zinc-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title="Edit name and calories"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => deleteMeal(mealDateKey, meal.id)}
                                className="p-1.5 text-zinc-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                title="Delete meal"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            );
                          })
                        )}
                      </div>

                      {/* Saved calorie cache */}
                      {(() => {
                        const remembered = listRememberedCalories(calorieMemory);
                        return (
                          <div className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setShowCalorieMemory((v) => !v)}
                                className="flex items-center gap-2 text-left"
                              >
                                {showCalorieMemory ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                                <div>
                                  <h3 className="text-sm font-bold text-zinc-800">Saved calorie counts ({remembered.length})</h3>
                                  <p className="text-[11px] text-zinc-500">
                                    Cached in Firebase and local storage. Click a name or the pencil to edit the name and calories.
                                  </p>
                                </div>
                              </button>
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={overwriteWithGlmCorrectedMeals}
                                  disabled={mergingGlmMeals}
                                  className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm transition-colors"
                                  title="Pulls the latest saved meals from Firebase, then fills any gaps from the GLM 5.2 list and repairs all-zero macros. Never overwrites a calorie count you set."
                                >
                                  <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                                  {mergingGlmMeals ? 'Syncing…' : `Sync + merge GLM 5.2 (${GLM_CORRECTED_SAVED_MEALS.length} items)`}
                                </button>
                                {remembered.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={downloadCalorieMemoryJson}
                                    className="px-3 py-1.5 text-xs font-semibold text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 flex items-center gap-1.5"
                                  >
                                    <Download className="w-3.5 h-3.5" /> Download JSON
                                  </button>
                                )}
                              </div>
                            </div>
                            {showCalorieMemory && (
                              remembered.length === 0 ? (
                                <p className="text-[12px] text-zinc-400">
                                  Log a meal or edit a calorie number to start a reusable cache.
                                </p>
                              ) : (
                                <div className="space-y-2">
                                  {remembered.map((entry) => {
                                    const isEditing = editingMemoryKey === entry.key;
                                    return (
                                      <div key={entry.key} className="flex items-center gap-3 p-2.5 rounded-xl border border-zinc-100 bg-zinc-50/70">
                                        {isEditing ? (
                                          <form
                                            className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5"
                                            onSubmit={(e) => {
                                              e.preventDefault();
                                              saveRememberedCalories(
                                                entry.key,
                                                editingMemoryCalories,
                                                editingMemoryName,
                                                editingMemoryProtein,
                                                editingMemoryCarbs,
                                                editingMemoryFat
                                              );
                                            }}
                                          >
                                            <input
                                              type="text"
                                              value={editingMemoryName}
                                              onChange={(e) => setEditingMemoryName(e.target.value)}
                                              className="flex-1 min-w-[9rem] bg-white border border-zinc-200 rounded-lg py-1 px-2 text-sm font-semibold text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                              placeholder="Meal name"
                                              aria-label="Saved meal name"
                                              autoFocus
                                            />
                                            <input
                                              type="number"
                                              min="0"
                                              max="8000"
                                              value={editingMemoryCalories}
                                              onChange={(e) => setEditingMemoryCalories(e.target.value)}
                                              className="w-18 bg-white border border-zinc-200 rounded-lg py-1 px-2 text-sm font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                              placeholder="kcal"
                                              aria-label="Saved calories"
                                            />
                                            <input
                                              type="number"
                                              min="0"
                                              max="500"
                                              step="any"
                                              value={editingMemoryProtein}
                                              onChange={(e) => setEditingMemoryProtein(e.target.value)}
                                              className="w-14 bg-white border border-zinc-200 rounded-lg py-1 px-1.5 text-xs font-semibold text-rose-700 tabular-nums focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                              placeholder="P (g)"
                                              aria-label="Protein (g)"
                                            />
                                            <input
                                              type="number"
                                              min="0"
                                              max="500"
                                              step="any"
                                              value={editingMemoryCarbs}
                                              onChange={(e) => setEditingMemoryCarbs(e.target.value)}
                                              className="w-14 bg-white border border-zinc-200 rounded-lg py-1 px-1.5 text-xs font-semibold text-amber-700 tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                              placeholder="C (g)"
                                              aria-label="Carbs (g)"
                                            />
                                            <input
                                              type="number"
                                              min="0"
                                              max="300"
                                              step="any"
                                              value={editingMemoryFat}
                                              onChange={(e) => setEditingMemoryFat(e.target.value)}
                                              className="w-14 bg-white border border-zinc-200 rounded-lg py-1 px-1.5 text-xs font-semibold text-sky-700 tabular-nums focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                                              placeholder="F (g)"
                                              aria-label="Fat (g)"
                                            />
                                            <button type="submit" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Save name, calories and macros">
                                              <Save className="w-4 h-4" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingMemoryKey(null);
                                                setEditingMemoryCalories('');
                                                setEditingMemoryName('');
                                                setEditingMemoryProtein('');
                                                setEditingMemoryCarbs('');
                                                setEditingMemoryFat('');
                                              }}
                                              className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded-lg"
                                              title="Cancel"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </form>
                                        ) : (
                                          <>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex flex-wrap items-center gap-1.5">
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setEditingMemoryKey(entry.key);
                                                    setEditingMemoryName(entry.name);
                                                    setEditingMemoryCalories(String(entry.calories));
                                                    setEditingMemoryProtein(entry.protein !== null && entry.protein !== undefined ? String(entry.protein) : '');
                                                    setEditingMemoryCarbs(entry.carbs !== null && entry.carbs !== undefined ? String(entry.carbs) : '');
                                                    setEditingMemoryFat(entry.fat !== null && entry.fat !== undefined ? String(entry.fat) : '');
                                                  }}
                                                  className="text-sm font-semibold text-zinc-800 break-words text-left hover:text-indigo-700"
                                                  title="Edit saved meal name"
                                                >
                                                  {entry.name}
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => applySavedMeal(entry)}
                                                  className="text-[10px] font-semibold text-sky-700 hover:text-sky-800"
                                                  title="Reuse this meal in log form"
                                                >
                                                  Reuse
                                                </button>
                                              </div>
                                              {(() => {
                                                const p = (entry.protein != null && entry.protein !== '') ? clampMacro(entry.protein) : null;
                                                const c = (entry.carbs != null && entry.carbs !== '') ? clampMacro(entry.carbs) : null;
                                                const f = (entry.fat != null && entry.fat !== '') ? clampMacro(entry.fat) : null;
                                                if (p === null && c === null && f === null) return null;
                                                return (
                                                  <div className="flex flex-wrap items-center gap-1 mt-1 text-[10px] font-medium text-zinc-600">
                                                    {p !== null ? <span className="bg-rose-50 border border-rose-200 text-rose-700 px-1.5 py-0.5 rounded">🥩 {p}g P</span> : null}
                                                    {c !== null ? <span className="bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded">🍚 {c}g C</span> : null}
                                                    {f !== null ? <span className="bg-sky-50 border border-sky-200 text-sky-700 px-1.5 py-0.5 rounded">🥑 {f}g F</span> : null}
                                                  </div>
                                                );
                                              })()}
                                              <div className="text-[10px] text-zinc-400 mt-0.5">
                                                Used {entry.timesUsed}× · {entry.portion} · {entry.edited ? 'edited' : 'from log'}
                                              </div>
                                            </div>
                                            <div className="text-sm font-bold tabular-nums text-zinc-700">
                                              {entry.calories} <span className="text-[10px] font-semibold text-zinc-400">kcal</span>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingMemoryKey(entry.key);
                                                setEditingMemoryName(entry.name);
                                                setEditingMemoryCalories(String(entry.calories));
                                                setEditingMemoryProtein(entry.protein !== null && entry.protein !== undefined ? String(entry.protein) : '');
                                                setEditingMemoryCarbs(entry.carbs !== null && entry.carbs !== undefined ? String(entry.carbs) : '');
                                                setEditingMemoryFat(entry.fat !== null && entry.fat !== undefined ? String(entry.fat) : '');
                                              }}
                                              className="p-1.5 text-zinc-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg"
                                              title="Edit name, calories and macros"
                                            >
                                              <Pencil className="w-4 h-4" />
                                            </button>
                                          </>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => deleteRememberedCalories(entry.key)}
                                          className="p-1.5 text-zinc-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg"
                                          title="Forget this meal"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )
                            )}
                          </div>
                        );
                      })()}

                      {/* Suggestions */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-bold text-zinc-800 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                          Suggestions for you
                        </h3>
                        {suggestions.map((tip, i) => {
                          const styles = {
                            warn: 'bg-amber-50 border-amber-200 text-amber-900',
                            health: 'bg-sky-50 border-sky-200 text-sky-900',
                            info: 'bg-zinc-50 border-zinc-200 text-zinc-800',
                            disclaimer: 'bg-zinc-100/80 border-zinc-200 text-zinc-500'
                          }[tip.type] || 'bg-zinc-50 border-zinc-200 text-zinc-800';
                          return (
                            <div key={i} className={`rounded-2xl border p-3.5 ${styles}`}>
                              <div className="flex items-start gap-2">
                                {tip.type === 'warn' || tip.type === 'disclaimer' ? (
                                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-70" />
                                ) : (
                                  <Flame className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-70" />
                                )}
                                <div>
                                  <div className="text-xs font-bold uppercase tracking-wider mb-0.5 opacity-80">{tip.title}</div>
                                  <p className="text-sm leading-relaxed">{tip.body}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {activeTab === 'money' && (
              <div className="space-y-6">
                <div className="text-center mb-2">
                  <h2 className="text-lg font-bold text-zinc-800">Money log</h2>
                  <p className="text-zinc-500 text-sm">
                    Three things you did today that made money — or moved you one step closer.
                  </p>
                  <CloudSyncLine
                    className="mt-2"
                    status={cloudSaveState.status}
                    detail={cloudSaveState.message}
                    domain="money"
                    activeDomain={cloudSaveState.domain}
                    saving="Syncing money log to Firebase…"
                    error="Money log saved on this device — Firebase sync failed"
                    idle="Money log synced to Firebase"
                  />
                </div>

                {(() => {
                  const moneyDateKey = getMoneyDateKey();
                  const items = getMoneyItemsForDate(moneyDateKey);
                  const filled = items.filter((t) => t.trim()).length;
                  const moneyDateLabel = (() => {
                    const d = new Date();
                    d.setHours(12, 0, 0, 0);
                    d.setDate(d.getDate() + moneyDateOffset);
                    if (moneyDateOffset === 0) return 'Today';
                    if (moneyDateOffset === -1) return 'Yesterday';
                    if (moneyDateOffset === 1) return 'Tomorrow';
                    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  })();
                  const placeholders = [
                    'e.g. Sent 5 outreach messages to warm leads',
                    'e.g. Shipped a paid feature / closed a small sale',
                    'e.g. Practiced a sales call or published an offer'
                  ];

                  return (
                    <>
                      <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setMoneyDateOffset((o) => o - 1)}
                            className="p-2 hover:bg-white rounded-lg text-zinc-600 border border-transparent hover:border-zinc-200 transition-colors"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <div className="text-center">
                            <div className="text-sm font-bold text-zinc-800">{moneyDateLabel}</div>
                            <div className="text-[11px] text-zinc-500">{moneyDateKey}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setMoneyDateOffset((o) => Math.min(o + 1, 7))}
                            className="p-2 hover:bg-white rounded-lg text-zinc-600 border border-transparent hover:border-zinc-200 transition-colors"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                        {moneyDateOffset !== 0 && (
                          <button
                            type="button"
                            onClick={() => setMoneyDateOffset(0)}
                            className="w-full text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                          >
                            Jump to today
                          </button>
                        )}
                        <div className="flex items-end justify-between gap-3">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Money moves</div>
                            <div className="text-2xl font-bold text-zinc-900 tabular-nums">
                              {filled}
                              <span className="text-sm font-semibold text-zinc-400"> / 3</span>
                            </div>
                          </div>
                          <div className={`text-right text-sm font-bold ${filled === 3 ? 'text-emerald-600' : 'text-zinc-500'}`}>
                            {filled === 3 ? 'Day complete' : `${3 - filled} left`}
                          </div>
                        </div>
                        <div className="h-2.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                            style={{ width: `${Math.round((filled / 3) * 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-3 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-emerald-600" />
                            <div>
                              <h3 className="text-sm font-bold text-zinc-800">Today’s 3 money moves</h3>
                              <p className="text-[11px] text-zinc-500">
                                Income, sales, outreach, shipping, learning that gets you paid. Autosaves to this device and Firebase.
                              </p>
                            </div>
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                            {cloudSaveState.status === 'saving'
                              ? 'Saving…'
                              : cloudSaveState.status === 'error'
                                ? 'Cache only'
                                : 'Cached · Firebase'}
                          </span>
                        </div>

                        {items.map((text, idx) => (
                          <div key={idx}>
                            <label className="block text-xs font-bold text-zinc-700 mb-1.5">
                              Move {idx + 1}
                            </label>
                            <textarea
                              value={text}
                              onChange={(e) => updateMoneyItem(moneyDateKey, idx, e.target.value)}
                              placeholder={placeholders[idx]}
                              rows={2}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none"
                            />
                          </div>
                        ))}

                        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                          <CloudSyncLine
                            className="mr-auto basis-full sm:basis-auto"
                            status={cloudSaveState.status}
                            detail={cloudSaveState.message}
                            saving="Syncing…"
                            error="Saved on this device — retrying on next change"
                            idle="Synced to Firebase"
                          />
                          <button
                            type="button"
                            onClick={() => saveMoneyLogNow(moneyDateKey)}
                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" /> Save money log
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {activeTab === 'integrated' && (
              <IntegratedTab
                entries={listIntegrations(integrations)}
                onSave={saveIntegration}
                onDelete={deleteIntegration}
                syncLine={(
                  <CloudSyncLine
                    className="mr-auto basis-full sm:basis-auto"
                    status={cloudSaveState.status}
                    detail={cloudSaveState.message}
                    domain="integrated"
                    activeDomain={cloudSaveState.domain}
                    saving="Syncing integrated ideas to Firebase…"
                    error="Integrated ideas saved on this device — Firebase sync failed"
                    idle="Integrated ideas synced to Firebase"
                  />
                )}
              />
            )}

            {activeTab === 'goals' && (
              <GoalsTab
                goals={listGoals(goals)}
                onSave={saveGoal}
                onToggleItem={toggleGoalItem}
                onAddItem={addGoalItem}
                onRemoveItem={deleteGoalItem}
                onDelete={deleteGoal}
                syncLine={(
                  <CloudSyncLine
                    className="mr-auto basis-full sm:basis-auto"
                    status={cloudSaveState.status}
                    detail={cloudSaveState.message}
                    domain="goals"
                    activeDomain={cloudSaveState.domain}
                    saving="Syncing goals to Firebase…"
                    error="Goals saved on this device — Firebase sync failed"
                    idle="Goals synced to Firebase"
                  />
                )}
              />
            )}

            {activeTab === 'journal' && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-lg font-bold text-zinc-800">Monthly Journal</h2>
                  <p className="text-zinc-500 text-sm">
                    Set expectations. Record what you actually accomplished.
                  </p>
                </div>

                <div className="flex items-center justify-between bg-zinc-100/50 p-2 rounded-xl border border-zinc-200">
                  <button
                    onClick={() => setJournalMonthOffset(prev => prev - 1)}
                    className="p-2 hover:bg-white rounded-lg text-zinc-600 hover:text-indigo-600 transition-colors shadow-sm border border-transparent hover:border-zinc-200"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <div className="text-sm font-semibold text-zinc-700">
                    {getMonthLabel(currentJournalMonthKey)}
                  </div>

                  <button
                    onClick={() => setJournalMonthOffset(prev => prev + 1)}
                    className="p-2 hover:bg-white rounded-lg text-zinc-600 hover:text-indigo-600 transition-colors shadow-sm border border-transparent hover:border-zinc-200"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-zinc-800 mb-2">
                      My expectations for this month
                    </label>
                    <textarea
                      value={journalDraft.expectations}
                      onChange={(e) =>
                        setJournalDraft(prev => ({ ...prev, expectations: e.target.value }))
                      }
                      placeholder="What do I expect from myself this month?"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-none"
                      rows={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-zinc-800 mb-2">
                      What I accomplished this month
                    </label>
                    <textarea
                      value={journalDraft.accomplished}
                      onChange={(e) =>
                        setJournalDraft(prev => ({ ...prev, accomplished: e.target.value }))
                      }
                      placeholder="What did I actually get done?"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-none"
                      rows={6}
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={saveJournalEntry}
                      className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save Journal
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* EXPORT & IMPORT FOOTER */}
        <div className="flex flex-col items-center justify-center gap-4 mt-6 mb-4 text-zinc-400 text-xs font-medium">
          
          {/* SECURE CONNECTION INDICATOR */}
          {user && (
            <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-400 mb-1 flex items-center gap-1.5 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
              <Wifi className="w-3 h-3" />
              Synced to: {user.email}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={handleExport} className="hover:text-zinc-600 transition-colors flex items-center gap-1.5 px-2 py-1"><Download className="w-3.5 h-3.5" /> Export Data</button>
            <span className="opacity-30">|</span>
            <label className="hover:text-zinc-600 transition-colors flex items-center gap-1.5 cursor-pointer px-2 py-1"><Upload className="w-3.5 h-3.5" /> Import Data<input type="file" accept=".json" className="hidden" onChange={handleImport} /></label>
            <span className="opacity-30">|</span>
            <button
              type="button"
              disabled={!user || backupBusy}
              onClick={async () => {
                setBackupBusy(true);
                try {
                  const ok = await writeCloudBackup('manual');
                  showNotification(ok ? 'Snapshot saved to Firebase (separate from live data).' : 'Snapshot skipped — nothing to save yet.');
                } catch (err) {
                  console.error(err);
                  showNotification('Snapshot failed. Add Firestore rules for rpg_tracker_backups.');
                } finally {
                  setBackupBusy(false);
                }
              }}
              className="hover:text-zinc-600 transition-colors flex items-center gap-1.5 px-2 py-1 disabled:opacity-40"
            >
              <Save className="w-3.5 h-3.5" /> Snapshot
            </button>
            <span className="opacity-30">|</span>
            <button
              type="button"
              onClick={() => {
                setShowBackupPanel((v) => !v);
                if (user) loadCloudBackups();
              }}
              className="hover:text-zinc-600 transition-colors flex items-center gap-1.5 px-2 py-1"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restore
            </button>
            <span className="opacity-30">|</span>
            <button onClick={handleTestConnection} className="text-emerald-600 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 rounded-lg transition-colors flex items-center gap-1.5 px-2.5 py-1.5 shadow-sm font-bold"><Wifi className="w-3.5 h-3.5" /> Test Connection</button>
            <span className="opacity-30">|</span>
            <button onClick={handleLogout} className="hover:text-red-500 transition-colors flex items-center gap-1.5 px-2 py-1 text-zinc-500"><LogOut className="w-3.5 h-3.5" /> Sign Out</button>
          </div>

          {showBackupPanel && (
            <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-2xl p-4 text-left text-zinc-700 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold">Firebase snapshots</h3>
                <button type="button" onClick={() => setShowBackupPanel(false)} className="p-1 text-zinc-400 hover:text-zinc-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11px] text-zinc-500 mb-3">
                These live in <span className="font-mono">users/…/rpg_tracker_backups</span>, not in the live <span className="font-mono">main</span> document. A bad live save cannot erase them.
              </p>
              {cloudBackups.length === 0 ? (
                <p className="text-xs text-zinc-500">No snapshots yet. Use Snapshot after you have data, or wait for the first auto-save.</p>
              ) : (
                <ul className="space-y-2">
                  {cloudBackups.map((row) => (
                    <li key={row.id} className="flex items-center justify-between gap-2 text-xs border border-zinc-100 rounded-xl px-3 py-2">
                      <div>
                        <div className="font-semibold">{new Date(row.createdAt).toLocaleString()}</div>
                        <div className="text-zinc-400">{row.source} · weight {row.weight}</div>
                      </div>
                      <button
                        type="button"
                        disabled={backupBusy}
                        onClick={() => restoreCloudBackup(row.id)}
                        className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg font-bold disabled:opacity-40"
                      >
                        Restore
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          <div className="flex flex-wrap items-center justify-center gap-2 w-full max-w-xl">
            <div className="flex items-center gap-2 bg-zinc-100/50 px-3 py-1.5 rounded-full border border-zinc-200 focus-within:border-indigo-300 focus-within:bg-white transition-all w-full max-w-[240px]">
              <Sparkles className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
              <input type="password" placeholder="Gemini 2.5 Flash API Key (primary AI)" value={userApiKey} onChange={(e) => setUserApiKey(e.target.value)} onBlur={() => updateCloud({ userApiKey })} className="bg-transparent border-none focus:outline-none text-[10px] w-full text-zinc-700 placeholder-zinc-400" />
            </div>
            {/* Second Google account. Gemini free-tier quota is per project, so this is
                a whole extra daily allowance rather than a retry of the first key. */}
            <div className="flex items-center gap-2 bg-zinc-100/50 px-3 py-1.5 rounded-full border border-zinc-200 focus-within:border-indigo-300 focus-within:bg-white transition-all w-full max-w-[240px]">
              <Sparkles className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
              <input type="password" placeholder="Gemini API Key #2 (2nd Google account)" value={userApiKey2} onChange={(e) => setUserApiKey2(e.target.value)} onBlur={() => updateCloud({ userApiKey2 })} className="bg-transparent border-none focus:outline-none text-[10px] w-full text-zinc-700 placeholder-zinc-400" />
            </div>
            <div className="flex items-center gap-2 bg-violet-50/80 px-3 py-1.5 rounded-full border border-violet-200 focus-within:border-violet-400 focus-within:bg-white transition-all w-full max-w-[280px]">
              <UtensilsCrossed className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
              <input type="password" placeholder="DeepSeek Flash fallback API key" value={deepseekApiKey} onChange={(e) => { setDeepseekApiKey(e.target.value); lifestyleRef.current = { ...lifestyleRef.current, deepseekApiKey: e.target.value }; }} onBlur={() => persistLifestyleToCloud({ deepseekApiKey })} className="bg-transparent border-none focus:outline-none text-[10px] w-full text-zinc-700 placeholder-zinc-400" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}